import * as THREE from 'three';
import type { Cue } from '../render/vendor/cue.js';

/**
 * canvas 上の aim/cue grabKind 判定の許容半径（CSS px）。
 * モジュール公開（drag-guide.ts が halo 幅をこの値の 2 倍 = capsule 直径として描画するため）。
 * 変更時は drag-guide.ts の GRAB_RADIUS_PX とも一致させること（不一致だと
 * 「見える範囲」と「掴める範囲」がズレて誤操作が増える）。
 */
export const AIM_GRAB_PX_EXPORT = 44;

/**
 * Aim 前方ガイド掴み範囲の長さ（m, world）。grabKind では cb + 2.5m 先までを 'aim' 領域として扱う。
 * drag-guide.ts への入力 `forwardLen` と完全同期させる（短くすると「見える範囲」が
 * 実際の「掴める範囲」より狭く見えて誤操作が増える）。
 */
export const AIM_FORWARD_LEN_M = 2.5;
import type { Table } from '../engine/table.js';
import type { CameraRig } from '../render/camera-rig.js';
import type {
  AuthResult,
  AdminResult,
  AdminUser,
  AdminPatch,
  AnalyticsResponse,
  RaceConfig,
} from '../net/lobby.js';
import { createProfilePanel } from './profile.js';
import { chatTts, type VoiceProfile } from '../audio/chat-tts.js';
import {
  computeRailClearanceElevation,
  computeRailMinOffsetY,
  computeSpinForbiddenDisks,
  type SpinForbiddenDisk,
  type PlayMode,
  ballRadius,
  getTableLayout,
  onTableBallIds,
  isFreeSpot,
  placeBallAt,
  addBall,
  removeBall,
  lowestOnTableBallId,
  ballPositionById,
  aimedTargetId,
  cueBallPos,
  getPocketAims,
  applySquirt,
  squirtDeflection,
  stopShotSpeed,
  simulateObjectBallDir,
  saveShotState,
  restoreShotState,
  hasShotState,
  clearShotState,
  serializeBalls,
  saveBreakCuePos,
  type PersistedBall,
} from '../sim/world.js';
import {
  solveAutoAim,
  findMakeablePockets,
  type Vec2,
  type AutoAimSolution,
} from '../sim/auto-aim.js';
import { BALL_COLORS } from '../render/balls.js';
import { effectiveCueLengthForGrab } from '../render/drag-guide.js';
import {
  createSettings,
  getSquirt,
  getUiUserScale,
  isAutoAim,
  isMatchActive,
  isBallRoster,
  getChatPresets,
  sanitizeChatText,
  CHAT_TEXT_MAXLEN,
  getShotInputMode,
  subscribeShotInputChange,
  isAutoViewReset,
  getAutoViewResetSec,
  isAutoViewResetZoom,
  getShotUiTempHideMs,
} from './settings.js';
import { shouldTempHide, computeRestoreAt } from './shot-ui-temp-hide.js';
import { quirks } from '../util/quirks.js';
import {
  getCpuPlayers,
  addCpuPlayer,
  updateCpuPlayer,
  deleteCpuPlayer,
  resetCpuPlayersToBuiltin,
  generateCpuId,
  BUILTIN_CPU_PLAYERS,
  type CpuStyle,
  type CpuPlayer,
} from '../ai/cpu-players.js';
import {
  STYLE_CONFIG_META,
  getCpuConfig,
  getCpuConfigDefault,
  setCpuConfig,
  resetCpuConfig,
  type StyleConfig,
  type StyleFieldMeta,
} from '../ai/cpu-config.js';

/**
 * 入力レイヤ（マウス + タッチ統合 / Pointer Events）。モード切替なし。
 * - 視点: 1本指/左ドラッグで自由回転、ピンチ/ホイールでズーム。
 * - ショット方向: 手球（またはキュー）をタップ/ホールドしている間だけドラッグで変更。
 *   狙うとカメラがショット方向を正面に向ける（camera-rig.alignToAim）。
 * - 撞点: パッドをタップ（tailuge cue.setSpin）。ショット完了ごとにセンターへ戻る。
 * - 強さ＋発射: 撞点UIの上の縦キューを下へ引き下げて離す。引き量が強さ（タイミング要素なし）。
 *   デフォルト位置（最上＝引き量ゼロ付近）で離すと発射しない。
 */
export interface ControlsDeps {
  readonly canvas: HTMLCanvasElement;
  readonly camera: THREE.PerspectiveCamera;
  readonly rig: CameraRig;
  readonly cue: Cue;
  readonly table: Table;
  readonly isResting: () => boolean;
  readonly onReset: () => void;
  /** プラクティス用「↺ デフォルトに戻す」ボタンが押された通知。
   *  main は保存配置を破棄したうえで通常リセット（プラクティスのデフォルト盤面）を実行する。 */
  readonly onResetPracticeLayoutToDefault?: () => void;
  /** 装備中キューのブレイクショット最大ヘッドスピード [m/s]（未装備/未指定で undefined）。
   *  指定があれば既定ブレイク上限よりも大きい場合に上書き採用する。 */
  readonly getEquippedCueBreakMaxSpeed?: () => number | undefined;
  /** 装備中 skill の「対戦中自動照準」許容カット角 [度]（未装備/未指定で undefined）。
   *  指定があれば対戦中（CPU戦・オンライン対戦）でもその角度まで自動照準を発動する。 */
  readonly getEquippedSkillAutoAimMaxCutDeg?: () => number | undefined;
  /** ドリル中に有効化する自動照準の許容カット角 [度]（保有スキルの最大・owned ベース）。
   *  未保有/未指定で undefined。ドリルはスキル測定だが、保有者にはその獲得範囲で自動照準を許す（F-30・案A）。
   *  Efren 報酬スキルも owned に含まれるため自動的に対象になる（二重実装回避）。 */
  readonly getDrillAutoAimMaxCutDeg?: () => number | undefined;
  /** 次のショットがブレイクショットかの**権威判定**（ルール状態由来）。
   *  ルール有効時は `ruleState.phase === 'breakPlacement'` を返し、ルール無効（プラクティス等）では
   *  `undefined` を返す。controls はこれが boolean のときそれを採用し、undefined のときのみ
   *  ローカルフラグ `broken` にフォールバックする。
   *  ★これにより「相手（CPU/オンライン）がブレイクした後、自分の最初の通常ショットが
   *    ブレイク扱いになってパワー増強が誤発動する」問題を防ぐ（broken はローカル shoot でしか
   *    true にならず、相手のブレイクでは同期されないため）。 */
  readonly isBreakShot?: () => boolean | undefined;
  /** ログアウト要求。main がクライアント状態リセット + nickname localStorage クリア
   *  + startAuthFlow('') を呼んで再ログイン画面に遷移する。 */
  readonly onLogout?: () => void;
  /** プレイモード変更（設定UI→ main が world+views 再構築）。 */
  readonly onPlayModeChange: (mode: PlayMode) => void;
  /** 現在のプレイモード取得（プラクティス判定用）。 */
  readonly getPlayMode: () => PlayMode;
  /** プラクティスで球を追加/削除した後、main が views を再構築する。 */
  readonly onBallsChanged: () => void;
  /** 🎯 ドリルボタン押下。main がドリル選択パネルを開く（F-30）。 */
  readonly onOpenDrill?: () => void;
  /** ショットを撞く直前へ戻す（アンドゥ）。main が再生状態を解除する。 */
  readonly onUndo: () => void;
  /** ドリル中の「↻ やり直し」: 現在のドリルを配置・ショット数・残球ごと最初からやり直す。
   *  （ドリル中はショット単位アンドゥを禁止し、戻すボタンをこの全リセットに切り替える） */
  readonly onDrillRestart: () => void;
  /** 撞く直前（cue.hit の前・全球静止）。オンラインで撞く直前スナップショットを取るのに使う。 */
  readonly onBeforeStrike?: () => void;
  /** 手球に速度を与えた瞬間（打撃時）。ショット音をフレーム遅延なく鳴らすために使う。 */
  readonly onShoot?: () => void;
  /** オートエイムの自動補正が走った瞬間。main がキューのきらめき演出を起動する。 */
  readonly onAutoAimGlimmer?: () => void;
  /** ラック調整の保存時（設定UI）。main が現在モードを再ラックして反映する。 */
  readonly onRackChange: () => void;
  /** テーブルコンディション（湿度）変更時。対戦中は main が相手へ通知する。 */
  readonly onHumidityChange?: (humidity: number) => void;
  /** プッシュアウト宣言（pushOutOption 時）。main が reducer に declarePushOut を送る。 */
  readonly onDeclarePushOut?: () => void;
  /** プッシュアウト応答（pushOutResponse 時）。takeShot=true で撞く / false で返す。 */
  readonly onPushOutResponse?: (takeShot: boolean) => void;
  /** オンライン対戦に接続（ループバック=同一ブラウザ2タブ）。 */
  readonly onConnectOnline?: () => void;
  /** 対戦待機トグル（ロビー）。 */
  readonly onLobbyWaitingToggle?: () => void;
  /** ロビーパネルを開いた時。main が最新の順位表（leaderboard）を取得して再描画する。 */
  readonly onLobbyOpen?: () => void;
  /** 対戦中ユーザーの観戦開始。 */
  readonly onSpectate?: (matchId: string) => void;
  /** ロビー行タップで個人プロファイルを開く。main がサーバーから取得して controls.openProfile を呼ぶ。
   *  2026-06-17: 最終アクセス（lastSeen・epoch ms / 0=未記録）と在席状態（online）を付加引数で渡す。
   *  プロファイルパネルのヘッダ近傍に「オンライン」「最終アクセス: ◯◯前」として表示する
   *  （旧ロビー一覧 Last online 列の機能をプライバシー保護のため個人画面へ移設したもの）。 */
  readonly onProfileOpen?: (name: string, lastSeen: number, online: boolean) => void;
  /** 名前入力 → 自動ログイン判定（§13.3）。status で次のモーダルを決める。 */
  readonly onAuthTry?: (name: string) => Promise<AuthResult>;
  /** 新規登録（PIN設定）。 */
  readonly onRegister?: (name: string, pin: string) => Promise<AuthResult>;
  /** PIN で端末紐づけ。 */
  readonly onAuthPin?: (name: string, pin: string) => Promise<AuthResult>;
  /** 名前変更（成績移送・本人のみ）。 */
  readonly onRename?: (to: string) => Promise<AuthResult>;
  /** PIN 設定/変更/救済（紐づけ済み端末）。 */
  readonly onSetPin?: (pin: string) => Promise<AuthResult>;
  /** 管理: アカウント一覧（pw はサーバー照合）。 */
  readonly onAdminList?: (pw: string) => Promise<AdminResult>;
  /** 管理: アカウント削除。 */
  readonly onAdminDelete?: (pw: string, name: string) => Promise<AdminResult>;
  /** 管理: アカウント編集（勝敗/改名/PIN解除）。 */
  readonly onAdminEdit?: (pw: string, name: string, patch: AdminPatch) => Promise<AdminResult>;
  /** 勝敗後の再戦リクエスト。 */
  readonly onRematch?: () => void;
  /** 対戦から退出してロビーへ戻る。 */
  readonly onLeaveMatch?: () => void;
  /** 対戦中チャット送信（main が相手へ中継）。 */
  readonly onChat?: (text: string) => void;
  /** 対 CPU 戦の申し込み。controls からロビー行タップで発火し、main が race 選択モーダル→対戦開始へ繋ぐ。 */
  readonly onChallengeCpu?: () => void;
  /** race 選択モーダルでユーザーが対戦開始を押したとき（CPU 戦専用）。
   *  人間対戦では `onRaceConfigChange` / `onRaceCancel` を使い、両者承諾後に main が startMatch を発火する。 */
  readonly onRaceStart?: (params: {
    readonly targetWins: 1 | 3 | 5 | 9;
    readonly breakRule: 'alternate' | 'winner';
  }) => void;
  /** 人間対戦のラック設定モーダル: 自分側の選択値変更/承諾フラグ更新。
   *  - accepted=false: 値変更（相手の承諾もリセットされる前提で main → lobby に通知）
   *  - accepted=true:  「対戦開始」押下＝自分の承諾。両者 accepted=true で main 側が startMatch を発火。 */
  readonly onRaceConfigChange?: (
    matchId: string,
    race: RaceConfig,
    accepted: boolean,
  ) => void;
  /** 人間対戦のラック設定モーダルで「キャンセル」を押下したとき。main が raceAborted を相手へ通知する。 */
  readonly onRaceCancel?: (matchId: string) => void;
  /** 相手選択モーダルで「対戦を申し込む」を押下したとき（選択中の候補 userId が渡る）。 */
  readonly onOpponentPickerSubmit?: (selectedUserId: string) => void;
  /** 相手選択モーダルで「キャンセル」を押下したとき。 */
  readonly onOpponentPickerCancel?: () => void;
  /** 相手選択モーダルで「別の人が来るまで待機する」を押下したとき。
   *  active=true でモーダルを閉じてバックグラウンド待機モードへ。
   *  active=false で待機モード解除（実質キャンセル相当＝モーダルも閉じる）。 */
  readonly onToggleBackgroundWaiting?: (active: boolean) => void;
  /** 対戦勧誘ダイアログで「対戦する」を押下したとき（main が requestMatch 発行）。 */
  readonly onAcceptMatchInvitation?: (opponentUserId: string) => void;
  /** 対戦勧誘ダイアログで「今は続ける」を押下したとき（main が通知済み Set に追加）。 */
  readonly onDismissMatchInvitation?: (opponentUserId: string) => void;
  /** ラック設定モーダル内チャット: 自分が送信した瞬間（main が lobby.sendLobbyChat と自分側 echo 表示を行う）。 */
  readonly onLobbyChatSend?: (matchId: string, text: string) => void;
}

/** ロビー順位リストの1行（main が整列・順位付けして渡す・自分も含む）。 */
export interface LobbyRowView {
  /** 順位（1始まり）。3試合未満は null（'－' 表示）。 */
  readonly pos: number | null;
  readonly name: string;
  readonly wins: number;
  readonly losses: number;
  /** 最終オンライン（epoch ms・0=未記録）。online=true のときは無視（'●' 表示）。 */
  readonly lastSeen: number;
  /** 在席中か（Last online を '●' に・offline 行を弱表示に）。 */
  readonly online: boolean;
  readonly status: 'idle' | 'waiting' | 'playing' | 'spectating' | 'offline';
  /** 自分の行か（ハイライト・「自分を探す」対象）。 */
  readonly isSelf: boolean;
  readonly matchId: string | null;
  readonly canSpectate: boolean;
  /** CPU 仮想プレイヤーか。true のとき行レンダリングを特殊化する。 */
  readonly isCpu?: boolean;
  /** 管理人アカウントか。true のとき行は常時表示・最終アクセス日時を出さず黄/緑ポッチで在席表示する。 */
  readonly isAdmin?: boolean;
}

/** ロビー HUD のレンダリング記述子（main が状態を整形して渡す）。 */
export interface LobbyView {
  readonly nickname: string;
  readonly waiting: boolean;
  /** ロビーアイコンの色（緑=待機者あり / 青=対戦中あり / グレー=なし）。 */
  readonly iconState: 'gray' | 'green' | 'blue';
  /** 順位リスト（自分含む全員・整列済み）。 */
  readonly rows: readonly LobbyRowView[];
  /** 対戦/観戦中か（true の間は「対戦相手をさがす」を無効化）。 */
  readonly inMatch: boolean;
}

/** インベントリパネルに渡す描画用ビュー（main が組み立て・controls は描画のみ）。 */
export interface InventoryItemView {
  readonly id: string;
  readonly category: string;
  readonly name: string;
  readonly description: string;
  readonly owned: boolean;
  readonly equipped: boolean;
  /** 解錠条件の表示テキスト（短文）。取得済/未取得を問わず必ず文字列を返す（旧 `hint`）。 */
  readonly unlockText: string;
  /** 解錠難易度スコア（小さいほど易しい）。デフォルトの昇順ソートに使う。 */
  readonly difficulty: number;
}
export interface InventoryView {
  /** カテゴリ別に並べたアイテム一覧（取得済みを先頭・未取得は薄表示）。 */
  readonly items: readonly InventoryItemView[];
  /** 装備変更のリクエスト（itemId=null で装備解除）。 */
  readonly onEquip: (category: string, itemId: string | null) => void;
  /** 管理人なら true（バッジ表示など）。 */
  readonly isAdmin?: boolean;
}

/** ルール HUD のレンダリング記述子（main が状態を整形して渡す・controls は描画のみ）。 */
export interface RuleView {
  /** ルールモード有効か（false なら HUD/ボタンを全て隠す）。 */
  readonly active: boolean;
  readonly phase:
    | 'breakPlacement'
    | 'pushOutOption'
    | 'pushOutResponse'
    | 'ballInHand'
    | 'inPlay'
    | 'gameOver';
  /** 現在のショット権者（0/1）。 */
  readonly current: 0 | 1;
  /** プッシュアウト宣言済みか（ボタン表示の切替）。 */
  readonly pushOutDeclared: boolean;
  /** 整形済みステータス行（手番・ファウル理由・勝敗など）。 */
  readonly message: string;
  readonly gameOver: boolean;
  /** オンライン対戦中か（リセット/戻す/フリー配置を無効化する）。 */
  readonly online: boolean;
  /** 自分の手番でない＝入力ロック（撞く・配置を禁止）。 */
  readonly inputLocked: boolean;
  /** 自分が再戦をリクエスト済みか（ボタン表示の切替）。 */
  readonly rematchRequested: boolean;
}

/** main から配置モード制御や状態同期を行うためのハンドル。 */
export interface ControlsApi {
  /** ルール HUD（手番・ファウル・プッシュアウト導線）を更新する。 */
  setRuleView: (view: RuleView) => void;
  /** ロビー（在席リスト・待機・観戦・名前）を更新する。 */
  setLobbyView: (view: LobbyView) => void;
  /** アカウント認証フローを開始する（保存名で自動ログイン or 名前入力→登録/PIN）。 */
  startAuthFlow: (name: string) => void;
  /** ロビーパネルを閉じる（対戦確定時など）。 */
  closeLobby: () => void;
  /** ロビーパネルを開く。対戦終了/中断時に main が呼ぶ。
   *  既存の onLobbyOpen フックを発火し、leaderboard を最新化する。 */
  openLobby: () => void;
  /** 通知トーストを表示する（自動で消える）。 */
  showToast: (text: string) => void;
  /** 対戦開始/終了などの大きめの中央バナーを表示する（showToast より目立つ）。
   *  kind により色分け: start=青 / win=金 / lose=赤 / abort=灰 / leave=灰。約 2.6 秒で自動消滅。 */
  showMatchEvent: (
    title: string,
    subtitle: string,
    kind: 'start' | 'win' | 'lose' | 'abort' | 'leave',
  ) => void;
  /** 対戦中チャットの送信UI（💬ボタン）の有効/無効を切り替える（対戦中のみ有効）。 */
  setChatEnabled: (enabled: boolean) => void;
  /** チャット吹き出しを表示する（who='me' 自分 / 'opponent' 相手・name=送信者名）。
   *  voice は受信メッセージに同梱されていた送信者の音声プロファイル（任意・読み上げ用）。 */
  showChatMessage: (
    text: string,
    who: 'me' | 'opponent',
    name?: string,
    voice?: VoiceProfile,
  ) => void;
  /** ブレーク/モード変更時に「配置モード」を開始する。 */
  enterPlacement: () => void;
  /** ドリル中ロック（F-30）。true で配置モード・球追加・球ドラッグを全面禁止（固定配置）。 */
  setDrillLock: (active: boolean) => void;
  /** スクラッチ後のボールインハンド開始（手玉を盤面自由配置・再ラックしない）。 */
  enterBallInHand: () => void;
  /**
   * レジューム復元時: 再ラックせずブレーク済み/ボールインハンド状態を復旧する。
   * ballInHand=true なら配置モードで開始（手玉を動かせる）、それ以外は通常エイム。
   */
  enterResumed: (state: { broken: boolean; ballInHand: boolean }) => void;
  /** 永続化用の現在の配置状態（ブレーク済み/ボールインハンド）を取得する。 */
  getPersistState: () => { broken: boolean; ballInHand: boolean };
  /** 手玉の球速 [m/s] を km/h 表示する（null または微小なら非表示）。
   *  main がブレイク中またはプラクティス中に「その一打の瞬間最高速度」を渡し、静止で null にして消す。 */
  setBallSpeed: (mps: number | null) => void;
  /** ユーザーが手動で立てたキュー仰角（`manualElev`）を 0 にリセットし、自動仰角のみを反映する。
   *  main がショット静止 → 次の構え立ち上がりのタイミングで呼ぶ（仰角を次のショットに引き継がない）。
   *  自動仰角（レール際で下撞点を撞くために必要な最小立て角）は通常通り適用される。 */
  resetCueElevation: () => void;
  /** 現在のドラッグジェスチャ種別（drag-guide の色変更用）。
   *  'aim'=ショット方向操作中 / 'elevate'=仰角操作中 / 'idle'=非ドラッグ or 他ジェスチャ。 */
  getDragGesture: () => 'aim' | 'elevate' | 'idle';
  /** 現在「配置モード中に動かせる球」の id 集合を返す。配置モードでない／相手手番中は空セット。
   *  プラクティス＝盤上の全球（手球+的玉）、それ以外＝手球(0)のみ。
   *  main は毎フレーム呼んで `updateHaloPulse` に渡し、ハロのふんわり明滅で視覚化する。 */
  getMovableBallIds: () => Set<number>;
  /** 自動照準で「狙える」と判定したポケット候補のリストと、UI で選択中のインデックスを返す。
   *  - positions: ポケット中心の Vec2 配列（経路が通り cutAngle ≤ maxCut のもののみ）
   *  - selectedIndex: positions 内で「現在 autoAim が使っているポケット」のインデックス（null=なし）
   *  main は毎フレーム呼んで pocket-glows に渡す。表示不可（autoAim 無効・ターゲット球なし等）は空配列。 */
  getMakeablePockets: () => {
    readonly positions: ReadonlyArray<{ readonly x: number; readonly y: number }>;
    readonly pocketIndices: ReadonlyArray<number>;
    readonly selectedIndex: number | null;
  };
  /** UI が「ポケットグローをタップした」ことを controls に伝えるエントリ。
   *  pocketIndex は pocketAims 上のインデックス（getMakeablePockets が返す pocketIndices の要素値）。
   *  preferredPocketIndex を設定し、即座に autoAim を再発動する。 */
  selectPocket: (pocketIndex: number) => void;
  /** アカウント情報（設定パネル下部のセクション表示用）。
   *  serverAuth=true のときのみ「アカウント」セクションを表示してログアウト可能にする。
   *  main がログイン完了時に setAccountInfo(name, true)、ログアウト時に setAccountInfo('', false) を呼ぶ。 */
  setAccountInfo: (name: string, serverAuth: boolean) => void;
  /** インベントリパネルのビューを更新する（保有・装備状態の最新を反映）。 */
  setInventoryView: (view: InventoryView) => void;
  /** インベントリパネルを開閉する（トグル）。 */
  toggleInventoryPanel: () => void;
  /** 個人プロファイルパネルを「読み込み中」で開く（取得開始時）。
   *  lastSeen/online は最終アクセス表示用（旧 Last online 列の移設先）。 */
  openProfileLoading: (name: string, lastSeen: number, online: boolean) => void;
  /** 個人プロファイルパネルを取得済みデータで描画する。data=null はデータなし表示。
   *  lastSeen/online は最終アクセス表示用（旧 Last online 列の移設先）。 */
  openProfile: (
    name: string,
    data: AnalyticsResponse | null,
    lastSeen: number,
    online: boolean,
  ) => void;
  /** 個人プロファイルパネルを閉じる。 */
  closeProfile: () => void;
  /** プラクティス用に保存された配置（PersistedBall[]）を返す。なければ null。
   *  main がリセット時に呼んで、保存配置があればそれを盤面に適用する。 */
  loadPracticeLayout: () => PersistedBall[] | null;
  /** カット角ガイド（直角四辺形）の 4 頂点を返す。表示不可なら null。
   *  - 表示条件: 静止中・手玉が盤上・ターゲットの的玉 (`aimTargetId`) あり・最適ポケット解あり
   *  - **基準軸 u = (的玉中心 − 手玉中心) の単位ベクトル**（=手玉→的玉ラインを 1 辺方向とする）。
   *    n = u を 90° 回転した直交単位。
   *  - 対角頂点 = 的玉中心 T と ポケット狙点 P。a = (P−T)·u、b = (P−T)·n とすると
   *      頂点1: T
   *      頂点2: T + a·u
   *      頂点3: P  (= T + a·u + b·n)
   *      頂点4: T + b·n
   *  - カット角 θ（=手玉→的玉ラインと 的玉→ポケットラインのなす角）に対し
   *    a = |P−T|cos θ・b = |P−T|sin θ ⟹ |a|=|b| ⟺ θ=45° で正方形になる。
   *  ガイド設定の ON/OFF 判定は main 側で行う（settings.isCutAngleGuide）。 */
  getCutAngleFrameVertices: () =>
    | ReadonlyArray<{ readonly x: number; readonly y: number }>
    | null;
  /** 残球表示で強調する「狙っている的玉」の id を設定する（null で特定対象なし＝第一接触へ）。
   *  main が最若球などへ自動照準した直後に呼び、間に障害球があっても意図した的玉を強調させる。 */
  setAimTarget: (id: number | null) => void;
  /** 現在「狙っている的玉」の id を取得する（最若球照準/手動タップ/AutoAim で設定済の値）。
   *  狙撃カム（ショットフリーズ）発動条件判定で main.ts が参照する。null=未設定/解消済。 */
  getAimTargetId: () => number | null;
  /** race スコアボード（画面上部中央）を更新する。null で非表示。
   *  myWins/oppWins=各プレイヤー獲得ラック数、targetWins=先取数、oppName=相手表示名（CPU は登録名）。 */
  setRaceScore: (
    view:
      | {
          readonly myWins: number;
          readonly oppWins: number;
          readonly targetWins: number;
          readonly rackIndex: number;
          readonly oppName: string;
          readonly vsCpu: boolean;
        }
      | null,
  ) => void;
  /** race 選択モーダル（1/3/5/9先 + 交互/勝者ブレイク）を表示する。
   *  - vsCpu=true（matchId 省略）: 「対戦開始」押下で `onRaceStart` を発火（後方互換の CPU 戦経路）。
   *  - vsCpu=false（matchId 必須）: 両者対称ネゴ UI。自分の値変更で `onRaceConfigChange(accepted=false)`、
   *    「対戦開始」押下で `onRaceConfigChange(accepted=true)`、「キャンセル」で `onRaceCancel(matchId)`。
   *  initialRace を渡すと冒頭の選択状態をその値で初期化する（pickAccepted 受信時の raceA など）。
   *  値変更時には `localStorage('pool.race.lastConfig')` に保存し次回起動時に既定値として復元する。 */
  showRaceModal: (params: {
    readonly opponentName: string;
    readonly vsCpu: boolean;
    readonly matchId?: string;
    readonly initialRace?: RaceConfig;
  }) => void;
  /** 人間対戦のラック設定モーダルで、相手側の値変更/承諾通知を反映する。
   *  - 「相手の設定」エリアの表示を更新する（vsCpu の場合は呼び出し無効）。
   *  - 相手の race が変わった場合、自分の承諾フラグもリセット（「対戦開始」をもう一度押す必要がある状態に戻す）。 */
  updateRaceModalRemote: (race: RaceConfig, accepted: boolean) => void;
  /** ラック設定モーダルを外部から閉じる（raceAborted/raceStart 受信時など）。 */
  closeRaceModal: () => void;
  /** ラック設定モーダル内チャットに 1 件追加（過去 3 件のリングで自然に古いものから消える）。
   *  - who='me' は自分の送信 echo（main が onLobbyChatSend → 直接呼ぶ）。
   *  - who='opponent' は相手からの受信（main が lobby.onLobbyChat → 自エコーを弾いた後に呼ぶ）。
   *  vsCpu のモーダルでは UI ごと非表示のため呼んでも視認できない（呼び出し側で抑止推奨）。 */
  showLobbyChatInRaceModal: (
    text: string,
    who: 'me' | 'opponent',
    name?: string,
    voice?: VoiceProfile,
  ) => void;
  /** 相手選択モーダルを表示する（左右スライド式 + 自動開始タイマー）。
   *  - 候補は呼び出し時のスナップショット。動的更新は updateOpponentPickerCandidates で。
   *  - 初期フォーカスは「最初の対人」（いなければ先頭 CPU）。
   *  - 「対人戦を自動開始」チェックが ON かつ対人カードにフォーカス中なら 5 秒後に自動申込。 */
  showOpponentPicker: (params: {
    readonly candidates: readonly {
      readonly userId: string;
      readonly nickname: string;
      readonly isCpu: boolean;
    }[];
  }) => void;
  /** 相手選択モーダルの候補一覧をリアルタイム更新する。
   *  - 消失した候補が現在表示中なら次のインデックスへフォールバック。
   *  - 現在 CPU にフォーカス中で新規対人が候補に入った場合は最初の対人へ自動フォーカス。
   *  - 自動開始タイマーは延長しない（手動カード切替/OFF→ON 切替時のみリセット起動）。 */
  updateOpponentPickerCandidates: (
    candidates: readonly {
      readonly userId: string;
      readonly nickname: string;
      readonly isCpu: boolean;
    }[],
  ) => void;
  /** 相手選択モーダルを外部から閉じる（pickAccepted 受信や離席時など）。 */
  closeOpponentPicker: () => void;
  /** バックグラウンド待機モードの表示状態を更新する。main が waiting フラグと連動して呼ぶ。
   *  active=true: picker 内の待機ボタンが緑表示・自動申込カウントダウン無効・ロビーボタンに小バッジ。
   *  active=false: 通常表示に戻す。 */
  setBackgroundWaitingMode: (active: boolean) => void;
  /** 対戦勧誘ダイアログを表示する。
   *  - cpuMatch=true の時の通知（CPU戦中に対人が waiting になった）と
   *    バックグラウンド待機モード中の通知（picker 閉だが waiting=true）で共通使用。
   *  - 表示中は他のダイアログと競合しないよう main 側で表示制御。 */
  showMatchInvitation: (params: {
    readonly opponentUserId: string;
    readonly opponentNickname: string;
  }) => void;
  /** 対戦勧誘ダイアログを外部から閉じる（roster から相手が消えた等）。 */
  closeMatchInvitation: () => void;
  /** 対戦勧誘ダイアログが現在表示中か（main 側の表示制御に使う）。 */
  isMatchInvitationOpen: () => boolean;
  /** CPU 対戦時のショット プレビュー（aim/撞点/パワー）を外部から適用する。
   *  ループバック対戦の擬似 'aim' メッセージ相当＝相手の構えを可視化するための内部 API。
   *  全パラメータは絶対値（cue.aim.* と同じスケール）。各 tick で連続呼び出しすると
   *  ガイド線・撞点パッド・パワーインジケータが滑らかに追従する。
   *  preview を終了する（通常 UI に戻す）には `clearAimPreview` を呼ぶ。 */
  applyAimPreview: (preview: {
    readonly angle: number; // 手球進路角 [rad]
    readonly offsetX: number; // 撞点 X [-0.5..0.5]
    readonly offsetY: number; // 撞点 Y [-0.5..0.5]
    readonly power: number; // パワー [m/s]（cue.aim.power と同単位）
    readonly elevation: number; // キュー仰角 [rad]
  }) => void;
  /** applyAimPreview の状態を解除する（CPU 発射直前に呼ぶ）。 */
  clearAimPreview: () => void;
}

const ORBIT_K = 0.006; // px → rad（視点）
const MAX_ELEVATION = Math.PI / 3; // キュー仰角の上限 [rad]（60°）
// 通常時の自動仰角の下限（4°）。完全に寝かせず少し立てることで、撞点（特に下撞点）を
// 確保しやすくする。レールクリアに必要な角度がこれ未満でも、最低この角度まで立てる。
const BASE_AUTO_ELEVATION = Math.PI / 45;
// ブレイク（リセット後の最初のショット）時だけ、下撞点を確保するため自動仰角を緩める。
// 一般にブレイクは少し下撞点を突くため、レール際でも下まで撞ける角度を許可する。
const BREAK_LOW_OFFSET_Y = -0.35; // ブレイク時に確保したい下撞点（offset.y）
const BREAK_RAIL_MAX_ELEVATION = Math.PI / 9; // ブレイク時のレールクリア自動仰角上限（20°）

// 通常ショットの構え既定撞点（offset.y。負=下＝引き寄り）。ド真ん中だと的玉ヒット後に
// 手玉が前へ転がりやすくコントロールが難しいため、少し下に置いて軽い引き/ストップ寄りにする。
// 値は感触で調整可（0=ド真ん中）。
const DEFAULT_SPIN_Y = -0.18;
// 撞点パッドのセンタースナップ半径（offset 単位）。中心からこの範囲内に来たら厳密に (0,0) へ
// 吸着し、指をこの外へ動かすと離脱＝「一旦中心で止まる」ことで ド真ん中に合わせやすくする。
const CENTER_DETENT = 0.07;
// 通常ショットのレールクリア自動仰角の上限（10°）。レール際で低撞点を撞くために立てるが、
// 立てても価値が無い（中心より上しか撞けない）場合は立てない（tick の wantRaise 判定）。
const NORMAL_RAIL_MAX_ELEVATION = Math.PI / 18;
// 「キューを立てる価値がある」と判定する撞点しきい値（offset.y）。上限仰角で到達できる最低撞点が
// これより下（中心よりやや下）に届く場合のみ立てる。微小なバタつき防止に -0.02 の余裕を持たせる。
const RAISE_BENEFIT_Y = -0.02;

// ブレイク既定の撞点（offset: x=横, y=縦。負=下＝引き）。一般にブレイクは中心〜やや下を撞く。
const breakSpin = (mode: PlayMode): { ox: number; oy: number } => {
  switch (mode) {
    case 'nineBall':
      return { ox: 0, oy: -0.1 }; // サイドブレイク: センターやや下
    case 'tenBall':
      return { ox: 0, oy: -0.1 };
    case 'eightBall':
    default:
      return { ox: 0, oy: -0.1 };
  }
};
// 縦キュー: トラック高に対する最大引き量の比と、発射しないデッドゾーン比。
// トラック高 × これ = 最大引き量(px)。
// **設計** (2026-06-10 スライダーのみ短縮・cue 本体サイズは維持):
//   - cueStick: top:0 / height:75%（cueTrack 40vh 時 = 30vh で旧 50vh×60% と同じサイズを維持）
//   - 視覚スライダー `::before`: top:4px / bottom:4px（cueTrack 全体を覆う＝スライダーは短縮済み）
//   - 引き travel = 25%（CUE_PULL_RATIO=0.25）：無引き時 cue 0〜75% → 最大引き時 cue 25〜100%
//   - 最大引き時に cue 後端が cueTrack 下端と一致（75 + 25 = 100%）
// ※ パワー範囲は ratio (0-1) × effectiveMaxPower() の写像なので CUE_PULL_RATIO の縮小は
//   ストロークの px のみを短くし、最大パワーには影響しない（強弱範囲は不変）。
const CUE_PULL_RATIO = 0.25;
const CUE_DEADZONE = 0.06; // 引き量比 これ以下なら発射しない（デフォルト位置）

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  parent: HTMLElement,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = cls;
  parent.appendChild(node);
  return node;
};

export const createControls = (deps: ControlsDeps): ControlsApi => {
  const { canvas, camera, rig, cue, table } = deps;
  const maxPower: number = cue.maxPower;
  // ブレイクショットのみ最大パワーを増す（割りにくさ対策）。
  // 通常キューの素の上限 = 160 * R = 5.24 m/s（GPL `cue.maxPower`・約 18.9 km/h）。
  // ブレイク時の到達上限 = 5.24 × 1.537 ≈ 8.06 m/s（約 29 km/h）。
  // 装備品 `effects.breakShotMaxSpeed`（registry.ts: 9.17 / 11.11 m/s 等）がこの値を超える場合は
  //   `effectiveMaxPower` で装備品側が優先される。
  const BREAK_POWER_BOOST = 1.537;
  // 次のショットがブレイクかの判定。権威信号（deps.isBreakShot = ルール状態由来）を最優先し、
  // それが undefined（ルール無効＝プラクティス等）のときのみローカルフラグ !broken にフォールバックする。
  // ★`broken` はローカルの人間 shoot() でしか true にならず、相手（CPU/オンライン）のブレイクでは
  //   同期されない。`!broken` を直接ブレイク判定に使うと、相手ブレイク後の自分の最初の通常ショットが
  //   ブレイク誤判定される（本修正の対象バグ）。
  const isBreakShotNow = (): boolean => {
    const authoritative = deps.isBreakShot?.();
    if (typeof authoritative === 'boolean') return authoritative;
    return !broken;
  };
  // 現在のショットで使う実効最大パワー。
  // ブレイクショット時のみ、装備品 effects.breakShotMaxSpeed があれば既定上限と比較して大きい方を採用する。
  // 通常ショットは装備品の影響を受けず素の maxPower を使う。
  const effectiveMaxPower = (): number => {
    if (!isBreakShotNow()) return maxPower;
    const equipped = deps.getEquippedCueBreakMaxSpeed?.();
    const boosted = maxPower * BREAK_POWER_BOOST;
    if (typeof equipped === 'number' && equipped > boosted) return equipped;
    return boosted;
  };

  // ジェスチャ状態
  type Pt = { x: number; y: number };
  const pointers = new Map<number, Pt>();
  // 'cuedrag' = バット（キュー手元）掴み直後の保留状態。最初のドラッグ方向で
  // 仰角（上下）/ショット方向（左右）へ確定する。
  let gesture:
    | 'idle'
    | 'orbit'
    | 'aim'
    | 'elevate'
    | 'cuedrag'
    | 'pinch'
    | 'place' = 'idle';
  let pinchDist = 0;

  // F-10t: ショット系UI（撞点パッド・仰角・縦キュー・退避タブ）の「操作中一時非表示」状態。
  // 方向アロー長押し（aimHoldDir != 0）/ ズーム長押し（zoomHoldDir != 0）/ キャンバスの aim/orbit/pinch
  // 中はフレームループで `shotUiInteractionActive = true` をセットし、解除時に `shotUiRestoreAt` を
  // 設定遅延後に予約する。フレームループ末尾で `shouldTempHide()` が 4 要素の display 制御へ合流。
  // 副作用ゼロの純粋関数は `./shot-ui-temp-hide.ts`、設定値は `getShotUiTempHideMs()`（既定 800ms）。
  let shotUiInteractionActive = false;
  let shotUiRestoreAt = 0;

  // 下見ビュー: 空き盤面タップで rig.setOrbitTarget が呼ばれた後、無操作 1 秒で自動解除する。
  // - 任意の canvas pointer 操作（orbit/pinch/aim/place 等）でタイマーを再充填。
  // - clearPreviewOrbit() は手球タップ・アンドゥ・ショット発射・モード遷移など明示解除でも呼ぶ。
  // - PREVIEW_HARD_MAX_MS は「絶対上限タイマー」: ユーザーが操作し続けても 5 秒で必ず解除する。
  //   iOS の visual viewport zoom 等で異常な視点ロックに陥ったときの強制復旧経路。
  const PREVIEW_IDLE_MS = 1000;
  const PREVIEW_HARD_MAX_MS = 5000;
  let previewOrbitActive = false;
  let previewOrbitTimer: ReturnType<typeof setTimeout> | null = null;
  let previewOrbitHardTimer: ReturnType<typeof setTimeout> | null = null;
  const armPreviewOrbitIdle = () => {
    if (previewOrbitTimer !== null) clearTimeout(previewOrbitTimer);
    previewOrbitTimer = setTimeout(() => {
      previewOrbitTimer = null;
      if (!previewOrbitActive) return;
      clearPreviewOrbit();
    }, PREVIEW_IDLE_MS);
  };
  const setPreviewOrbit = (x: number, y: number, z: number) => {
    // 異常座標ガード: NaN/Infinity または極端な値は無視する。
    // iOS で visual viewport がズームすると `clientX/Y` と canvas の対応関係が崩れ、
    // `pointerToTable` が異常座標を返すことがある。注視点が盤外へロックされる事故を防ぐ。
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
    const SAFE_LIMIT = 2.5; // tableHalfX(=約 1.27) より少し外側まで許容
    if (Math.abs(x) > SAFE_LIMIT || Math.abs(y) > SAFE_LIMIT) return;
    // iOS visual viewport zoom 中は座標系が破綻している可能性が高いためスキップ。
    if (quirks.needsViewportZoomRecovery) {
      const vv = window.visualViewport;
      if (vv && vv.scale > 1.05) return;
    }
    rig.setOrbitTarget(x, y, z);
    previewOrbitActive = true;
    armPreviewOrbitIdle();
    // 絶対上限タイマー: 操作中でも 5 秒で必ず解除（無操作タイマーと並列に走る）。
    // ユーザーがタップを繰り返してアイドルタイマーをリセットし続けても抜け出せるようにする。
    if (previewOrbitHardTimer !== null) clearTimeout(previewOrbitHardTimer);
    previewOrbitHardTimer = setTimeout(() => {
      previewOrbitHardTimer = null;
      if (!previewOrbitActive) return;
      clearPreviewOrbit();
    }, PREVIEW_HARD_MAX_MS);
  };
  const clearPreviewOrbit = () => {
    if (previewOrbitTimer !== null) {
      clearTimeout(previewOrbitTimer);
      previewOrbitTimer = null;
    }
    if (previewOrbitHardTimer !== null) {
      clearTimeout(previewOrbitHardTimer);
      previewOrbitHardTimer = null;
    }
    previewOrbitActive = false;
    rig.clearOrbitTarget();
  };
  const refreshPreviewOrbitIdle = () => {
    if (previewOrbitActive) armPreviewOrbitIdle();
  };

  // ビューアングル自動リセットタイマー（設定 ON 時のみ）。
  // - 各 rig.orbit / rig.zoom / rig.zoomBy 呼び出し直後に armViewResetTimer() を呼ぶ。
  //   既存タイマーをクリアして N 秒（getAutoViewResetSec）で再武装するため、操作が
  //   連続する間は発火しない（最後の操作から N 秒経過で発火）。
  // - 発火時に rig.restoreDefault(cue.aim.angle + Math.PI) を呼び、初期構図へ滑らかに復帰。
  // - cancelViewResetTimer() はショット発射時など、復帰を望まないタイミングで明示解除する。
  // - トップビュー中の topPinch/topPan はこのタイマーをトリガしない（呼び出し側で除外）。
  let viewResetTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelViewResetTimer = (): void => {
    if (viewResetTimer !== null) {
      clearTimeout(viewResetTimer);
      viewResetTimer = null;
    }
  };
  const armViewResetTimer = (): void => {
    cancelViewResetTimer();
    if (!isAutoViewReset()) return;
    const sec = getAutoViewResetSec();
    if (!Number.isFinite(sec) || sec <= 0) return;
    viewResetTimer = setTimeout(() => {
      viewResetTimer = null;
      // 発火タイミングで設定が OFF に変わっていたら何もしない（保険）。
      if (!isAutoViewReset()) return;
      // 手球後方の方位（現在のショット方向の裏側）へ向け直し、仰角を初期値へ補間。
      // 「ズームも含む」ON のときは半径（カメラ距離）も初期値へ戻す。
      rig.restoreDefault(cue.aim.angle + Math.PI, isAutoViewResetZoom());
    }, sec * 1000);
  };

  // ショット軸への掴み許容は「スクリーン空間の px 距離」で判定する。
  // ワールド距離だとズームで見かけ幅が変わり、ズームアウト時に極端に狭くなるため。
  // モジュール公開定数（AIM_GRAB_PX）の別名（drag-guide.ts が同じ値で halo 幅を描画するため）。
  const AIM_GRAB_PX = AIM_GRAB_PX_EXPORT;
  const tmpProj = new THREE.Vector3();
  // ワールド座標 → スクリーン（CSS）座標。visible=false はカメラ後方（投影破綻）。
  const worldToScreen = (
    wx: number,
    wy: number,
    wz: number,
  ): { x: number; y: number; visible: boolean } => {
    tmpProj.set(wx, wy, wz).project(camera);
    const rect = canvas.getBoundingClientRect();
    return {
      x: rect.left + (tmpProj.x * 0.5 + 0.5) * rect.width,
      y: rect.top + (-tmpProj.y * 0.5 + 0.5) * rect.height,
      visible: tmpProj.z < 1,
    };
  };
  // 点(px,py) から線分(ax,ay)-(bx,by) までの距離 [px]。
  const segDistPx = (
    px: number,
    py: number,
    ax: number,
    ay: number,
    bx: number,
    by: number,
  ): number => {
    const vx = bx - ax;
    const vy = by - ay;
    const len2 = vx * vx + vy * vy;
    let t = len2 > 0 ? ((px - ax) * vx + (py - ay) * vy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + vx * t), py - (ay + vy * t));
  };
  // 掴み種別を画面上の近接で判定。'cue'=キュー手元側（仰角ドラッグ）/'aim'=手玉・前方
  // ガイド（ショット方向）/null=どちらでもない。キュー手元は仰角で持ち上がるため z を考慮。
  const grabKind = (cx: number, cy: number): 'cue' | 'aim' | null => {
    const cb = table.cueball.pos;
    const dx = Math.cos(cue.aim.angle);
    const dy = Math.sin(cue.aim.angle);
    const c = worldToScreen(cb.x, cb.y, ballRadius);
    const ballD = Math.hypot(cx - c.x, cy - c.y);
    // キュー手元（仰角で持ち上がった 3D 位置）を**camera 距離でクランプ**して frustum 内に収める。
    // 物理 cue.length=1.5m そのままでは狙撃カム ON（半径≈1.0m）・低仰角で
    // キュー手元が camera 後方へ突き抜け、`Vector3.project()` の透視除算が破綻して
    // 掴み判定が機能しなくなる。`drag-guide.ts effectiveCueLengthForGrab` と完全同期したロジックで
    // 「見える範囲」と「掴める範囲」を一致させる。
    const eff = cue.aim.elevation;
    const cosE = Math.cos(eff);
    const sinE = Math.sin(eff);
    const effLen = effectiveCueLengthForGrab(
      cue.length,
      cb.x,
      cb.y,
      ballRadius,
      camera,
    );
    const a = worldToScreen(
      cb.x - dx * effLen * cosE,
      cb.y - dy * effLen * cosE,
      ballRadius + effLen * sinE,
    );
    const b = worldToScreen(
      cb.x + dx * AIM_FORWARD_LEN_M,
      cb.y + dy * AIM_FORWARD_LEN_M,
      ballRadius,
    );
    const cueD = a.visible ? segDistPx(cx, cy, a.x, a.y, c.x, c.y) : Infinity;
    const fwdD = b.visible ? segDistPx(cx, cy, c.x, c.y, b.x, b.y) : Infinity;
    if (Math.min(ballD, cueD, fwdD) > AIM_GRAB_PX) return null;
    // キュー側が最も近い（かつ手玉中心より近い）→ 仰角優先（横ドラッグなら aim へ後方分岐）。
    // それ以外 → ショット方向（aim 専用）。
    return cueD < fwdD && cueD < ballD ? 'cue' : 'aim';
  };
  // 掴んだ点が後方（キュー側）か（true=キュー側→反対へ回す）。
  let aimFromCue = false;

  /**
   * 現在のポインタ位置から aim 状態を初期化（jump 防止）。
   * cuedrag→aim, elevate→aim（モード切替）, kind='aim' 起点の各経路で共通利用する。
   * `aimFromCueSide=true` のときバット側掴み扱いで回転符号を反転する。
   */
  const initAimFromCurrentPointer = (
    e: PointerEvent,
    aimFromCueSide: boolean,
  ): void => {
    disengageAutoAim(); // 手動 aim 操作 → オートエイム解除
    const cb = table.cueball.pos;
    const a = cue.aim.angle;
    const p = pointerToTable(e.clientX, e.clientY);
    aimStartAngle = a;
    aimPerpX = -Math.sin(a);
    aimPerpY = Math.cos(a);
    if (p) {
      aimGrabX = p.x;
      aimGrabY = p.y;
      aimGrabRadius = Math.max(ballRadius * 4, Math.hypot(p.x - cb.x, p.y - cb.y));
    } else {
      aimGrabX = cb.x;
      aimGrabY = cb.y;
      aimGrabRadius = ballRadius * 4;
    }
    aimFromCue = aimFromCueSide;
    rig.alignToAim(cue.aim.angle);
  };
  /** 現在のポインタ位置から elevate 状態を初期化（jump 防止）。 */
  const initElevateFromCurrentPointer = (e: PointerEvent): void => {
    elevStartY = e.clientY;
    elevStartManual = manualElev;
  };
  // エイムは「掴んだ点を手玉まわりに回す」相対方式。掴み半径が大きい（=的球側で掴む）
  // ほど 1px あたりの回転が小さくなり微調整できる。
  const AIM_GAIN = 0.6; // 全体の回転感度（<1 で控えめ）
  let aimStartAngle = 0; // 掴んだ瞬間の照準角
  let aimGrabX = 0; // 掴んだテーブル平面座標
  let aimGrabY = 0;
  let aimGrabRadius = 1; // 掴み点の手玉からの距離（固定・感度の分母）
  let aimPerpX = 0; // 掴み時の照準方向に直交する単位ベクトル（横移動量の計測軸）
  let aimPerpY = 0;

  // ---- HUD レイアウト編集（ドラッグ移動＋永続化） ----
  // 設定→「HUDレイアウト編集」で起動。各HUD要素を画面の自由位置にドラッグで移動できる。
  //
  // 位置はアンカー方式で保存: 画面中央線で左右/上下のどちら半分にあるかを判定し、
  // その辺からの px オフセットだけを記録する（例: {anchorX:'right', anchorY:'bottom', offsetX:14, offsetY:18}）。
  // 適用時は CSS の left/right/top/bottom を直接指定するため、画面回転・リサイズ時に
  // ブラウザが自動で再配置する＝ portrait で配置したものを landscape でも辺基準で維持できる。
  // 旧バージョンの絶対座標形式は黙って破棄して CSS 既定へフォールバックする。
  //
  // 撞点UI（padWrap）とキュー（cueWrap）は別個のキーで独立保存。ドラッグ中は同じグループとして
  // 連動移動し（cueWrap を掴んでも padWrap が anchor）、ドラッグ確定時に両者の anchor+offset を保存。
  type HudPos = {
    anchorX: 'left' | 'right';
    anchorY: 'top' | 'bottom';
    offsetX: number;
    offsetY: number;
  };
  type MovableEl = { el: HTMLElement; key: string };
  type MovableHud = {
    primary: MovableEl;
    followers: MovableEl[]; // 連動移動かつ独立保存
  };
  const HUD_STORAGE_KEY = 'pool.hud.positions.v2';
  const movableHuds: MovableHud[] = [];
  let hudEditing = false;
  let dragPointerId: number | null = null;
  let dragHud: MovableHud | null = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOrigLeft = 0;
  let dragOrigTop = 0;
  let dragFollowerOffsets: Array<{ el: HTMLElement; dx: number; dy: number }> = [];

  const readHudPositions = (): Record<string, HudPos> => {
    try {
      const raw = localStorage.getItem(HUD_STORAGE_KEY);
      if (!raw) return {};
      const obj = JSON.parse(raw) as unknown;
      if (!obj || typeof obj !== 'object') return {};
      const out: Record<string, HudPos> = {};
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
        if (!v || typeof v !== 'object') continue;
        const o = v as {
          anchorX?: unknown;
          anchorY?: unknown;
          offsetX?: unknown;
          offsetY?: unknown;
        };
        if (
          (o.anchorX === 'left' || o.anchorX === 'right') &&
          (o.anchorY === 'top' || o.anchorY === 'bottom') &&
          typeof o.offsetX === 'number' &&
          typeof o.offsetY === 'number' &&
          Number.isFinite(o.offsetX) &&
          Number.isFinite(o.offsetY)
        ) {
          out[k] = {
            anchorX: o.anchorX,
            anchorY: o.anchorY,
            offsetX: o.offsetX,
            offsetY: o.offsetY,
          };
        }
      }
      return out;
    } catch {
      return {};
    }
  };
  const writeHudPositions = (positions: Record<string, HudPos>): void => {
    try {
      localStorage.setItem(HUD_STORAGE_KEY, JSON.stringify(positions));
    } catch {
      // QuotaExceeded 等は無視
    }
  };
  // 与えられた要素の現在の bounding rect から最も近い辺を選び anchor+offset を算出。
  // 端から少なくとも 8px 内側になるようクランプして、リサイズで完全に画面外へ出ないようにする。
  const computeAnchorPos = (target: HTMLElement): HudPos => {
    const rect = target.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const anchorX: 'left' | 'right' = cx < window.innerWidth / 2 ? 'left' : 'right';
    const anchorY: 'top' | 'bottom' = cy < window.innerHeight / 2 ? 'top' : 'bottom';
    const VISIBILITY_MARGIN = 8;
    const maxOffX = Math.max(VISIBILITY_MARGIN, window.innerWidth - rect.width - VISIBILITY_MARGIN);
    const maxOffY = Math.max(VISIBILITY_MARGIN, window.innerHeight - rect.height - VISIBILITY_MARGIN);
    const rawOffX = anchorX === 'left' ? rect.left : window.innerWidth - rect.right;
    const rawOffY = anchorY === 'top' ? rect.top : window.innerHeight - rect.bottom;
    return {
      anchorX,
      anchorY,
      offsetX: Math.max(VISIBILITY_MARGIN, Math.min(maxOffX, rawOffX)),
      offsetY: Math.max(VISIBILITY_MARGIN, Math.min(maxOffY, rawOffY)),
    };
  };
  // CSS の left/right/top/bottom を直接指定して anchor を反映。
  // hidden 要素にも安全（offsetWidth=0 でもブラウザが正しく配置する）。
  const applyHudAnchorPos = (target: HTMLElement, pos: HudPos): void => {
    if (pos.anchorX === 'left') {
      target.style.left = `${pos.offsetX}px`;
      target.style.right = 'auto';
    } else {
      target.style.left = 'auto';
      target.style.right = `${pos.offsetX}px`;
    }
    if (pos.anchorY === 'top') {
      target.style.top = `${pos.offsetY}px`;
      target.style.bottom = 'auto';
    } else {
      target.style.top = 'auto';
      target.style.bottom = `${pos.offsetY}px`;
    }
  };
  const clearHudInline = (target: HTMLElement): void => {
    target.style.left = '';
    target.style.top = '';
    target.style.right = '';
    target.style.bottom = '';
  };
  const registerMovableHud = (
    elTarget: HTMLElement,
    key: string,
    followers: MovableEl[] = [],
  ): void => {
    elTarget.classList.add('pool-hud-movable');
    for (const f of followers) f.el.classList.add('pool-hud-follower');
    const hud: MovableHud = { primary: { el: elTarget, key }, followers };
    movableHuds.push(hud);
    const positions = readHudPositions();
    // hidden 要素は描画前に offsetWidth=0 のため、初回適用は requestAnimationFrame 後でもよい。
    // ただし CSS の left/right を直接指定する方式は hidden でも問題なく動くので即適用でよい。
    const primarySaved = positions[key];
    if (primarySaved) applyHudAnchorPos(elTarget, primarySaved);
    for (const f of followers) {
      const fSaved = positions[f.key];
      if (fSaved) applyHudAnchorPos(f.el, fSaved);
    }
  };
  const resetHudPositions = (): void => {
    writeHudPositions({});
    for (const hud of movableHuds) {
      clearHudInline(hud.primary.el);
      for (const f of hud.followers) clearHudInline(f.el);
    }
  };
  // 編集モード中のみ、HUD（および follower）に乗ったポインタを掴んでドラッグへ振る。
  // capture フェーズで stopImmediatePropagation して、HUD配下の既存ハンドラ（パッド/キュー
  // /ボタン）に届かないようにする＝編集中はクリックが従来の動作に流れない。
  const findHudForTarget = (target: EventTarget | null): MovableHud | null => {
    if (!(target instanceof Node)) return null;
    for (const hud of movableHuds) {
      if (hud.primary.el.contains(target)) return hud;
      for (const f of hud.followers) if (f.el.contains(target)) return hud;
    }
    return null;
  };
  const onHudPointerDown = (e: PointerEvent): void => {
    if (!hudEditing) return;
    const hud = findHudForTarget(e.target);
    if (!hud) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (dragPointerId !== null) return;
    dragPointerId = e.pointerId;
    dragHud = hud;
    // iOS / Android で pointermove/up を確実に発火させるため、ターゲットへ明示的に
    // ポインターキャプチャを移す。document の capture phase だけだと、タッチ由来 pointer が
    // iOS Safari/WebKit で取りこぼされる既知挙動への対策（Pointer Events 仕様の推奨パターン）。
    if (quirks.needsPointerCapture && e.target instanceof Element) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch {
        // 一部の WebKit 古バージョン等で失敗することがある。document capture でフォールバック。
      }
    }
    const rect = hud.primary.el.getBoundingClientRect();
    dragOrigLeft = rect.left;
    dragOrigTop = rect.top;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragFollowerOffsets = hud.followers.map((f) => {
      const r = f.el.getBoundingClientRect();
      return { el: f.el, dx: r.left - rect.left, dy: r.top - rect.top };
    });
  };
  const onHudPointerMove = (e: PointerEvent): void => {
    if (!hudEditing || dragHud === null || e.pointerId !== dragPointerId) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const dx = e.clientX - dragStartX;
    const dy = e.clientY - dragStartY;
    // ドラッグ中は left/top の絶対座標で動かす（連動が直感的）。確定時に anchor 化して保存する。
    const w = dragHud.primary.el.offsetWidth || 1;
    const h = dragHud.primary.el.offsetHeight || 1;
    const nl = Math.max(0, Math.min(window.innerWidth - w, dragOrigLeft + dx));
    const nt = Math.max(0, Math.min(window.innerHeight - h, dragOrigTop + dy));
    dragHud.primary.el.style.left = `${nl}px`;
    dragHud.primary.el.style.top = `${nt}px`;
    dragHud.primary.el.style.right = 'auto';
    dragHud.primary.el.style.bottom = 'auto';
    for (const o of dragFollowerOffsets) {
      o.el.style.left = `${nl + o.dx}px`;
      o.el.style.top = `${nt + o.dy}px`;
      o.el.style.right = 'auto';
      o.el.style.bottom = 'auto';
    }
  };
  const onHudPointerUp = (e: PointerEvent): void => {
    if (dragPointerId === null || e.pointerId !== dragPointerId) return;
    if (hudEditing && dragHud) {
      const positions = readHudPositions();
      // 確定: 各要素の現在位置から anchor+offset を算出し、anchor で再適用する
      // （以降は CSS の left/right で位置が決まる＝リサイズ追従可能）。
      const primaryPos = computeAnchorPos(dragHud.primary.el);
      positions[dragHud.primary.key] = primaryPos;
      applyHudAnchorPos(dragHud.primary.el, primaryPos);
      for (const f of dragHud.followers) {
        const fp = computeAnchorPos(f.el);
        positions[f.key] = fp;
        applyHudAnchorPos(f.el, fp);
      }
      writeHudPositions(positions);
    }
    dragPointerId = null;
    dragHud = null;
    dragFollowerOffsets = [];
  };
  document.addEventListener('pointerdown', onHudPointerDown, true);
  document.addEventListener('pointermove', onHudPointerMove, true);
  document.addEventListener('pointerup', onHudPointerUp, true);
  document.addEventListener('pointercancel', onHudPointerUp, true);

  // ---- DOM オーバーレイ ----
  const root = el('div', 'pool-ui', document.body);
  // ---- グローバル UI スケール（低解像度・小画面のスケーラブル対応） ----
  // 基準ビューポート 360×780 CSSpx（≒720×1560 @DPR2 = 現行の最適化ターゲット）に対する
  // 自動縮小率（0.65〜1.0・拡大なし）に、設定「UIサイズ倍率」（getUiUserScale: 1.0〜1.3・
  // localStorage 永続）を乗算した最終スケールを :root の --pool-ui-scale に設定する。
  // 小さい画面では HUD 全体が等比で縮み、ユーザー倍率で体感を上乗せ調整できる
  // （例: 自動 0.728 × 1.2 ≈ 0.874。基準画面で 1.3× なら従来比 30% 拡大）。
  // 下限 0.65: タップ目標が物理的に小さくなりすぎないための床
  // （mobile-hud-ux-guidelines.md §1.5。CSS 側の適用対象も同 §1.5 を参照）。
  const UI_SCALE_BASE_W = 360;
  const UI_SCALE_BASE_H = 780;
  const UI_SCALE_MIN = 0.65;
  const updateUiScale = (): void => {
    try {
      const raw = Math.min(
        1,
        window.innerWidth / UI_SCALE_BASE_W,
        window.innerHeight / UI_SCALE_BASE_H,
      );
      const auto = Math.max(UI_SCALE_MIN, raw);
      const scale = Math.round(auto * getUiUserScale() * 1000) / 1000;
      document.documentElement.style.setProperty('--pool-ui-scale', String(scale));
    } catch (err) {
      // 失敗時は CSS 既定値 1（従来表示）のまま継続する。
      console.error('UI スケール更新に失敗:', err);
    }
  };
  updateUiScale();
  // 注意: ここで登録する resize リスナーは updateTopbarCompact（後段で登録）より先に実行され、
  // 圧縮判定が新しいスケール適用後のレイアウトで行われる（リスナーは登録順に発火）。
  window.addEventListener('resize', updateUiScale);
  const topbar = el('div', 'pool-topbar', root);
  // アカウント情報の現在値（main から setAccountInfo で更新される）。
  // 設定パネル末尾の表示用。サーバー権威モードでない場合は表示しない（ニックネームのみあっても
  // ローカルプレイなのでログアウトの概念がない）。
  let accountName: string = '';
  let accountServerAuth: boolean = false; // サーバー認証中か（serverStats 相当）
  const accountSectionRefs: { rebuild: (() => void) | null } = { rebuild: null };
  const settings = createSettings({
    onPlayModeChange: deps.onPlayModeChange,
    onRackChange: deps.onRackChange,
    onEditHud: () => enterHudEditMode(),
    // UIサイズ倍率の変更: 最終スケール再計算＋トップバー圧縮の再判定（拡大で 1 段に
    // 収まらなくなる場合はアイコンのみ表示へ縮退・パレット位置追従も内包）。
    onUiScaleChange: () => {
      updateUiScale();
      updateTopbarCompact();
    },
    ...(deps.onHumidityChange
      ? { onHumidityChange: deps.onHumidityChange }
      : {}),
    mountAccountSection: (parent: HTMLElement): void => {
      // 設定パネル下部に DOM を組み立てる。再描画用関数も保持する。
      // ニックネームが設定されている限り表示する（ローカル/サーバー認証どちらも対応）。
      const rebuild = (): void => {
        while (parent.firstChild) parent.removeChild(parent.firstChild);
        if (accountName.length === 0) {
          // 未ログイン（名前未確定）: セクションを表示しない。
          parent.hidden = true;
          return;
        }
        parent.hidden = false;
        const header = el('div', 'pool-settings-account-header', parent);
        header.textContent = 'アカウント';
        const row = el('div', 'pool-settings-account-row', parent);
        const label = el('span', 'pool-settings-account-label', row);
        label.textContent = 'ニックネーム';
        const value = el('span', 'pool-settings-account-value', row);
        value.textContent = accountName;
        // サーバー認証モードのときだけ「サーバー成績と連動」マーカーを併記。
        if (accountServerAuth) {
          const badge = el('span', 'pool-settings-account-badge', row);
          badge.textContent = 'サーバー';
          badge.title = 'サーバー成績と連動しています';
        }
        const logoutBtn = el('button', 'pool-btn pool-settings-account-logout', parent);
        logoutBtn.textContent = accountServerAuth ? 'ログアウト' : 'ニックネームを変更';
        logoutBtn.addEventListener('click', () => openLogoutConfirm());
      };
      accountSectionRefs.rebuild = rebuild;
      rebuild();
    },
  });

  // ログアウト確認モーダル（pool-modal クラスを継承して既存スタイル流用）。
  // 「ニックネームと PIN の再入力が必要」を明示し、誤操作を防ぐ。
  const logoutModal = el('div', 'pool-modal', document.body);
  logoutModal.hidden = true;
  const logoutBox = el('div', 'pool-modal-box', logoutModal);
  const logoutTitle = el('div', 'pool-modal-title', logoutBox);
  logoutTitle.textContent = 'ログアウト';
  const logoutMsg = el('div', 'pool-modal-msg', logoutBox);
  const logoutActions = el('div', 'pool-modal-actions', logoutBox);
  const logoutOk = el('button', 'pool-btn', logoutActions);
  logoutOk.textContent = 'ログアウト';
  const logoutCancel = el('button', 'pool-modal-sub', logoutActions);
  logoutCancel.textContent = 'キャンセル';
  const openLogoutConfirm = (): void => {
    logoutTitle.textContent = accountServerAuth ? 'ログアウト' : 'ニックネームを変更';
    logoutMsg.textContent = accountServerAuth
      ? `「${accountName}」からログアウトします。次回プレイするにはニックネームと PIN の入力が必要です。`
      : `「${accountName}」を解除します。次回プレイ時に再度ニックネームを入力してください。`;
    logoutOk.textContent = accountServerAuth ? 'ログアウト' : '変更する';
    logoutModal.hidden = false;
  };
  const closeLogoutConfirm = (): void => {
    logoutModal.hidden = true;
  };
  logoutCancel.addEventListener('click', () => closeLogoutConfirm());
  logoutOk.addEventListener('click', () => {
    closeLogoutConfirm();
    // 設定パネルを閉じてから ログアウト処理を実行（ログアウト後の認証モーダルが見やすくなる）。
    settings.toggle(); // 開いている前提（このボタンは設定パネル内なので）
    deps.onLogout?.();
  });

  // HUDレイアウト編集用ツールバー（編集中のみ表示・「完了」「リセット」）
  const hudEditToolbar = el('div', 'pool-hud-edit-toolbar', document.body);
  hudEditToolbar.hidden = true;
  const hudEditHint = el('div', 'pool-hud-edit-hint', hudEditToolbar);
  hudEditHint.textContent = 'HUDをドラッグで移動';
  const hudEditDoneBtn = el('button', 'pool-hud-edit-btn', hudEditToolbar);
  hudEditDoneBtn.textContent = '完了';
  const hudEditResetBtn = el('button', 'pool-hud-edit-btn is-secondary', hudEditToolbar);
  hudEditResetBtn.textContent = 'リセット';
  // 残球エリアの位置を再評価するためのフック（実体は rebuildRoster 近辺で代入）。
  // HUD 編集の完了／HUD 位置リセット時にズーム UI が動いた可能性があるため呼ぶ。
  let onZoomCtrlMoved: (() => void) | null = null;
  const enterHudEditMode = (): void => {
    if (hudEditing) return;
    cancelTimingShot(); // 同時アクティブを避ける（HUD編集とタイミング撞きの操作競合防止）
    hudEditing = true;
    document.body.classList.add('pool-hud-editing');
    hudEditToolbar.hidden = false;
  };
  const exitHudEditMode = (): void => {
    if (!hudEditing) return;
    hudEditing = false;
    document.body.classList.remove('pool-hud-editing');
    hudEditToolbar.hidden = true;
    // ズーム UI が編集中に移動した可能性があるので残球の重なり回避を再計算。
    onZoomCtrlMoved?.();
  };
  hudEditDoneBtn.addEventListener('click', () => exitHudEditMode());
  hudEditResetBtn.addEventListener('click', () => {
    resetHudPositions();
    // 既定位置に戻ったあとも残球の重なり回避を再計算（HUD編集モード中でも有効）。
    onZoomCtrlMoved?.();
  });

  // 手玉の球速表示（左上・ブレイク中のみ＝一打の瞬間最高速度を km/h で）。CSS は index.html の .pool-speed。
  // pointer-events を持たないよう body 直下に置く（表示専用・タッチを奪わない）。
  const speedHud = el('div', 'pool-speed', document.body);
  speedHud.hidden = true;
  const setBallSpeed = (mps: number | null): void => {
    if (mps === null || mps < 0.05) {
      speedHud.hidden = true;
      return;
    }
    speedHud.hidden = false;
    // m/s → km/h（×3.6）で表示。
    speedHud.textContent = `${(mps * 3.6).toFixed(1)} km/h`;
  };
  // カット角 HUD（プラクティス時のみ・自動照準が決まっている時〜ショット完了まで）。CSS は index.html の .pool-cut-angle。
  const cutAngleHud = el('div', 'pool-cut-angle', document.body);
  cutAngleHud.hidden = true;
  const setCutAngle = (deg: number | null): void => {
    if (deg === null) {
      cutAngleHud.hidden = true;
      return;
    }
    cutAngleHud.hidden = false;
    cutAngleHud.textContent = `カット角 ${deg.toFixed(1)}°`;
  };
  // HUD レイアウト編集の対象として登録（位置をドラッグ移動・localStorage 永続化）。
  registerMovableHud(speedHud, 'speed');
  registerMovableHud(cutAngleHud, 'cutAngle');
  // 真上トップビューのトグル（持続。再押下で解除）。
  // 導線確保のためトップバーから分離し、画面左下にテーブル俯瞰アイコンとして配置。
  let topViewOn = false;
  // 配置ボタン押下でトップビューを強制した際の「押下前の状態」を保持し、配置 OFF で復元する。
  let topViewBeforePlacement = false;
  let placementTopViewActive = false; // 配置ボタン起因でトップビューを強制中か
  const topViewBtn = el('button', 'pool-topview', root);
  topViewBtn.setAttribute('aria-label', 'トップビュー切替');
  // トップビュー状態の唯一の設定経路（状態変数・ボタン表示・rig を同期）。
  const setTopView = (on: boolean): void => {
    topViewOn = on;
    topViewBtn.classList.toggle('is-camera', on);
    rig.setTopViewLocked(on);
  };
  topViewBtn.addEventListener('click', () => {
    setTopView(!topViewOn);
  });
  // 配置ボタン ON 時: 現在のトップビュー状態を保存してトップビュー化する。
  const enterPlacementTopView = (): void => {
    if (placementTopViewActive) return;
    topViewBeforePlacement = topViewOn;
    placementTopViewActive = true;
    if (!topViewOn) setTopView(true);
  };
  // 配置ボタン OFF（または配置確定）時: 押下前のトップビュー状態へ戻す。
  const exitPlacementTopView = (): void => {
    if (!placementTopViewActive) return;
    placementTopViewActive = false;
    if (topViewOn !== topViewBeforePlacement) setTopView(topViewBeforePlacement);
  };
  // 視点を元に戻す（手動）。「ビューアングルを自動で戻す」設定 OFF（既定）時に左下へ表示し、
  // 自動リセットと同じ処理（手球後方の方位＋初期仰角へ滑らかに復帰。ズームは設定に従う）を
  // 押下で即実行する。表示制御は描画ループ内（shootable 判定箇所）で行う。
  const recenterBtn = el('button', 'pool-recenter is-off', root);
  recenterBtn.setAttribute('aria-label', '視点を元に戻す');
  recenterBtn.title = '視点を元に戻す';
  let recenterActiveTimer: ReturnType<typeof setTimeout> | null = null;
  recenterBtn.addEventListener('click', () => {
    try {
      rig.restoreDefault(cue.aim.angle + Math.PI, isAutoViewResetZoom());
      recenterBtn.classList.add('is-active');
      if (recenterActiveTimer !== null) clearTimeout(recenterActiveTimer);
      recenterActiveTimer = setTimeout(() => {
        recenterBtn.classList.remove('is-active');
        recenterActiveTimer = null;
      }, 220);
    } catch (err) {
      console.error('視点リセットに失敗', err);
    }
  });
  // トップバーボタンは「アイコン + ラベル」の 2 スパン構造で生成する。
  // 狭幅画面でボタン合計幅が 1 段に収まらない場合、updateTopbarCompact() が .pool-topbar に
  // is-compact を付与してラベルを隠し、アイコンのみで「常に 1 段」を維持する。
  // 段を増やすと直下の固定 UI（ロビー・設定パネル等）と重なるため禁止
  // （knowledge/architecture/mobile-hud-ux-guidelines.md §3 縮退順序）。
  const topbarBtn = (icon: string, label: string): HTMLButtonElement => {
    const btn = el('button', 'pool-btn', topbar);
    const ico = el('span', 'pool-btn-ico', btn);
    ico.textContent = icon;
    if (label) {
      const lab = el('span', 'pool-btn-label', btn);
      lab.textContent = label;
      // compact（アイコンのみ）時もスクリーンリーダー/長押しで機能が分かるように。
      btn.setAttribute('aria-label', label);
      btn.title = label;
    }
    return btn;
  };
  // 配置モード（ブレークの手玉配置 / プラクティスの球配置）トグル。
  const placeBtn = topbarBtn('✋', '配置');
  // 「＋球」（プラクティスの球追加パレット開閉）はトップバーに入れない。
  // 理由: 配置ON時にトップバーのボタン数が 1 つ増えると updateTopbarCompact() が
  // is-compact（アイコンのみ）へ縮退し、全ボタンが縮んで右寄せで位置がズレ、配置トグルの
  // ON/OFF が認識しづらくなるため（2026-06-14d UX 修正）。トップバーは常に固定数・ラベル付きに保ち、
  // ＋球は「プラクティス＋配置モード時のみ」上中央（パレット領域）へ独立表示する。
  const addBtn = el('button', 'pool-btn pool-add-ball', root) as HTMLButtonElement;
  addBtn.type = 'button';
  {
    const ico = el('span', 'pool-btn-ico', addBtn);
    ico.textContent = '＋';
    const lab = el('span', 'pool-btn-label', addBtn);
    lab.textContent = '球を追加';
    addBtn.setAttribute('aria-label', '球を追加');
    addBtn.title = '球を追加';
  }
  // ドリル中のロック（F-30）: 配置モード・球追加・球ドラッグを全面禁止し、固定配置を保証する。
  // syncUndo / setDrillLock が参照するため、トップバーボタン生成より前に宣言する（TDZ 回避）。
  let drillLock = false;
  const resetBtn = topbarBtn('↺', 'リセット');
  // アンドゥ（ショットを撞く直前へ戻す。進行中でも押せる）。
  const undoBtn = topbarBtn('↶', '戻す');
  const syncUndo = (): void => {
    // ドリル中は「↻ やり直し」として常時押下可能（スナップショット有無に依らない）。
    undoBtn.disabled = drillLock ? false : !hasShotState();
  };
  // インベントリ（装備品切替）ボタン。トップバーの「設定」直前に配置。
  // main から setInventoryView でアイテム一覧を渡され、押下でモーダルを開閉する。
  const inventoryBtn = topbarBtn('🎒', '');
  inventoryBtn.title = 'アイテム';
  inventoryBtn.setAttribute('aria-label', 'アイテム');
  inventoryBtn.addEventListener('click', () => {
    invModal.hidden = !invModal.hidden;
    if (!invModal.hidden) renderInventoryPanel();
  });
  // インベントリモーダル本体（pool-modal クラス継承で既存のスタイル流用）。
  const invModal = el('div', 'pool-modal', document.body);
  invModal.hidden = true;
  const invBox = el('div', 'pool-modal-box', invModal);
  const invTitle = el('h3', 'pool-modal-title', invBox);
  invTitle.textContent = '🎒 アイテム';
  // 旧版の説明文（pool-modal-desc）は冗長だったため撤去（タイトル＋ツールバーで意図は伝わる）。

  // ツールバー: 「取得済みのみ」フィルタ + ソート方向トグル。
  // インベントリ UI 内で完結する状態（モーダル閉じてもセッション中は保持・リロードで初期化）。
  const invToolbar = el('div', 'pool-inventory-toolbar', invBox);
  let invOwnedOnly = false; // false=全件 / true=取得済みのみ
  let invSortDir: 'asc' | 'desc' = 'asc'; // asc=易→難（既定） / desc=難→易
  // 取得済みのみフィルタ（チェックボックス）。
  const filterLabel = el('label', 'pool-inventory-filter', invToolbar);
  const filterCheckbox = document.createElement('input');
  filterCheckbox.type = 'checkbox';
  filterCheckbox.checked = invOwnedOnly;
  filterLabel.appendChild(filterCheckbox);
  const filterText = el('span', '', filterLabel);
  filterText.textContent = '取得済みのみ';
  filterCheckbox.addEventListener('change', () => {
    invOwnedOnly = filterCheckbox.checked;
    renderInventoryPanel();
  });
  // ソート方向トグル（ボタン）。テキストで方向を明示する。
  const sortBtn = el('button', 'pool-inventory-sort', invToolbar) as HTMLButtonElement;
  sortBtn.type = 'button';
  const syncSortBtnLabel = (): void => {
    sortBtn.textContent = invSortDir === 'asc' ? '難易度 ↑（易→難）' : '難易度 ↓（難→易）';
  };
  syncSortBtnLabel();
  sortBtn.addEventListener('click', () => {
    invSortDir = invSortDir === 'asc' ? 'desc' : 'asc';
    syncSortBtnLabel();
    renderInventoryPanel();
  });

  const invList = el('div', 'pool-inventory-list', invBox);
  const invCloseBtn = el('button', 'pool-btn', invBox);
  invCloseBtn.textContent = '閉じる';
  invCloseBtn.addEventListener('click', () => {
    invModal.hidden = true;
  });
  let invView: InventoryView = { items: [], onEquip: () => undefined };
  const renderInventoryPanel = (): void => {
    while (invList.firstChild) invList.removeChild(invList.firstChild);
    // フィルタ＋ソート適用（main から渡される items はソート前の素の配列）。
    let rows = invView.items.slice();
    if (invOwnedOnly) rows = rows.filter((it) => it.owned);
    rows.sort((a, b) => {
      const cmp = a.difficulty - b.difficulty;
      const primary = invSortDir === 'asc' ? cmp : -cmp;
      if (primary !== 0) return primary;
      // 同難易度内は装備中→取得済み→未取得→名前順で安定化（UI の毎フレーム揺れ防止）。
      if (a.equipped !== b.equipped) return a.equipped ? -1 : 1;
      if (a.owned !== b.owned) return a.owned ? -1 : 1;
      return a.name < b.name ? -1 : 1;
    });
    if (rows.length === 0) {
      const empty = el('div', 'pool-inventory-empty', invList);
      // 全件モード（invOwnedOnly=false）で 0 件になるのは normally 起動直後の
      // 未読み込み状態のみ（registry に 11 件以上定義されている）。文言は「読み込み中」とし、
      // ユーザーには「サーバー応答待ち」と分かるようにする。
      empty.textContent = invOwnedOnly
        ? '取得済みアイテムはまだありません。CPU プレイヤーを倒したりドリルをクリアすると入手できます。'
        : 'アイテム一覧を読み込み中です…（しばらく待っても表示されない場合はリロードしてください）';
      return;
    }
    for (const it of rows) {
      const row = el('div', 'pool-inventory-row', invList);
      if (it.equipped) row.classList.add('is-equipped');
      if (!it.owned) row.classList.add('is-locked');
      // 取得状況バッジ（装備中／取得済／未取得）。視覚的に状態を即座に判別できるようにする。
      const status = el('span', 'pool-inventory-badge', row);
      if (it.equipped) {
        status.classList.add('is-equipped');
        status.textContent = '装備中';
      } else if (it.owned) {
        status.classList.add('is-owned');
        status.textContent = '取得済';
      } else {
        status.classList.add('is-locked');
        status.textContent = '未取得';
      }
      const nameEl = el('div', 'pool-inventory-name', row);
      nameEl.textContent = it.name;
      const descEl = el('div', 'pool-inventory-desc', row);
      descEl.textContent = it.description;
      // 取得条件は取得済/未取得を問わず常時表示（要件: 「取得条件と取得状況を表示」）。
      const unlockEl = el('div', 'pool-inventory-unlock', row);
      unlockEl.textContent = `取得条件: ${it.unlockText}`;
      if (it.owned) {
        const actionBtn = el('button', 'pool-btn', row);
        actionBtn.textContent = it.equipped ? '解除' : '装備する';
        actionBtn.addEventListener('click', () => {
          invView.onEquip(it.category, it.equipped ? null : it.id);
        });
      }
    }
  };

  // 🎯 ドリル（ソロ・レッスン）ボタン。インベントリの右隣・設定の左に配置（F-30）。
  // 押下で main がドリル選択パネル（src/ui/drill-panel.ts）を開く。
  const drillBtn = topbarBtn('🎯', '');
  drillBtn.title = 'ドリル';
  drillBtn.setAttribute('aria-label', 'ドリル');
  drillBtn.addEventListener('click', () => {
    deps.onOpenDrill?.();
  });

  // 設定ボタンは画面右端（トップバーの最右）に配置。
  const settingsBtn = topbarBtn('⚙', '設定');
  // ⚙ を5秒長押しで管理メニュー（隠し導線・§14）。通常タップは設定トグル。
  const LONGPRESS_MS = 5000;
  let lpTimer = 0;
  let lpFired = false;
  const cancelLongPress = (): void => {
    if (lpTimer) {
      window.clearTimeout(lpTimer);
      lpTimer = 0;
    }
  };
  settingsBtn.addEventListener('pointerdown', () => {
    lpFired = false;
    cancelLongPress();
    lpTimer = window.setTimeout(() => {
      lpFired = true;
      openAdminPw();
    }, LONGPRESS_MS);
  });
  settingsBtn.addEventListener('pointerup', cancelLongPress);
  settingsBtn.addEventListener('pointerleave', cancelLongPress);
  settingsBtn.addEventListener('pointercancel', cancelLongPress);
  settingsBtn.addEventListener('click', (e) => {
    if (lpFired) {
      lpFired = false; // 長押し発火時は設定を開かない
      e.preventDefault();
      return;
    }
    settings.toggle();
  });

  // ---- 管理メニュー（⚙長押し→パスワード→アカウント一覧/削除/編集・§14） ----
  let adminPw = ''; // 認証後にメモリ保持（各操作でサーバー再照合）
  const adminPwModal = el('div', 'pool-modal', document.body);
  adminPwModal.hidden = true;
  const adminPwBox = el('div', 'pool-modal-box', adminPwModal);
  const adminPwTitle = el('div', 'pool-modal-title', adminPwBox);
  adminPwTitle.textContent = '管理メニュー';
  const adminPwInput = el('input', 'pool-modal-input', adminPwBox);
  adminPwInput.type = 'password';
  adminPwInput.placeholder = '管理パスワード（ローカル開発時は空のままで可）';
  const adminPwErr = el('div', 'pool-modal-error', adminPwBox);
  adminPwErr.hidden = true;
  const adminPwActions = el('div', 'pool-modal-actions', adminPwBox);
  const adminPwOk = el('button', 'pool-btn', adminPwActions);
  adminPwOk.textContent = '開く';
  const adminPwCancel = el('button', 'pool-modal-sub', adminPwActions);
  adminPwCancel.textContent = '閉じる';

  const adminPanel = el('div', 'pool-admin', document.body);
  adminPanel.hidden = true;
  const adminHeader = el('div', 'pool-admin-header', adminPanel);
  const adminTitle = el('span', 'pool-admin-title', adminHeader);
  adminTitle.textContent = '🛠 管理パネル';
  // タブ切替: ユーザー管理 / CPU 調整
  const adminTabUsers = el('button', 'pool-admin-btn pool-admin-tab is-active', adminHeader);
  adminTabUsers.textContent = '👥 ユーザー';
  const adminTabCpu = el('button', 'pool-admin-btn pool-admin-tab', adminHeader);
  adminTabCpu.textContent = '🤖 CPU調整';
  const adminRefresh = el('button', 'pool-admin-btn', adminHeader);
  adminRefresh.textContent = '🔄';
  adminRefresh.title = '再読込';
  const adminClose = el('button', 'pool-admin-btn', adminHeader);
  adminClose.textContent = '✕';
  const adminList = el('div', 'pool-admin-list', adminPanel);
  const adminCpuPanel = el('div', 'pool-admin-list pool-admin-cpupanel', adminPanel);
  adminCpuPanel.hidden = true;

  type AdminTab = 'users' | 'cpu';
  const switchAdminTab = (tab: AdminTab): void => {
    if (tab === 'users') {
      adminList.hidden = false;
      adminCpuPanel.hidden = true;
      adminTabUsers.classList.add('is-active');
      adminTabCpu.classList.remove('is-active');
      adminRefresh.hidden = false;
    } else {
      adminList.hidden = true;
      adminCpuPanel.hidden = false;
      adminTabUsers.classList.remove('is-active');
      adminTabCpu.classList.add('is-active');
      adminRefresh.hidden = true;
      renderCpuPanel();
    }
  };
  adminTabUsers.addEventListener('click', () => switchAdminTab('users'));
  adminTabCpu.addEventListener('click', () => switchAdminTab('cpu'));

  // ---- CPU 調整パネル（localStorage オーバーレイ） ----
  type CpuFormState = {
    cpuId: string;
    style: CpuStyle;
    draft: Record<string, unknown>;
  };
  let cpuFormState: CpuFormState | null = null;

  const buildDraftFromConfig = (cfg: StyleConfig): Record<string, unknown> => {
    const draft: Record<string, unknown> = {};
    for (const meta of STYLE_CONFIG_META) {
      draft[meta.key] = cfg[meta.key];
    }
    return draft;
  };

  /** number-array を「0.5, 1.0, 2.0」形式に整形。 */
  const formatNumberArray = (arr: readonly number[]): string =>
    arr.map((n) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, '').replace(/\.$/, ''))).join(', ');

  // parseNumberArray は number-array UI のチップ化に伴い使用箇所が無くなったため削除。

  /** draft と baseline を比較して 1 つでも差分があれば true。 */
  const isDirty = (
    draft: Record<string, unknown>,
    baseline: Record<string, unknown>,
  ): boolean => {
    for (const meta of STYLE_CONFIG_META) {
      const a = draft[meta.key];
      const b = baseline[meta.key];
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return true;
        for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
      } else if (a !== b) {
        return true;
      }
    }
    return false;
  };

  /** CPU プレイヤー追加モーダルを開く。OKで作成して onCreated(newId) を呼ぶ。 */
  const openCpuAddModal = (onCreated: (newId: string) => void): void => {
    const modal = el('div', 'pool-modal', document.body);
    const box = el('div', 'pool-modal-box', modal);
    const title = el('div', 'pool-modal-title', box);
    title.textContent = '新規 CPU プレイヤーを作成';

    const nameWrap = el('label', 'pool-cpu-modal-field', box);
    const nameLbl = el('span', '', nameWrap);
    nameLbl.textContent = '名前';
    const nameInput = el('input', 'pool-modal-input', nameWrap) as HTMLInputElement;
    nameInput.type = 'text';
    nameInput.placeholder = '例: Pro';
    nameInput.maxLength = 16;

    const styleWrap = el('label', 'pool-cpu-modal-field', box);
    const styleLbl = el('span', '', styleWrap);
    styleLbl.textContent = 'ベーススタイル';
    const styleSelect = el('select', 'pool-cpu-select', styleWrap) as HTMLSelectElement;
    const optTricky = el('option', '', styleSelect) as HTMLOptionElement;
    optTricky.value = 'tricky';
    optTricky.textContent = 'tricky（攻撃的・キック/バンク使用）';
    const optSafe = el('option', '', styleSelect) as HTMLOptionElement;
    optSafe.value = 'safe';
    optSafe.textContent = 'safe（保守的・厚撞き重視・セーフティ得意）';

    const err = el('div', 'pool-modal-error', box);
    err.hidden = true;

    const actions = el('div', 'pool-modal-actions', box);
    const okBtn = el('button', 'pool-btn', actions);
    okBtn.textContent = '作成';
    const cancelBtn = el('button', 'pool-modal-sub', actions);
    cancelBtn.textContent = 'キャンセル';

    setTimeout(() => nameInput.focus(), 0);

    const close = (): void => {
      if (modal.parentNode) modal.parentNode.removeChild(modal);
    };
    okBtn.addEventListener('click', () => {
      const name = nameInput.value.trim().slice(0, 16);
      if (name.length === 0) {
        err.textContent = '名前を入力してください';
        err.hidden = false;
        return;
      }
      const existing = getCpuPlayers().some((p) => p.displayName === name);
      if (existing) {
        err.textContent = '同じ名前の CPU プレイヤーが既に存在します';
        err.hidden = false;
        return;
      }
      const newCpu: CpuPlayer = {
        id: generateCpuId(),
        displayName: name,
        style: styleSelect.value === 'safe' ? 'safe' : 'tricky',
      };
      addCpuPlayer(newCpu);
      close();
      onCreated(newCpu.id);
    });
    cancelBtn.addEventListener('click', close);
  };

  const renderCpuPanel = (): void => {
    while (adminCpuPanel.firstChild) adminCpuPanel.removeChild(adminCpuPanel.firstChild);

    const players = getCpuPlayers();

    // 上段ツールバー（追加・組込に戻す）
    const toolbar = el('div', 'pool-cpu-toolbar', adminCpuPanel);
    const addBtn = el('button', 'pool-admin-btn is-primary', toolbar);
    addBtn.textContent = '⊕ プレイヤーを追加';
    const restoreBtn = el('button', 'pool-admin-btn', toolbar);
    restoreBtn.textContent = '↻ 組込に戻す';

    addBtn.addEventListener('click', () => {
      openCpuAddModal((newId) => {
        showToast('プレイヤーを追加しました');
        cpuFormState = { cpuId: newId, style: 'tricky', draft: {} };
        renderCpuPanel();
      });
    });
    restoreBtn.addEventListener('click', () => {
      // 組込プレイヤー名はレジストリ（BUILTIN_CPU_PLAYERS）から導出する（名前のハードコード禁止:
      // .claude/rules/no-hardcoded-context.md。組込変更時に文言が旧名で残るのを防ぐ）。
      const builtinNames = BUILTIN_CPU_PLAYERS.map((p) => p.displayName).join(' / ');
      if (!window.confirm(`CPU プレイヤー一覧を組込（${builtinNames}）に戻します。追加したプレイヤーは消えます（パラメータ調整は保持されます）。よろしいですか？`)) return;
      resetCpuPlayersToBuiltin();
      cpuFormState = null;
      showToast('組込に戻しました');
      renderCpuPanel();
    });

    if (players.length === 0) {
      const empty = el('div', 'pool-admin-empty', adminCpuPanel);
      empty.textContent = 'CPU プレイヤーが登録されていません。「⊕ プレイヤーを追加」または「↻ 組込に戻す」で追加してください。';
      return;
    }

    // CPU 選択 + 名前編集 + 削除
    const selectorRow = el('div', 'pool-cpu-selector', adminCpuPanel);
    const selectorLabel = el('span', 'pool-cpu-selector-label', selectorRow);
    selectorLabel.textContent = 'プレイヤー: ';
    const selectorBox = el('select', 'pool-cpu-select', selectorRow) as HTMLSelectElement;
    for (const p of players) {
      const opt = el('option', '', selectorBox) as HTMLOptionElement;
      opt.value = p.id;
      opt.textContent = `${p.displayName}（${p.style}）`;
    }
    const renameBtn = el('button', 'pool-admin-btn pool-cpu-action-btn', selectorRow);
    renameBtn.textContent = '✏ 名前';
    renameBtn.title = '名前を変更';
    const deleteBtn = el('button', 'pool-admin-btn is-danger pool-cpu-action-btn', selectorRow);
    deleteBtn.textContent = '✕';
    deleteBtn.title = 'このプレイヤーを削除';

    // 初期値（前回の選択を維持・なければ先頭）
    const initialCpu = cpuFormState
      ? players.find((p) => p.id === cpuFormState!.cpuId) ?? players[0]!
      : players[0]!;
    selectorBox.value = initialCpu.id;

    const formWrap = el('div', 'pool-cpu-form', adminCpuPanel);

    const renderForm = (cpuId: string): void => {
      while (formWrap.firstChild) formWrap.removeChild(formWrap.firstChild);
      const cpu = getCpuPlayers().find((p) => p.id === cpuId);
      if (!cpu) return;
      const style = cpu.style;
      // プレイヤー個別のオーバーレイをマージした実効値（= 保存済みの値）
      const current = getCpuConfig(cpuId, style);
      // 比較表示用のスタイルデフォルト
      const defaults = getCpuConfigDefault(style);
      const draft = buildDraftFromConfig(current);
      // dirty 判定のため、保存済みのスナップショットを別途保持。
      const baseline = buildDraftFromConfig(current);
      cpuFormState = { cpuId, style, draft };

      // 説明
      if (cpu.description) {
        const desc = el('div', 'pool-cpu-desc', formWrap);
        desc.textContent = cpu.description;
      }
      const styleNote = el('div', 'pool-cpu-desc', formWrap);
      styleNote.textContent = `style="${style}" — 設定はプレイヤー個別に保存されます`;

      // ボタン参照の先行確保（hoisting 用）。renderField の onChange から触る。
      let saveBtn!: HTMLButtonElement;
      const refreshSaveState = (): void => {
        saveBtn.disabled = !isDirty(draft, baseline);
      };

      // 各フィールド
      for (const meta of STYLE_CONFIG_META) {
        renderField(formWrap, meta, draft, defaults, refreshSaveState);
      }

      // アクションボタン
      const actions = el('div', 'pool-cpu-actions', formWrap);
      saveBtn = el('button', 'pool-admin-btn is-primary', actions) as HTMLButtonElement;
      saveBtn.textContent = '保存';
      saveBtn.disabled = true; // 初期表示は差分なし → 非アクティブ
      const resetBtn = el('button', 'pool-admin-btn is-danger', actions) as HTMLButtonElement;
      resetBtn.textContent = 'デフォルトに戻す';

      saveBtn.addEventListener('click', () => {
        if (saveBtn.disabled) return;
        setCpuConfig(cpuId, style, draft as Partial<StyleConfig>);
        showToast(`${cpu.displayName} の設定を保存しました`);
        renderForm(cpuId); // 再描画でキャッシュ反映
      });
      resetBtn.addEventListener('click', () => {
        if (!window.confirm(`${cpu.displayName} の設定をデフォルト（${style}スタイル）に戻します。よろしいですか？`)) return;
        resetCpuConfig(cpuId);
        showToast('デフォルトに戻しました');
        renderForm(cpuId);
      });
    };

    selectorBox.addEventListener('change', () => renderForm(selectorBox.value));
    renameBtn.addEventListener('click', () => {
      const cpu = getCpuPlayers().find((p) => p.id === selectorBox.value);
      if (!cpu) return;
      const next = window.prompt(`「${cpu.displayName}」の新しい名前を入力してください（最大16文字）`, cpu.displayName);
      if (next === null) return;
      const trimmed = next.trim().slice(0, 16);
      if (trimmed.length === 0) {
        showToast('名前が空のため変更しませんでした');
        return;
      }
      if (trimmed === cpu.displayName) return;
      const dup = getCpuPlayers().some((p) => p.id !== cpu.id && p.displayName === trimmed);
      if (dup) {
        showToast('同じ名前の CPU プレイヤーが既に存在します');
        return;
      }
      updateCpuPlayer(cpu.id, { displayName: trimmed });
      showToast('名前を変更しました');
      renderCpuPanel();
    });
    deleteBtn.addEventListener('click', () => {
      const cpu = getCpuPlayers().find((p) => p.id === selectorBox.value);
      if (!cpu) return;
      if (!window.confirm(`「${cpu.displayName}」を削除します。よろしいですか？\n（保存済みパラメータ調整も同時に削除されます）`)) return;
      deleteCpuPlayer(cpu.id);
      resetCpuConfig(cpu.id); // パラメータ調整も同時に削除
      cpuFormState = null;
      showToast('削除しました');
      renderCpuPanel();
    });

    renderForm(initialCpu.id);
  };

  /** 1 フィールドぶんの UI を生成し、draft への書き込みハンドラを取り付ける。
   *  値が変更されるたびに onChange を呼ぶ（保存ボタン dirty 状態の更新用）。 */
  const renderField = (
    parent: HTMLElement,
    meta: StyleFieldMeta,
    draft: Record<string, unknown>,
    defaults: StyleConfig,
    onChange: () => void,
  ): void => {
    const row = el('div', 'pool-cpu-field', parent);
    const labelWrap = el('div', 'pool-cpu-field-label', row);
    const label = el('span', '', labelWrap);
    label.textContent = meta.label;
    const defaultBadge = el('span', 'pool-cpu-default', labelWrap);
    defaultBadge.textContent = `（デフォルト: ${formatDefaultValue(meta, defaults[meta.key])}）`;

    const inputWrap = el('div', 'pool-cpu-field-input', row);

    if (meta.kind === 'boolean') {
      const cb = el('input', '', inputWrap) as HTMLInputElement;
      cb.type = 'checkbox';
      cb.checked = Boolean(draft[meta.key]);
      cb.addEventListener('change', () => {
        draft[meta.key] = cb.checked;
        onChange();
      });
    } else if (meta.kind === 'number-array') {
      // チップ選択型 UI: meta.chipValues の各値をトグルで選択。
      // draft[meta.key] には選択中の値（昇順）を配列で保持。
      const chipBox = el('div', 'pool-cpu-chips', inputWrap);
      const chipValues = meta.chipValues ?? [];
      const current = draft[meta.key] as readonly number[];
      const selected = new Set<number>();
      for (const v of current) {
        // chipValues に最も近い値にスナップ（旧データ互換）
        if (chipValues.length === 0) {
          selected.add(v);
          continue;
        }
        let best = chipValues[0]!;
        let bestDist = Math.abs(best - v);
        for (const c of chipValues) {
          const d = Math.abs(c - v);
          if (d < bestDist) {
            best = c;
            bestDist = d;
          }
        }
        selected.add(best);
      }
      const formatChipLabel = (n: number): string =>
        Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
      const syncDraft = (): void => {
        const arr = Array.from(selected).sort((a, b) => a - b);
        draft[meta.key] = arr;
      };
      syncDraft();
      const chipButtons: HTMLButtonElement[] = [];
      for (const v of chipValues) {
        const chip = el('button', 'pool-cpu-chip', chipBox) as HTMLButtonElement;
        chip.type = 'button';
        chip.textContent = formatChipLabel(v);
        if (selected.has(v)) chip.classList.add('is-on');
        chip.addEventListener('click', () => {
          if (selected.has(v)) {
            selected.delete(v);
            chip.classList.remove('is-on');
          } else {
            selected.add(v);
            chip.classList.add('is-on');
          }
          syncDraft();
          onChange();
        });
        chipButtons.push(chip);
      }
      // 何も選んでいない状態の警告（保存はできるが推奨ではない）
      if (chipButtons.length > 0 && selected.size === 0) {
        const warn = el('div', 'pool-cpu-hint', row);
        warn.textContent = '⚠ 何も選択されていません（CPU が思考候補を持たない状態）';
      }
    } else {
      // number / angle-deg
      const isAngle = meta.kind === 'angle-deg';
      const currentRaw = draft[meta.key] as number;
      const min = meta.min ?? 0;
      const max = meta.max ?? 10;
      const step = meta.step ?? 0.1;

      const range = el('input', 'pool-cpu-range', inputWrap) as HTMLInputElement;
      range.type = 'range';
      range.min = String(min);
      range.max = String(max);
      range.step = String(step);
      const num = el('input', 'pool-cpu-num', inputWrap) as HTMLInputElement;
      num.type = 'text';

      const toDisplay = (raw: number): string => {
        if (meta.allowInfinity && !Number.isFinite(raw)) return '';
        const v = isAngle ? (raw * 180) / Math.PI : raw;
        return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
      };
      const toRangeValue = (raw: number): number => {
        if (meta.allowInfinity && !Number.isFinite(raw)) return max;
        const v = isAngle ? (raw * 180) / Math.PI : raw;
        return Math.min(max, Math.max(min, v));
      };
      const setRaw = (raw: number, notify: boolean): void => {
        draft[meta.key] = raw;
        range.value = String(toRangeValue(raw));
        num.value = toDisplay(raw);
        if (notify) onChange();
      };

      // 初期表示は notify=false（saveBtn を dirty にしない）。
      setRaw(currentRaw, false);

      range.addEventListener('input', () => {
        const v = Number(range.value);
        const raw = isAngle ? (v * Math.PI) / 180 : v;
        setRaw(raw, true);
      });
      num.addEventListener('change', () => {
        const s = num.value.trim();
        if (s === '' && meta.allowInfinity) {
          setRaw(Infinity, true);
          return;
        }
        const v = Number(s);
        if (!Number.isFinite(v)) {
          showToast('数値を入力してください');
          setRaw(currentRaw, false);
          return;
        }
        const raw = isAngle ? (v * Math.PI) / 180 : v;
        setRaw(raw, true);
      });
    }

    if (meta.hint) {
      const hint = el('div', 'pool-cpu-hint', row);
      hint.textContent = meta.hint;
    }
  };

  const formatDefaultValue = (meta: StyleFieldMeta, v: unknown): string => {
    if (meta.kind === 'boolean') return v ? 'ON' : 'OFF';
    if (meta.kind === 'number-array') return formatNumberArray(v as readonly number[]);
    if (meta.kind === 'angle-deg') {
      const n = v as number;
      const deg = (n * 180) / Math.PI;
      return `${Number.isInteger(deg) ? deg : deg.toFixed(1)}°`;
    }
    const n = v as number;
    if (!Number.isFinite(n)) return '∞';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  const openAdminPw = (): void => {
    adminPwInput.value = '';
    adminPwErr.hidden = true;
    adminPwModal.hidden = false;
    adminPwInput.focus();
  };
  const closeAdmin = (): void => {
    adminPanel.hidden = true;
    adminPw = '';
    // 次回開いた時はユーザータブから始める
    switchAdminTab('users');
  };
  const renderAdmin = (entries: readonly AdminUser[]): void => {
    while (adminList.firstChild) adminList.removeChild(adminList.firstChild);
    const sorted = [...entries].sort((a, b) => b.lastSeen - a.lastSeen);
    if (sorted.length === 0) {
      const empty = el('div', 'pool-admin-empty', adminList);
      empty.textContent = 'アカウントがありません';
      return;
    }
    for (const u of sorted) {
      const row = el('div', 'pool-admin-row', adminList);
      const head = el('div', 'pool-admin-rowhead', row);
      const nm = el('span', 'pool-admin-name', head);
      nm.textContent = u.name;
      nm.title = u.name;
      const meta = el('span', 'pool-admin-meta', head);
      meta.textContent = `${u.wins}勝${u.losses}敗 ・ ${u.devices}台 ${u.hasPin ? '🔒' : ''}`;
      meta.title = u.lastSeen > 0 ? new Date(u.lastSeen).toLocaleString() : '最終オンライン記録なし';
      const editBtn = el('button', 'pool-admin-btn', head);
      editBtn.textContent = '編集';
      const delBtn = el('button', 'pool-admin-btn is-danger', head);
      delBtn.textContent = '削除';
      // 編集フォーム（行内・初期は隠す）。
      const form = el('div', 'pool-admin-edit', row);
      form.hidden = true;
      const mkField = (label: string): HTMLInputElement => {
        const wrap = el('label', 'pool-admin-field', form);
        const l = el('span', '', wrap);
        l.textContent = label;
        return el('input', '', wrap);
      };
      const winIn = mkField('勝');
      winIn.type = 'number';
      winIn.min = '0';
      winIn.value = String(u.wins);
      const loseIn = mkField('敗');
      loseIn.type = 'number';
      loseIn.min = '0';
      loseIn.value = String(u.losses);
      const nameIn = mkField('新名');
      nameIn.type = 'text';
      nameIn.maxLength = 16;
      nameIn.placeholder = '（変更時のみ）';
      const pinWrap = el('label', 'pool-admin-field pool-admin-check', form);
      const pinCb = el('input', '', pinWrap);
      pinCb.type = 'checkbox';
      const pinLbl = el('span', '', pinWrap);
      pinLbl.textContent = 'PINを解除する';
      // 個人ショット分析データ（累計＋日次バケット）を初期化する開発者向けオプション。
      const anaWrap = el('label', 'pool-admin-field pool-admin-check', form);
      const anaCb = el('input', '', anaWrap);
      anaCb.type = 'checkbox';
      const anaLbl = el('span', '', anaWrap);
      anaLbl.textContent = '分析データを初期化する';
      const saveBtn = el('button', 'pool-admin-btn is-primary', form);
      saveBtn.textContent = '保存';
      editBtn.addEventListener('click', () => {
        form.hidden = !form.hidden;
      });
      delBtn.addEventListener('click', () => {
        if (!window.confirm(`「${u.name}」を削除します。よろしいですか？`)) return;
        void (async (): Promise<void> => {
          const r = (await deps.onAdminDelete?.(adminPw, u.name)) ?? { ok: false };
          if (r.ok) {
            showToast('削除しました');
            await refreshAdmin();
          } else showToast('削除に失敗しました');
        })();
      });
      saveBtn.addEventListener('click', () => {
        const patch: {
          wins?: number;
          losses?: number;
          newName?: string;
          clearPin?: boolean;
          clearAnalytics?: boolean;
        } = {};
        const w = Number(winIn.value);
        const l = Number(loseIn.value);
        if (Number.isFinite(w) && w >= 0 && w !== u.wins) patch.wins = Math.floor(w);
        if (Number.isFinite(l) && l >= 0 && l !== u.losses) patch.losses = Math.floor(l);
        const nn = nameIn.value.trim().slice(0, 16);
        if (nn.length > 0 && nn !== u.name) patch.newName = nn;
        if (pinCb.checked) patch.clearPin = true;
        if (anaCb.checked) patch.clearAnalytics = true;
        void (async (): Promise<void> => {
          const r = (await deps.onAdminEdit?.(adminPw, u.name, patch)) ?? { ok: false };
          if (r.ok) {
            showToast('保存しました');
            await refreshAdmin();
          } else if (r.error === 'nameTaken') showToast('その名前は使用済みです');
          else showToast('保存に失敗しました');
        })();
      });
    }
  };
  const refreshAdmin = async (): Promise<void> => {
    const r = (await deps.onAdminList?.(adminPw)) ?? { ok: false };
    if (r.ok && r.entries) renderAdmin(r.entries);
    else {
      closeAdmin();
      showToast('管理一覧を取得できませんでした');
    }
  };
  const submitAdminPw = async (): Promise<void> => {
    // ローカル開発（loopback）では空パスワードでも開けるよう、空文字でもサーバーへ送る。
    // サーバーモードでは空パスワードは ADMIN_PASSWORD 不一致で NG が返り「パスワードが違います」表示。
    const pw = adminPwInput.value;
    adminPwOk.disabled = true;
    try {
      const r = (await deps.onAdminList?.(pw)) ?? { ok: false, error: 'disabled' };
      if (r.ok && r.entries) {
        adminPw = pw;
        adminPwModal.hidden = true;
        renderAdmin(r.entries);
        adminPanel.hidden = false;
        return;
      }
      if (r.locked) {
        adminPwErr.textContent = `ロック中。あと ${Math.ceil((r.retryAfterMs ?? 60000) / 1000)} 秒お待ちください。`;
      } else if (r.error === 'disabled') {
        adminPwErr.textContent = '管理機能は無効です（サーバー未設定）。';
      } else if (r.attemptsLeft != null) {
        adminPwErr.textContent = `パスワードが違います（残り ${r.attemptsLeft} 回）。`;
      } else {
        adminPwErr.textContent = '管理メニューを開けませんでした。';
      }
      adminPwErr.hidden = false;
    } finally {
      adminPwOk.disabled = false;
    }
  };
  adminPwOk.addEventListener('click', () => void submitAdminPw());
  adminPwInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void submitAdminPw();
  });
  adminPwCancel.addEventListener('click', () => {
    adminPwModal.hidden = true;
  });
  adminRefresh.addEventListener('click', () => void refreshAdmin());
  adminClose.addEventListener('click', () => closeAdmin());

  // ---- ルール HUD（手番・ファウル・プッシュアウト導線。上中央・ルールモード時のみ） ----
  const rulesHud = el('div', 'pool-rules', root);
  rulesHud.hidden = true;
  const rulesText = el('div', 'pool-rules-text', rulesHud);
  const pushoutBtn = el('button', 'pool-btn pool-rules-pushout', rulesHud);
  pushoutBtn.textContent = 'プッシュアウト宣言';
  pushoutBtn.hidden = true;
  pushoutBtn.addEventListener('click', () => deps.onDeclarePushOut?.());
  const responseWrap = el('div', 'pool-rules-response', rulesHud);
  responseWrap.hidden = true;
  const takeShotBtn = el('button', 'pool-btn', responseWrap);
  takeShotBtn.textContent = '撞く';
  takeShotBtn.addEventListener('click', () => deps.onPushOutResponse?.(true));
  const giveBackBtn = el('button', 'pool-btn', responseWrap);
  giveBackBtn.textContent = '返す';
  giveBackBtn.addEventListener('click', () => deps.onPushOutResponse?.(false));
  // 勝敗後の導線（オンラインのみ）: 再戦 / ロビーに戻る。
  const gameOverWrap = el('div', 'pool-rules-response', rulesHud);
  gameOverWrap.hidden = true;
  const rematchBtn = el('button', 'pool-btn', gameOverWrap);
  rematchBtn.textContent = '再戦';
  rematchBtn.addEventListener('click', () => deps.onRematch?.());
  const leaveBtn = el('button', 'pool-btn', gameOverWrap);
  leaveBtn.textContent = 'ロビーに戻る';
  leaveBtn.addEventListener('click', () => deps.onLeaveMatch?.());

  let ruleView: RuleView = {
    active: false,
    phase: 'breakPlacement',
    current: 0,
    pushOutDeclared: false,
    message: '',
    gameOver: false,
    online: false,
    inputLocked: false,
    rematchRequested: false,
  };
  // ルール上、ショットを禁止すべき状態か（応答待ち・ゲーム終了・相手の手番）。
  const rulesBlockShoot = (): boolean =>
    ruleView.active &&
    (ruleView.phase === 'pushOutResponse' ||
      ruleView.phase === 'gameOver' ||
      ruleView.inputLocked);
  const renderRuleView = (): void => {
    rulesHud.hidden = !ruleView.active;
    // オンライン時はリセット/戻すを無効化（一方的な再ラック・打ち直し禁止）。
    resetBtn.disabled = ruleView.online;
    if (ruleView.online) undoBtn.disabled = true;
    if (!ruleView.active) return;
    rulesText.textContent = ruleView.message;
    rulesText.hidden = ruleView.message === ''; // 空メッセージ時は出さない（通常進行中）
    // 手番をプレイヤー色で示す（P1=青系 / P2=琥珀系）。
    rulesText.classList.toggle('is-p1', ruleView.current === 0);
    rulesText.classList.toggle('is-p2', ruleView.current === 1);
    // プッシュアウト導線は自分の手番のときだけ出す。
    pushoutBtn.hidden = ruleView.phase !== 'pushOutOption' || ruleView.inputLocked;
    pushoutBtn.classList.toggle('is-camera', ruleView.pushOutDeclared);
    pushoutBtn.textContent = ruleView.pushOutDeclared
      ? 'プッシュアウト宣言済み'
      : 'プッシュアウト宣言';
    responseWrap.hidden = ruleView.phase !== 'pushOutResponse' || ruleView.inputLocked;
    // 勝敗後（オンライン）: 再戦/ロビーに戻る。
    gameOverWrap.hidden = !(ruleView.gameOver && ruleView.online);
    rematchBtn.classList.toggle('is-camera', ruleView.rematchRequested);
    rematchBtn.textContent = ruleView.rematchRequested ? '再戦待機中…' : '再戦';
  };

  // 個人プロファイルパネル（ロビー行クリックで開く・全員参照可）。
  // overlay は document.body 直下（root の上に被せて全画面化）。
  const profilePanel = createProfilePanel();

  // ---- ロビー（在席・マッチメイク・観戦・ニックネーム） ----
  // 設定アイコンの下に「ロビー」ボタンを置く（色＝在席状況）。
  // ロビー入口は独立ボタン（右側・ルールHUDの下段）。トップバーには載せない。
  const lobbyBtn = el('button', 'pool-lobby-btn', root);
  // HUDレイアウト編集の対象に登録（編集中はクリックを抑止しドラッグへ）。
  registerMovableHud(lobbyBtn, 'lobby');
  const lobbyBtnLabel = el('span', 'pool-lobby-btn-label', lobbyBtn);
  lobbyBtnLabel.textContent = 'ロビー';
  // 人数バッジ（自分以外のオンライン人数・0人なら非表示）。
  const lobbyCount = el('span', 'pool-lobby-count', lobbyBtn);
  lobbyCount.hidden = true;
  let lobbyOpen = false;
  // CPU プレビュー中（applyAimPreview 〜 clearAimPreview の間）。true の間は inputLocked でも
  // 撞点パッド・縦キュー UI を見せて「相手の構え」を可視化する。
  let cpuPreviewActive = false;
  const lobbyPanel = el('div', 'pool-lobby', root);
  lobbyPanel.hidden = true;
  const lobbyHeader = el('div', 'pool-lobby-header', lobbyPanel);
  const lobbyName = el('span', 'pool-lobby-name', lobbyHeader);
  const lobbyEditBtn = el('button', 'pool-lobby-edit', lobbyHeader);
  lobbyEditBtn.textContent = '✎ 名前';
  const lobbyCloseBtn = el('button', 'pool-lobby-close', lobbyHeader);
  lobbyCloseBtn.textContent = '✕';
  lobbyCloseBtn.setAttribute('aria-label', 'ロビーを閉じる');
  lobbyCloseBtn.title = 'ロビーを閉じる';
  // 順位リストのツールバー（控えめ）: オンラインのみ表示トグル＋自分を探す。
  const lobbyTools = el('div', 'pool-lobby-tools', lobbyPanel);
  const onlineOnlyLabel = el('label', 'pool-lobby-online-only', lobbyTools);
  const onlineOnlyCb = el('input', '', onlineOnlyLabel);
  onlineOnlyCb.type = 'checkbox';
  // 初期は全員表示（オフライン含む）＝個人プロファイル参照のため誰でも見つけやすく。
  onlineOnlyCb.checked = false;
  let onlineOnly = false;
  const onlineOnlyText = el('span', '', onlineOnlyLabel);
  onlineOnlyText.textContent = 'オンラインのみ';
  const findSelfBtn = el('button', 'pool-lobby-findself', lobbyTools);
  findSelfBtn.textContent = '⌖ 自分';
  findSelfBtn.title = '自分の順位へスクロール';
  // 順位リスト見出し（固定）＋本体。
  // 2026-06-17: 「Last online」列を一覧から削除（プライバシー保護）。最終アクセス日時は
  //   個人プロファイルパネル（行クリックで開く）に移した。Online/Offline は行の左ボーダー強調
  //   （.is-online クラス）＋ Status バッジ（「在席」「対戦中」「オフライン」等）で表現する。
  const lobbyHead = el('div', 'pool-lobby-row pool-lobby-grid pool-lobby-headrow', lobbyPanel);
  for (const [label, cls] of [
    ['Pos', 'pool-lobby-c-pos'],
    ['Name', 'pool-lobby-c-name'],
    ['Win', 'pool-lobby-c-num'],
    ['Lose', 'pool-lobby-c-num'],
    ['Status', 'pool-lobby-c-status'],
  ] as const) {
    const c = el('span', `${cls} pool-lobby-h`, lobbyHead);
    c.textContent = label;
  }
  const lobbyList = el('div', 'pool-lobby-list', lobbyPanel);
  const lobbyWaitStatus = el('div', 'pool-lobby-wait-status', lobbyPanel);
  lobbyWaitStatus.textContent = '🔍 相手をさがしています…';
  lobbyWaitStatus.hidden = true;
  const lobbyWaitBtn = el('button', 'pool-btn pool-lobby-wait', lobbyPanel);
  lobbyWaitBtn.textContent = '🎱 対戦相手をさがす（9ボール）';

  let lobbyView: LobbyView = {
    nickname: '',
    waiting: false,
    iconState: 'gray',
    rows: [],
    inMatch: false,
  };
  const STATUS_LABEL: Record<LobbyRowView['status'], string> = {
    idle: '在席',
    waiting: '待機中',
    playing: '対戦中',
    spectating: '観戦中',
    offline: 'オフライン',
  };
  // 2026-06-17: formatLastOnline / formatLastOnlineFull は個人プロファイル側
  //   （src/ui/profile.ts）へ移設。ロビー一覧から Last online 列を削除したため、
  //   ここでは不要になった。
  let selfRowEl: HTMLElement | null = null;
  // 1行（順位リスト）を描画する。
  const renderRow = (r: LobbyRowView): HTMLElement => {
    const row = el('div', 'pool-lobby-row pool-lobby-grid', lobbyList);
    row.classList.toggle('is-self', r.isSelf);
    row.classList.toggle('is-online', r.online);
    row.classList.toggle('is-offline', !r.online);
    // CPU 行は専用クラスで弱く強調（他人間と並ぶ普通の在席行扱い）。
    row.classList.toggle('is-cpu', !!r.isCpu);
    // 管理人行は専用クラスで控えめに強調（CSS でアクセント色を出す余地を残す）。
    row.classList.toggle('is-admin', !!r.isAdmin);
    const pos = el('span', 'pool-lobby-c-pos', row);
    pos.textContent = r.isCpu ? 'P' : r.pos === null ? '－' : String(r.pos);
    if (r.isCpu) pos.title = 'Pro（CPU）';
    const nm = el('span', 'pool-lobby-c-name', row);
    nm.textContent = r.name || '(名前未設定)';
    nm.title = r.isCpu ? `${r.name}（コンピューター・常時対戦受付）` : r.name;
    const win = el('span', 'pool-lobby-c-num', row);
    const lose = el('span', 'pool-lobby-c-num', row);
    if (r.isCpu) {
      win.textContent = '—';
      lose.textContent = '—';
    } else {
      win.textContent = String(r.wins);
      lose.textContent = String(r.losses);
    }
    // 2026-06-17: 旧 Last online 列（seen）はプライバシー保護のため一覧から削除し、
    //   個人プロファイルパネルへ移した。Online/Offline は .is-online 左ボーダーと Status
    //   バッジで表現する（管理人は .is-admin で名前を黄色強調）。
    // Status 列: CPU 行も他人間と同じく status バッジ（'idle'='在席'）を表示。
    // 専用「挑戦」ボタンは廃止＝ペアリングは「対戦相手をさがす」共通フローへ統合。
    if (r.canSpectate && r.matchId) {
      const spec = el('button', 'pool-lobby-c-status pool-lobby-spec', row);
      spec.textContent = '👁観戦';
      spec.title = '観戦する（対戦中）';
      const mid = r.matchId;
      spec.addEventListener('click', (ev) => {
        ev.stopPropagation();
        deps.onSpectate?.(mid);
      });
    } else {
      const st = el('span', `pool-lobby-c-status pool-lobby-badge is-${r.status}`, row);
      st.textContent = STATUS_LABEL[r.status];
    }
    // 行全体クリック: CPU 行はプロファイルを持たない → 何もしない（誤タップ防止）。
    // 他人間行は従来通り個人プロファイルを開く。
    // 2026-06-17: 最終アクセス（lastSeen）とオンライン状態をプロファイル側で表示するため
    //   コールバックに付加引数として渡す。
    if (!r.isCpu && r.name && r.name.length > 0) {
      row.classList.add('pool-lobby-row-clickable');
      row.title = `${r.name} のプロファイルを開く`;
      row.addEventListener('click', () => {
        deps.onProfileOpen?.(r.name, r.lastSeen, r.online);
      });
    }
    return row;
  };
  const renderLobby = (): void => {
    lobbyBtn.classList.toggle('is-green', lobbyView.iconState === 'green');
    lobbyBtn.classList.toggle('is-blue', lobbyView.iconState === 'blue');
    // 自分が対戦待機中は在席状況の色より優先して緑枠（パネルを閉じていても分かる）
    lobbyBtn.classList.toggle('is-waiting', lobbyView.waiting);
    lobbyName.textContent = lobbyView.nickname || '(名前未設定)';
    // 対戦/観戦中は新たな相手募集を無効化（押せてしまう不具合の防止）。
    lobbyWaitBtn.disabled = lobbyView.inMatch;
    lobbyWaitBtn.textContent = lobbyView.inMatch
      ? '🎱 対戦中'
      : lobbyView.waiting
        ? '待機キャンセル'
        : '🎱 対戦相手をさがす（9ボール）';
    lobbyWaitStatus.hidden = !lobbyView.waiting || lobbyView.inMatch;
    lobbyWaitBtn.classList.toggle('is-active', lobbyView.waiting && !lobbyView.inMatch);
    // 人数バッジ: 自分以外のオンライン**人間**人数。CPU 常駐分は除外（CPU しかいない時に
    // 数字が出続けて緑バッジと相まって過剰に目立つのを防ぐ）。0人ならバッジ非表示。
    const others = lobbyView.rows.filter(
      (r) => r.online && !r.isSelf && !r.isCpu,
    ).length;
    lobbyCount.textContent = String(others);
    lobbyCount.hidden = others === 0;
    lobbyPanel.hidden = !lobbyOpen;
    onlineOnlyCb.checked = onlineOnly;
    if (!lobbyOpen) return;
    selfRowEl = null;
    while (lobbyList.firstChild) lobbyList.removeChild(lobbyList.firstChild);
    // オンラインのみ表示: offline 行を隠す（自分・管理人は常に表示）。
    const visible = lobbyView.rows.filter(
      (r) => r.isSelf || r.online || r.isAdmin || !onlineOnly,
    );
    for (const r of visible) {
      const row = renderRow(r);
      if (r.isSelf) selfRowEl = row;
    }
    // 自分以外に表示できるプレイヤーがいなければ、その旨を下に表示（自分の行は残す）。
    if (visible.every((r) => r.isSelf)) {
      const empty = el('div', 'pool-lobby-empty', lobbyList);
      empty.textContent = '他のプレイヤーはいません';
    }
  };
  // 自分の行へスクロールして一瞬強調する。
  const findSelf = (): void => {
    if (!lobbyOpen) {
      lobbyOpen = true;
      renderLobby();
    }
    if (!selfRowEl) return;
    selfRowEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    selfRowEl.classList.remove('is-flash');
    // 再トリガのため次フレームで付与。
    requestAnimationFrame(() => selfRowEl?.classList.add('is-flash'));
  };
  lobbyBtn.addEventListener('click', () => {
    lobbyOpen = !lobbyOpen;
    if (lobbyOpen) deps.onLobbyOpen?.(); // 開いたら最新の順位表を取得
    renderLobby();
  });
  onlineOnlyCb.addEventListener('change', () => {
    onlineOnly = onlineOnlyCb.checked;
    renderLobby();
  });
  findSelfBtn.addEventListener('click', () => findSelf());
  lobbyCloseBtn.addEventListener('click', () => {
    lobbyOpen = false;
    renderLobby();
  });
  lobbyWaitBtn.addEventListener('click', () => deps.onLobbyWaitingToggle?.());

  // ---- アカウント認証モーダル（名前/新規登録/PIN/ユーザー情報/PIN変更/名前変更・設計§13） ----
  const modal = el('div', 'pool-modal', document.body);
  modal.hidden = true;
  const modalBox = el('div', 'pool-modal-box', modal);
  const modalTitle = el('div', 'pool-modal-title', modalBox);
  const modalMsg = el('div', 'pool-modal-msg', modalBox);
  modalMsg.hidden = true;
  // ログイン / 新規登録 セグメント切替（'name' ステップでのみ表示）。
  // ユーザーが「ログインしたい」「新規登録したい」を明示することで、サーバー応答との不一致を
  // 検知して誤って新規アカウントを作成してしまう事故を防ぐ。
  const modalSeg = el('div', 'pool-modal-seg', modalBox);
  modalSeg.hidden = true;
  const segLoginBtn = el('button', 'pool-modal-seg-btn', modalSeg);
  segLoginBtn.type = 'button';
  segLoginBtn.textContent = 'ログイン';
  const segRegBtn = el('button', 'pool-modal-seg-btn', modalSeg);
  segRegBtn.type = 'button';
  segRegBtn.textContent = '新規登録';
  let authIntent: 'login' | 'register' = 'login';
  const syncSeg = (): void => {
    segLoginBtn.classList.toggle('is-active', authIntent === 'login');
    segRegBtn.classList.toggle('is-active', authIntent === 'register');
  };
  const setAuthIntent = (intent: 'login' | 'register'): void => {
    authIntent = intent;
    syncSeg();
    setErr(null);
    // 意図切替で OK ボタンのテキストも変えてユーザーに現在の操作を明示。
    if (!modal.hidden && step === 'name') {
      modalOk.textContent = intent === 'login' ? 'ログイン' : '次へ（新規登録）';
    }
  };
  segLoginBtn.addEventListener('click', () => setAuthIntent('login'));
  segRegBtn.addEventListener('click', () => setAuthIntent('register'));
  const nameInput = el('input', 'pool-modal-input', modalBox);
  nameInput.type = 'text';
  nameInput.maxLength = 16;
  nameInput.placeholder = '例: Player';
  const pinInput = el('input', 'pool-modal-input pool-modal-pin', modalBox);
  pinInput.type = 'password';
  pinInput.inputMode = 'numeric';
  pinInput.maxLength = 4;
  pinInput.placeholder = 'PIN（4桁）';
  const pin2Input = el('input', 'pool-modal-input pool-modal-pin', modalBox);
  pin2Input.type = 'password';
  pin2Input.inputMode = 'numeric';
  pin2Input.maxLength = 4;
  pin2Input.placeholder = 'PIN（確認）';
  const modalError = el('div', 'pool-modal-error', modalBox);
  modalError.hidden = true;
  const modalActions = el('div', 'pool-modal-actions', modalBox);
  const modalOk = el('button', 'pool-btn', modalActions);
  const modalSub = el('button', 'pool-modal-sub', modalActions);
  const modalSub2 = el('button', 'pool-modal-sub', modalActions);

  type AuthStep = 'name' | 'register' | 'pin' | 'menu' | 'setpin' | 'rename';
  let step: AuthStep = 'name';
  let stepName = ''; // 対象アカウント名（register/pin/setpin）
  let busy = false;
  let lockTimer = 0;

  const validPin = (p: string): boolean => /^\d{4}$/.test(p) && !/^(\d)\1{3}$/.test(p);
  const setErr = (t: string | null): void => {
    modalError.textContent = t ?? '';
    modalError.hidden = !t;
  };
  const clearLockTimer = (): void => {
    if (lockTimer) {
      window.clearInterval(lockTimer);
      lockTimer = 0;
    }
  };
  const closeModal = (): void => {
    clearLockTimer();
    modal.hidden = true;
  };
  // 表示するフィールドを指定（残りは隠す）。
  const layout = (opts: {
    title: string;
    msg?: string;
    seg?: boolean; // ログイン/新規登録 セグメント（'name' step のみ true）
    name?: boolean;
    pin?: boolean;
    pin2?: boolean;
    ok: string;
    sub?: string;
    sub2?: string;
  }): void => {
    clearLockTimer();
    modalTitle.textContent = opts.title;
    modalMsg.textContent = opts.msg ?? '';
    modalMsg.hidden = !opts.msg;
    modalSeg.hidden = !opts.seg;
    if (opts.seg) syncSeg();
    nameInput.hidden = !opts.name;
    pinInput.hidden = !opts.pin;
    pin2Input.hidden = !opts.pin2;
    if (opts.pin) pinInput.value = '';
    if (opts.pin2) pin2Input.value = '';
    setErr(null);
    modalOk.textContent = opts.ok;
    modalOk.disabled = false;
    modalSub.textContent = opts.sub ?? '';
    modalSub.hidden = !opts.sub;
    modalSub2.textContent = opts.sub2 ?? '';
    modalSub2.hidden = !opts.sub2;
    modal.hidden = false;
  };

  const openName = (prefill: string, errMsg?: string): void => {
    step = 'name';
    layout({
      title: authIntent === 'login' ? 'ログイン' : '新規登録',
      // ガイド文: ユーザーがどちらを選んでいるかを毎回明示する。
      msg:
        authIntent === 'login'
          ? 'ニックネームを入力してください（登録済みアカウント）。'
          : 'ニックネームを入力してください（新しいアカウントを作成します）。',
      seg: true,
      name: true,
      ok: authIntent === 'login' ? 'ログイン' : '次へ（新規登録）',
    });
    nameInput.value = prefill;
    if (errMsg) setErr(errMsg); // layout が消した後に表示（理由が見えるように）
    nameInput.focus();
  };
  const openRegister = (name: string): void => {
    step = 'register';
    stepName = name;
    layout({
      title: '新規登録',
      msg: `「${name}」を登録します。PIN（4桁・ゾロ目不可）を設定してください。`,
      pin: true,
      pin2: true,
      ok: '登録',
      sub: '別の名前',
    });
    pinInput.focus();
  };
  const openPin = (name: string, attemptsLeft?: number, retryAfterMs?: number): void => {
    step = 'pin';
    stepName = name;
    const left = attemptsLeft != null ? `（残り ${attemptsLeft} 回）` : '';
    layout({
      title: 'PIN入力',
      msg: `「${name}」は登録済みです。PINを入力してください。${left}`,
      pin: true,
      ok: 'ログイン',
      sub: '別の名前',
    });
    if (retryAfterMs && retryAfterMs > 0) startLockCountdown(retryAfterMs);
    else pinInput.focus();
  };
  const openMenu = (): void => {
    step = 'menu';
    layout({
      title: 'ユーザー情報',
      msg: `名前: ${lobbyView.nickname || '(未設定)'}`,
      ok: '名前変更',
      sub: 'PIN変更',
      sub2: '閉じる',
    });
  };
  const openSetPin = (legacy: boolean): void => {
    step = 'setpin';
    stepName = lobbyView.nickname;
    layout({
      title: legacy ? 'PIN設定' : 'PIN変更',
      msg: legacy
        ? '安全のため PIN（4桁・ゾロ目不可）を設定してください。'
        : '新しい PIN（4桁・ゾロ目不可）を設定してください。',
      pin: true,
      pin2: true,
      ok: '設定',
      sub: '閉じる',
    });
    pinInput.focus();
  };
  const openRename = (): void => {
    step = 'rename';
    layout({
      title: '名前変更',
      msg: '新しい名前を入力（成績は引き継がれます）。',
      name: true,
      ok: '変更',
      sub: '閉じる',
    });
    nameInput.value = lobbyView.nickname;
    nameInput.focus();
  };

  const startLockCountdown = (ms: number): void => {
    clearLockTimer();
    let remain = Math.ceil(ms / 1000);
    modalOk.disabled = true;
    const tick = (): void => {
      if (remain <= 0) {
        clearLockTimer();
        modalOk.disabled = false;
        setErr(null);
        pinInput.focus();
        return;
      }
      setErr(`試行回数を超えました。あと ${remain} 秒お待ちください。`);
      remain -= 1;
    };
    tick();
    lockTimer = window.setInterval(tick, 1000);
  };

  const CONN_ERR = 'サーバーに接続できません。少し待って再試行してください。';
  const branchAuth = (name: string, r: AuthResult): void => {
    if (r.status === 'ok' || r.status === 'local') {
      closeModal();
      if (r.legacyNoPin) openSetPin(true); // PIN未設定→設定を促す
      return;
    }
    // ユーザー意図とサーバー応答の照合（誤って新規アカウントを作成する事故を防ぐ）。
    if (r.status === 'free') {
      if (authIntent === 'login') {
        // ログイン意図なのに「未登録」 → 新規登録への自動遷移を止めてエラー表示。
        openName(
          name,
          'そのニックネームは登録されていません。新規登録の場合は上部の「新規登録」を選んでください。',
        );
        return;
      }
      // 新規登録意図 → PIN設定画面へ（正常）。
      openRegister(name);
      return;
    }
    if (r.status === 'pinRequired') {
      if (authIntent === 'register') {
        // 新規登録意図なのに「登録済み」 → エラー表示（誤って他人のアカウントを作る危険防止）。
        openName(
          name,
          'そのニックネームは既に使われています。ログインの場合は上部の「ログイン」を選んでください。',
        );
        return;
      }
      openPin(name, r.attemptsLeft);
      return;
    }
    if (r.status === 'locked') openPin(name, undefined, r.retryAfterMs);
    else openName(name, CONN_ERR); // 'error' 等＝fail-closed（ログインさせない・再試行）
  };

  // 起動/名前確定の入口。空名は入力モーダル、名前ありは自動ログイン判定。
  const startAuthFlow = (name: string): void => {
    if (!name) {
      openName('');
      return;
    }
    void (async (): Promise<void> => {
      const r = (await deps.onAuthTry?.(name)) ?? { status: 'local' as const };
      branchAuth(name, r);
    })();
  };

  const submit = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    modalOk.disabled = true;
    try {
      if (step === 'name') {
        const name = nameInput.value.trim().slice(0, 16);
        if (!name) {
          setErr('名前を入力してください。');
          return;
        }
        const r = (await deps.onAuthTry?.(name)) ?? { status: 'local' as const };
        branchAuth(name, r);
      } else if (step === 'register') {
        const pin = pinInput.value;
        if (!validPin(pin)) {
          setErr('PIN は4桁の数字（ゾロ目不可）にしてください。');
          return;
        }
        if (pin !== pin2Input.value) {
          setErr('PIN（確認）が一致しません。');
          return;
        }
        const r = (await deps.onRegister?.(stepName, pin)) ?? { status: 'local' as const };
        if (r.status === 'ok' || r.status === 'local') closeModal();
        else if (r.status === 'error') setErr(CONN_ERR);
        else if (r.status === 'nameTaken')
          openName(stepName, 'その名前は使われました。別の名前にしてください。');
        else setErr('PIN が無効です。4桁・ゾロ目不可で設定してください。');
      } else if (step === 'pin') {
        const pin = pinInput.value;
        if (!validPin(pin)) {
          setErr('PIN は4桁の数字です。');
          return;
        }
        const r = (await deps.onAuthPin?.(stepName, pin)) ?? { status: 'local' as const };
        if (r.status === 'ok' || r.status === 'local') closeModal();
        else if (r.status === 'pinWrong') {
          const left = r.attemptsLeft != null ? `（残り ${r.attemptsLeft} 回）` : '';
          setErr(`PIN が違います。${left}`);
          pinInput.value = '';
        } else if (r.status === 'locked') startLockCountdown(r.retryAfterMs ?? 60000);
        else if (r.status === 'error') setErr(CONN_ERR);
        else setErr('ログインできませんでした。');
      } else if (step === 'setpin') {
        const pin = pinInput.value;
        if (!validPin(pin)) {
          setErr('PIN は4桁の数字（ゾロ目不可）にしてください。');
          return;
        }
        if (pin !== pin2Input.value) {
          setErr('PIN（確認）が一致しません。');
          return;
        }
        const r = (await deps.onSetPin?.(pin)) ?? { status: 'local' as const };
        if (r.status === 'ok' || r.status === 'local') {
          closeModal();
          showToast('PIN を設定しました');
        } else if (r.status === 'notOwner') setErr('この端末はログインしていません。');
        else if (r.status === 'error') setErr(CONN_ERR);
        else setErr('PIN が無効です。');
      } else if (step === 'rename') {
        const to = nameInput.value.trim().slice(0, 16);
        if (!to) {
          setErr('新しい名前を入力してください。');
          return;
        }
        const r = (await deps.onRename?.(to)) ?? { status: 'local' as const };
        if (r.status === 'ok' || r.status === 'local') {
          closeModal();
          showToast('名前を変更しました');
        } else if (r.status === 'nameTaken') setErr('その名前は使用済みです。');
        else if (r.status === 'notOwner') setErr('この端末はログインしていません。');
        else if (r.status === 'error') setErr(CONN_ERR);
        else setErr('名前を変更できませんでした。');
      }
    } finally {
      busy = false;
      if (!modal.hidden && lockTimer === 0) modalOk.disabled = false;
    }
  };

  modalOk.addEventListener('click', () => {
    if (step === 'menu') openRename();
    else void submit();
  });
  modalSub.addEventListener('click', () => {
    if (step === 'menu') openSetPin(false);
    else if (step === 'register' || step === 'pin') openName(stepName);
    else closeModal(); // setpin / rename の「閉じる」
  });
  modalSub2.addEventListener('click', () => closeModal());
  const onModalKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && step !== 'menu') void submit();
  };
  nameInput.addEventListener('keydown', onModalKey);
  pinInput.addEventListener('keydown', onModalKey);
  pin2Input.addEventListener('keydown', onModalKey);
  lobbyEditBtn.addEventListener('click', () => openMenu());

  // 通知トースト（切断・コンディション変更など・自動で消える）。
  const toast = el('div', 'pool-toast', root);
  toast.hidden = true;
  let toastTimer = 0;
  const showToast = (text: string): void => {
    toast.textContent = text;
    toast.hidden = false;
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
      toastTimer = 0;
    }, 3500);
  };

  // 対戦開始/終了の大きめバナー（中央寄せ・kind 別色・showToast より目立つ）。
  // 試合の節目（接続/勝敗/退出/切断）をユーザーが見落とさないようにする。
  const matchEvent = el('div', 'pool-match-event', root);
  matchEvent.hidden = true;
  const matchEventTitle = el('div', 'pool-match-event-title', matchEvent);
  const matchEventSub = el('div', 'pool-match-event-sub', matchEvent);
  let matchEventTimer = 0;
  const MATCH_EVENT_KINDS = ['start', 'win', 'lose', 'abort', 'leave'] as const;
  const showMatchEvent = (
    title: string,
    subtitle: string,
    kind: 'start' | 'win' | 'lose' | 'abort' | 'leave',
  ): void => {
    matchEventTitle.textContent = title;
    matchEventSub.textContent = subtitle;
    // kind クラス（is-start / is-win / is-lose / is-abort / is-leave）を排他付与。
    for (const k of MATCH_EVENT_KINDS) matchEvent.classList.remove(`is-${k}`);
    matchEvent.classList.add(`is-${kind}`);
    // 一旦 hidden を解除 → リフロー → アニメーション用クラスを付ける（再表示時に再生される）。
    matchEvent.hidden = false;
    matchEvent.classList.remove('is-show');
    // 強制リフロー（読み取り）でアニメ再生を確実に。
    void matchEvent.offsetWidth;
    matchEvent.classList.add('is-show');
    if (matchEventTimer) window.clearTimeout(matchEventTimer);
    matchEventTimer = window.setTimeout(() => {
      matchEvent.classList.remove('is-show');
      // フェードアウト後に hidden（CSS transition 終了を待つ）。
      window.setTimeout(() => {
        matchEvent.hidden = true;
      }, 350);
      matchEventTimer = 0;
    }, 2600);
  };

  // ---- チャット読み上げ（TTS・Web Speech API・送受信で声を変える） ----
  // 対戦中チャット／ロビーチャットの両方で共有するシングルトン（chat-tts.ts でモジュール定義）。
  // デフォルト OFF・トグルはチャットパネルのヘッダに配置（既存close ✕ の左）。
  // 非対応ブラウザではトグル自体を非表示にする（UI側で isSupported() を判定）。
  // チャットヘッダ用の TTS トグルボタンを生成する小ヘルパ。
  // 引数 parent にトグルボタンを追加し、その要素を返す（非対応時は hidden=true）。
  const makeChatTtsToggle = (parent: HTMLElement): HTMLButtonElement => {
    const btn = el('button', 'pool-chat-tts-toggle', parent) as HTMLButtonElement;
    btn.type = 'button';
    btn.setAttribute('aria-label', 'チャットの読み上げを切り替え');
    const supported = chatTts.isSupported();
    const sync = (): void => {
      const on = chatTts.isEnabled();
      btn.textContent = on ? '🔊' : '🔈';
      btn.title = on ? '読み上げ ON（クリックで OFF）' : '読み上げ OFF（クリックで ON）';
      btn.classList.toggle('is-on', on);
    };
    btn.hidden = !supported;
    if (!supported) return btn;
    sync();
    btn.addEventListener('click', () => {
      chatTts.setEnabled(!chatTts.isEnabled());
      sync();
      // 他方のヘッダのトグルとも見た目同期させるため、カスタムイベントで通知。
      window.dispatchEvent(new CustomEvent('pool:chat-tts-changed'));
    });
    window.addEventListener('pool:chat-tts-changed', sync);
    return btn;
  };

  // ---- 対戦中チャット（定型文＋自由入力・対戦中のみ💬ボタン表示） ----
  let chatEnabled = false;
  let chatOpen = false;
  let lastChatSentAt = 0; // 連投抑制用（ローカルのみ）
  const chatBtn = el('button', 'pool-chat-btn', root);
  chatBtn.textContent = '💬';
  chatBtn.setAttribute('aria-label', 'メッセージ');
  chatBtn.hidden = true;
  const chatPanel = el('div', 'pool-chat-panel', root);
  chatPanel.hidden = true;
  const chatPanelHeader = el('div', 'pool-chat-panel-header', chatPanel);
  const chatPanelTitle = el('span', 'pool-chat-panel-title', chatPanelHeader);
  chatPanelTitle.textContent = 'メッセージ';
  // TTS トグル（close ✕ の左）。非対応環境では hidden。
  makeChatTtsToggle(chatPanelHeader);
  const chatPanelClose = el('button', 'pool-chat-panel-close', chatPanelHeader);
  chatPanelClose.textContent = '✕';
  chatPanelClose.setAttribute('aria-label', '閉じる');
  const chatChips = el('div', 'pool-chat-chips', chatPanel);
  const chatInputWrap = el('div', 'pool-chat-input-wrap', chatPanel);
  const chatInput = el('input', 'pool-chat-input', chatInputWrap);
  chatInput.type = 'text';
  chatInput.maxLength = CHAT_TEXT_MAXLEN;
  chatInput.placeholder = '自由入力…';
  const chatSendBtn = el('button', 'pool-btn pool-chat-send', chatInputWrap);
  chatSendBtn.textContent = '送信';
  // 受信/送信した吹き出しの表示領域（画面下中央付近・自動消去）。
  const chatMsgs = el('div', 'pool-chat-msgs', root);

  const sendChat = (raw: string): void => {
    const text = sanitizeChatText(raw);
    if (!text) return;
    const now = Date.now();
    if (now - lastChatSentAt < 800) return; // 連投抑制
    lastChatSentAt = now;
    // 送信＋自分側の表示は main が担う（送信者名を付けて echo するため）。
    deps.onChat?.(text);
  };
  const renderChatChips = (): void => {
    while (chatChips.firstChild) chatChips.removeChild(chatChips.firstChild);
    const presets = getChatPresets();
    if (presets.length === 0) {
      const hint = el('div', 'pool-chat-empty', chatChips);
      hint.textContent = '定型文は設定（⚙）→「対戦チャット定型文」で登録できます';
      return;
    }
    for (const p of presets) {
      const chip = el('button', 'pool-chat-chip', chatChips);
      chip.textContent = p; // textContent（XSS防止）
      chip.addEventListener('click', () => {
        sendChat(p);
        closeChat();
      });
    }
  };
  const openChat = (): void => {
    if (!chatEnabled) return;
    chatOpen = true;
    renderChatChips();
    chatPanel.hidden = false;
  };
  const closeChat = (): void => {
    chatOpen = false;
    chatPanel.hidden = true;
  };
  chatBtn.addEventListener('click', () => (chatOpen ? closeChat() : openChat()));
  chatPanelClose.addEventListener('click', closeChat);
  chatSendBtn.addEventListener('click', () => {
    sendChat(chatInput.value);
    chatInput.value = '';
    closeChat();
  });
  chatInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      sendChat(chatInput.value);
      chatInput.value = '';
      closeChat();
    }
  });
  // チャット吹き出し表示（最大3件・各4秒で自動消去・textContent で安全描画）。
  // name を渡すと「名前: 本文」で表示（誰が投げたか分かるように）。
  function showChatMessage(
    text: string,
    who: 'me' | 'opponent',
    name?: string,
    voice?: VoiceProfile,
  ): void {
    const t = sanitizeChatText(text);
    if (!t) return;
    const bubble = el('div', `pool-chat-bubble is-${who}`, chatMsgs);
    const label = (name ?? '').trim().slice(0, 16);
    bubble.textContent = label ? `${label}: ${t}` : t;
    while (chatMsgs.childElementCount > 3 && chatMsgs.firstChild) {
      chatMsgs.removeChild(chatMsgs.firstChild);
    }
    window.setTimeout(() => {
      if (bubble.parentNode === chatMsgs) chatMsgs.removeChild(bubble);
    }, 4000);
    // 読み上げ（OFF時 / 非対応時は no-op）。
    // 受信時 voice が同梱されていれば「相手の設定に従う」、無ければ既定で読む。
    chatTts.speak(t, who, voice);
  }
  const setChatEnabled = (enabled: boolean): void => {
    chatEnabled = enabled;
    chatBtn.hidden = !enabled;
    if (!enabled) closeChat();
  };

  // 撞く直前の狙い（角度・撞点）。盤面状態は world のスナップショットが保持。
  let preAim: { angle: number; ox: number; oy: number } | null = null;
  undoBtn.addEventListener('click', () => {
    // ドリル中は「↻ やり直し」＝配置・ショット数・残球を最初からリセット（ショット単位アンドゥ禁止）。
    // これによりショット数を消費せず無限にやり直せる抜け道を塞ぐ（drill-pass-model）。
    if (drillLock) {
      deps.onDrillRestart();
      return;
    }
    if (!restoreShotState(table) || !preAim) return;
    // 狙い・撞点を復元、強さは0へ。カメラはエイム構図へ戻す。
    cue.aim.angle = preAim.angle;
    const r = padBall.getBoundingClientRect().width / 2;
    applySpin(preAim.ox, preAim.oy, r);
    setCuePull(0);
    rig.setOverview(false);
    rig.setFollow(false); // フォローフレーミングを解除（エイム構図へ戻す）
    rig.setAutoTopView(false);
    rig.clearUserOverride();
    clearPreviewOrbit(); // 下見フォーカスも解除
    rig.setFraming(true);
    rig.alignToAim(cue.aim.angle);
    deps.onUndo(); // main: 再生状態（shotActive 等）を解除
    // 1回分のみ戻せる: スナップショットを消費して無効化。
    clearShotState();
    preAim = null;
    syncUndo();
  });
  syncUndo(); // 初期は無効（スナップショット無し）

  const padWrap = el('div', 'pool-pad', root);
  const padBall = el('div', 'pool-pad-ball', padWrap);
  const padBand = el('div', 'pool-pad-band', padBall); // 後方クッションによる下方制限
  const padDot = el('div', 'pool-pad-dot', padBall);

  // ---- 仰角UI（独立 HUD・横並び `[∨] [∧]`＋下に「仰角」値ラベル） ----
  // padWrap と cueWrap の中間に積む（下から: padWrap → elevWrap → cueWrap）。
  // 構造: flex column = [ ボタン行(∨ ∧) , 仰角度ラベル ]。
  // 単タップで 1°ステップ・長押しで連続変更（既存 startElevHold/endElevHold/elevHoldLoop 流用）。
  // 加えて elevWrap 上の水平ドラッグ/フリックでも調整可（右→上げる・左→下げる）。
  // 撞点UI / cueWrap と同じ幅・右端で配置することで横軸センタリングを揃える。
  const elevWrap = el('div', 'pool-elev', root);
  const elevButtonsRow = el('div', 'pool-elev-row', elevWrap);
  const elevDownBtn = el('button', 'pool-elev-arrow down', elevButtonsRow);
  elevDownBtn.type = 'button';
  elevDownBtn.textContent = '∨';
  elevDownBtn.setAttribute('aria-label', '仰角を下げる');
  const elevUpBtn = el('button', 'pool-elev-arrow up', elevButtonsRow);
  elevUpBtn.type = 'button';
  elevUpBtn.textContent = '∧';
  elevUpBtn.setAttribute('aria-label', '仰角を上げる');
  // 仰角度の数値表示（旧来 padWrap 配下から **elevWrap 配下へ移動**＝仰角UI とセットで HUD 編集移動可）。
  const elevLabel = el('div', 'pool-elevation-label', elevWrap);
  elevLabel.textContent = '仰角 0°';

  // 縦キュー（撞点UI＋仰角UI の上に積まれる）: 下へ引き下げて離すと発射。
  // 旧来あった `pool-cue-zone-elev/pool-cue-zone-pull` のゾーン分割は廃止し、cueTrack 全体が
  // pull ドラッグ範囲に戻った（仰角は独立した elevWrap で扱う）。
  const cueWrap = el('div', 'pool-cue', root);
  const cueTrack = el('div', 'pool-cue-track', cueWrap);
  const cueStick = el('div', 'pool-cue-stick', cueTrack);
  el('div', 'pool-cue-tip', cueStick);
  // 旧来の小さい動く ⌄（cueArrow）は display:none で非表示残置（pull-chevrons に置換済）。
  const cueArrow = el('div', 'pool-cue-arrow', cueTrack);
  cueArrow.textContent = '⌄';
  cueArrow.setAttribute('aria-hidden', 'true');
  // 「引いて撞く」アフォーダンス: ∨ シェブロン 3 つを縦に並べてマーチング・アニメ
  // （pointer-events:none・装飾のみ）。cueTrack 直下の子（ゾーン廃止後）。
  const cuePullChevrons = el('div', 'pool-cue-pull-chevrons', cueTrack);
  cuePullChevrons.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 3; i++) {
    const ch = el('span', 'pool-cue-pull-chevron', cuePullChevrons);
    ch.textContent = '∨';
  }
  const pullLabelHint = el('span', 'pool-cue-pull-label', cueTrack);
  pullLabelHint.textContent = '引いて撞く';
  // 旧 `pool-cue-hint`（cueWrap 配下・display:none）は維持（DOM 参照を壊さない）。
  const cueHint = el('div', 'pool-cue-hint', cueWrap);
  cueHint.textContent = '引いて撞く';

  // HUDレイアウト編集の対象に登録。3 ユニット（spinPad / elev / cueTrack）はそれぞれ独立ドラッグ可。
  // padWrap には仰角ラベル＋padScaleCtrl を子として含む（DOM 構造として一緒に動く）。
  // elevWrap は新規ユニット。cueWrap は slider+cue のみに集約。
  registerMovableHud(padWrap, 'spinPad', []);
  registerMovableHud(elevWrap, 'elev', []);
  registerMovableHud(cueWrap, 'cueTrack', []);

  // ---- ショットUIトグル: 撞点UI / 仰角UI / ショットUI（縦キュー）をまとめて画面右にスライドアウトして隠す ----
  // 用途: 静止盤面で HUD が視界の邪魔になるときに一時退避。再表示時は元の位置（HUD編集アンカー含む）へ戻る。
  // 配置: 右端アンカーの横長ピル（既定=ロビーボタンの少し下）。HUDレイアウト編集中は非表示（編集を妨げない）。
  // 永続化: localStorage `pool.shotUiHidden`（'1' で隠した状態を次回起動時にも復元）。
  // 注意: ショット中/相手手番中の完全非表示（hideShotUiHard: display:none）とは独立。配置中は
  //       placementCollapse で同じ右収納（pool-shot-ui-hidden）に合流し、タブは「表示 «」で残る。
  const SHOT_UI_HIDDEN_KEY = 'pool.shotUiHidden';
  const loadShotUiHidden = (): boolean => {
    try {
      return localStorage.getItem(SHOT_UI_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  };
  const saveShotUiHidden = (v: boolean): void => {
    try {
      localStorage.setItem(SHOT_UI_HIDDEN_KEY, v ? '1' : '0');
    } catch {
      /* localStorage 不可（プライベートブラウズ等）でも UI 動作は継続。 */
    }
  };
  const shotUiToggleBtn = el('button', 'pool-shot-ui-toggle', root);
  shotUiToggleBtn.type = 'button';
  let shotUiHidden = loadShotUiHidden(); // 永続: ユーザーが明示的に隠した状態
  // 一時: プラクティス配置モード中の自動収納（永続しない＝配置を抜ければ元の表示状態へ戻る）。
  // 配置中はショットUIを「右へ収納」しつつ退避タブは残し、復帰導線を確保する（2026-06-15）。
  let placementCollapse = false;
  // 実効収納状態 = ユーザーが隠した or 配置中の自動収納。どちらでも右収納＆タブを「表示 «」化する。
  const isShotUiCollapsed = (): boolean => shotUiHidden || placementCollapse;
  let lastAppliedCollapse: boolean | null = null; // 状態変化時のみ再構築するためのキャッシュ
  const applyShotUiAppearance = (): void => {
    const collapsed = isShotUiCollapsed();
    if (collapsed === lastAppliedCollapse) return; // 変化時のみ DOM 再構築（毎フレーム呼んでも安全）
    lastAppliedCollapse = collapsed;
    document.body.classList.toggle('pool-shot-ui-hidden', collapsed);
    // 構成は常に [アイコン][ラベル][シェブロン]（アイコンを先頭に固定して「何を」操作するか明示）。
    // 表示中:「[撞点] 隠す »」(右へ収納) / 隠れ中:「[撞点] 表示 «」(右から引き出す)。
    // innerHTML 禁止のため span を組み立てる（アイコンは CSS background-image の SVG）。
    while (shotUiToggleBtn.firstChild) {
      shotUiToggleBtn.removeChild(shotUiToggleBtn.firstChild);
    }
    const icon = document.createElement('span');
    icon.className = 'pool-shot-ui-icon';
    const labelSpan = document.createElement('span');
    labelSpan.textContent = collapsed ? '表示' : '隠す';
    const chevron = document.createElement('span');
    chevron.className = 'pool-shot-ui-chevron';
    chevron.textContent = collapsed ? '«' : '»';
    shotUiToggleBtn.append(icon, labelSpan, chevron);
    const label = collapsed ? 'ショットUIを表示' : 'ショットUIを隠す';
    shotUiToggleBtn.title = label;
    shotUiToggleBtn.setAttribute('aria-label', label);
    shotUiToggleBtn.setAttribute('aria-pressed', collapsed ? 'true' : 'false');
  };
  applyShotUiAppearance();
  // ---- トグルタブの縦ドラッグ移動（HUD編集モード不要・通常時いつでも可） ----
  // 既定位置は CSS（ロビーボタンの少し下・右端アンカー）。ドラッグで上下に動かし、
  // 「タブ中心 Y / ビューポート高さ」の比率で永続化する＝画面回転・リサイズでも相対位置を
  // 維持し、適用時に毎回ビューポート内へクランプして画面外に出ない
  // （mobile-hud-ux-guidelines.md §2 不変条件2: 固定 HUD は画面外にはみ出さない）。
  // タップ（移動量 6px 未満）は従来どおり開閉トグルとして動作する。
  const SHOT_UI_TOGGLE_POS_KEY = 'pool.shotUiTogglePos';
  const TOGGLE_EDGE_MARGIN = 8; // 上下端に残す最小マージン [px]
  const toggleHeight = (): number => shotUiToggleBtn.offsetHeight || 36;
  const clampToggleTop = (top: number): number => {
    const max = window.innerHeight - toggleHeight() - TOGGLE_EDGE_MARGIN;
    return Math.min(Math.max(top, TOGGLE_EDGE_MARGIN), Math.max(TOGGLE_EDGE_MARGIN, max));
  };
  const applyToggleTopPx = (top: number): void => {
    shotUiToggleBtn.style.top = `${Math.round(clampToggleTop(top))}px`;
    shotUiToggleBtn.style.transform = 'none'; // CSS 既定の translateY(-50%) を解除
  };
  const applyToggleRatio = (ratio: number): void => {
    applyToggleTopPx(ratio * window.innerHeight - toggleHeight() / 2);
  };
  const loadToggleRatio = (): number | null => {
    try {
      const raw = localStorage.getItem(SHOT_UI_TOGGLE_POS_KEY);
      if (raw === null) return null;
      const v = Number(raw);
      return Number.isFinite(v) && v > 0 && v < 1 ? v : null;
    } catch {
      return null;
    }
  };
  const saveToggleRatio = (ratio: number): void => {
    try {
      localStorage.setItem(SHOT_UI_TOGGLE_POS_KEY, String(ratio));
    } catch {
      /* localStorage 不可でもドラッグ自体（セッション内の移動）は有効のまま継続。 */
    }
  };
  let shotUiToggleRatio = loadToggleRatio();
  if (shotUiToggleRatio !== null) applyToggleRatio(shotUiToggleRatio);
  window.addEventListener('resize', () => {
    if (shotUiToggleRatio !== null) applyToggleRatio(shotUiToggleRatio);
  });
  let toggleDragMoved = false; // ドラッグ直後に発火する click（トグル誤動作）を抑止するフラグ
  shotUiToggleBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggleDragMoved) {
      toggleDragMoved = false; // ドラッグ終了に伴う click はトグルしない
      return;
    }
    // 配置モード中の「表示」押下 = 配置を解除してショットUIを出す（通常操作と同じ感覚）。
    // ✋配置ボタンに加えてこのタブからも配置を抜けられる二重導線（2026-06-15）。
    if (placementMode && isPractice()) {
      commitPlacement(); // 配置解除＋autoAim 再発動（撞ける状態へ）
      placementCollapse = false;
      if (shotUiHidden) {
        // ユーザーが事前に隠していた場合も「表示」の意図どおりショットUIを出す。
        shotUiHidden = false;
        saveShotUiHidden(false);
      }
      applyShotUiAppearance();
      return;
    }
    shotUiHidden = !shotUiHidden;
    saveShotUiHidden(shotUiHidden);
    applyShotUiAppearance();
  });
  {
    let dragPointer: number | null = null;
    let dragStartY = 0;
    let dragStartTop = 0;
    // capture: true で登録する（重要）。同一要素では capture リスナーがバブルリスナーより
    // 先に実行されるため、バブル登録だと下の stopPropagation（capture）に先回りされて
    // pointerdown が届かない（DOM 仕様: 同一ノードでも capture サブフェーズで
    // stopPropagation されるとバブルサブフェーズのリスナーは呼ばれない）。
    shotUiToggleBtn.addEventListener(
      'pointerdown',
      (e) => {
        // HUD編集モードの document capture ハンドラ等に流さない（旧 stopPropagation 専用
        // リスナーの役割をここに統合）。click によるトグル動作には影響しない。
        e.stopPropagation();
        dragPointer = e.pointerId;
        dragStartY = e.clientY;
        dragStartTop = shotUiToggleBtn.getBoundingClientRect().top;
        toggleDragMoved = false;
        try {
          shotUiToggleBtn.setPointerCapture(e.pointerId);
        } catch {
          /* capture 非対応環境でもタップ（トグル）は従来通り動作する。 */
        }
      },
      true,
    );
    shotUiToggleBtn.addEventListener('pointermove', (e) => {
      if (dragPointer !== e.pointerId) return;
      const dy = e.clientY - dragStartY;
      if (!toggleDragMoved && Math.abs(dy) < 6) return; // タップ判定の遊び
      toggleDragMoved = true;
      applyToggleTopPx(dragStartTop + dy);
    });
    const endToggleDrag = (e: PointerEvent): void => {
      if (dragPointer !== e.pointerId) return;
      dragPointer = null;
      if (!toggleDragMoved) return;
      const r = shotUiToggleBtn.getBoundingClientRect();
      if (window.innerHeight > 0) {
        shotUiToggleRatio = (r.top + r.height / 2) / window.innerHeight;
        saveToggleRatio(shotUiToggleRatio);
      }
    };
    shotUiToggleBtn.addEventListener('pointerup', endToggleDrag);
    shotUiToggleBtn.addEventListener('pointercancel', endToggleDrag);
  }

  // ---- 撞点パッドのサイズ調整（HUDレイアウト編集モード中のみ操作可・永続化） ----
  // CSS変数 --pool-pad-scale を padBall に設定し、base サイズ(28.6vmin/169px=旧22vmin/130pxの1.3倍)
  // に対する倍率で表示。デフォルト 1.0（=100%）。0.6〜2.5 を10%刻みで調整可能。
  const PAD_SCALE_KEY = 'pool.hud.padScale';
  const PAD_SCALE_MIN = 0.6;
  const PAD_SCALE_MAX = 2.5;
  const PAD_SCALE_STEP = 0.1;
  const PAD_SCALE_DEFAULT = 1.0;
  const loadPadScale = (): number => {
    try {
      const v = localStorage.getItem(PAD_SCALE_KEY);
      if (v === null) return PAD_SCALE_DEFAULT;
      const n = parseFloat(v);
      if (!Number.isFinite(n) || n < PAD_SCALE_MIN || n > PAD_SCALE_MAX) return PAD_SCALE_DEFAULT;
      return n;
    } catch {
      return PAD_SCALE_DEFAULT;
    }
  };
  const savePadScale = (s: number): void => {
    try {
      localStorage.setItem(PAD_SCALE_KEY, String(s));
    } catch {
      // QuotaExceeded 等は無視（次回起動でデフォルトへ戻る）
    }
  };
  let padScale = loadPadScale();
  const applyPadScale = (): void => {
    // body に昇格して全要素から参照可能にする（兄弟要素=矢印UI等が撞点パッド
    // の高さに追従してレイアウト調整できるようにするため）。
    // ★`.pool-pad-ball` 等の消費側で `--pool-pad-scale` をローカル宣言してはならない
    //   （宣言すると body 継承値を上書き＝シャドーイングして倍率が球に効かなくなる）。
    //   消費側はフォールバックを var(--pool-pad-scale, 1) の第2引数で与える。
    document.body.style.setProperty('--pool-pad-scale', String(padScale));
  };
  applyPadScale();
  // サイズ調整UI（編集モード中のみ可視: CSS の body.pool-hud-editing で制御）。
  const padScaleCtrl = el('div', 'pool-pad-scale-ctrl', padWrap);
  const padScaleMinus = el('button', 'pool-pad-scale-btn', padScaleCtrl);
  padScaleMinus.type = 'button';
  padScaleMinus.textContent = '−';
  padScaleMinus.setAttribute('aria-label', '撞点パッドを縮小');
  const padScaleLabel = el('span', 'pool-pad-scale-label', padScaleCtrl);
  const padScalePlus = el('button', 'pool-pad-scale-btn', padScaleCtrl);
  padScalePlus.type = 'button';
  padScalePlus.textContent = '＋';
  padScalePlus.setAttribute('aria-label', '撞点パッドを拡大');
  const refreshPadScaleLabel = (): void => {
    padScaleLabel.textContent = `${Math.round(padScale * 100)}%`;
  };
  const changePadScale = (delta: number): void => {
    // 浮動小数の累積誤差を避けるため 10 倍整数で計算
    const next = Math.round((padScale + delta) * 10) / 10;
    const clamped = Math.max(PAD_SCALE_MIN, Math.min(PAD_SCALE_MAX, next));
    if (clamped === padScale) return;
    padScale = clamped;
    applyPadScale();
    savePadScale(padScale);
    refreshPadScaleLabel();
  };
  refreshPadScaleLabel();
  padScaleMinus.addEventListener('click', (ev) => {
    ev.stopPropagation();
    changePadScale(-PAD_SCALE_STEP);
  });
  padScalePlus.addEventListener('click', (ev) => {
    ev.stopPropagation();
    changePadScale(PAD_SCALE_STEP);
  });
  // HUD編集中は document の capture で pointerdown を奪っているため、
  // ボタンの pointerdown を伝播させるとサイズ調整ではなくドラッグになってしまう。
  // stopPropagation して capture フェーズより前に止める（addEventListener も capture 指定）。
  const padScaleStopProp = (ev: Event): void => ev.stopPropagation();
  const padScaleBtns: HTMLButtonElement[] = [padScaleMinus, padScalePlus];
  for (const b of padScaleBtns) {
    b.addEventListener('pointerdown', padScaleStopProp, true);
    b.addEventListener('pointerup', padScaleStopProp, true);
  }

  // ---- ショット UI（縦キュー）のサイズ調整（HUDレイアウト編集モード中のみ操作可・永続化） ----
  // 撞点パッドと同じパターン。CSS 変数 `--pool-cue-scale` を body に設定し、`.pool-cue-track` の
  // height/max-height が `calc(... * var(--pool-cue-scale))` で連動する。
  // 0.6〜2.5 を 10% 刻みで調整・既定 1.0。
  const CUE_SCALE_KEY = 'pool.hud.cueScale';
  const CUE_SCALE_MIN = 0.6;
  const CUE_SCALE_MAX = 2.5;
  const CUE_SCALE_STEP = 0.1;
  const CUE_SCALE_DEFAULT = 1.0;
  const loadCueScale = (): number => {
    try {
      const v = localStorage.getItem(CUE_SCALE_KEY);
      if (v === null) return CUE_SCALE_DEFAULT;
      const n = parseFloat(v);
      if (!Number.isFinite(n) || n < CUE_SCALE_MIN || n > CUE_SCALE_MAX) return CUE_SCALE_DEFAULT;
      return n;
    } catch {
      return CUE_SCALE_DEFAULT;
    }
  };
  const saveCueScale = (s: number): void => {
    try {
      localStorage.setItem(CUE_SCALE_KEY, String(s));
    } catch {
      // QuotaExceeded 等は無視
    }
  };
  let cueScale = loadCueScale();
  const applyCueScale = (): void => {
    document.body.style.setProperty('--pool-cue-scale', String(cueScale));
  };
  applyCueScale();
  const cueScaleCtrl = el('div', 'pool-cue-scale-ctrl', cueWrap);
  const cueScaleMinus = el('button', 'pool-cue-scale-btn', cueScaleCtrl);
  cueScaleMinus.type = 'button';
  cueScaleMinus.textContent = '−';
  cueScaleMinus.setAttribute('aria-label', 'ショット UI を縮小');
  const cueScaleLabel = el('span', 'pool-cue-scale-label', cueScaleCtrl);
  const cueScalePlus = el('button', 'pool-cue-scale-btn', cueScaleCtrl);
  cueScalePlus.type = 'button';
  cueScalePlus.textContent = '＋';
  cueScalePlus.setAttribute('aria-label', 'ショット UI を拡大');
  const refreshCueScaleLabel = (): void => {
    cueScaleLabel.textContent = `${Math.round(cueScale * 100)}%`;
  };
  const changeCueScale = (delta: number): void => {
    const next = Math.round((cueScale + delta) * 10) / 10;
    const clamped = Math.max(CUE_SCALE_MIN, Math.min(CUE_SCALE_MAX, next));
    if (clamped === cueScale) return;
    cueScale = clamped;
    applyCueScale();
    saveCueScale(cueScale);
    refreshCueScaleLabel();
  };
  refreshCueScaleLabel();
  cueScaleMinus.addEventListener('click', (ev) => {
    ev.stopPropagation();
    changeCueScale(-CUE_SCALE_STEP);
  });
  cueScalePlus.addEventListener('click', (ev) => {
    ev.stopPropagation();
    changeCueScale(CUE_SCALE_STEP);
  });
  const cueScaleStopProp = (ev: Event): void => ev.stopPropagation();
  for (const b of [cueScaleMinus, cueScalePlus]) {
    b.addEventListener('pointerdown', cueScaleStopProp, true);
    b.addEventListener('pointerup', cueScaleStopProp, true);
  }

  // ---- ショット方向 アロー（画面下・中央対称・内側=微調整／外側=大移動） ----
  // 配置順（左→右）: 大左(◀◀)・小左(◀)・小右(▶)・大右(▶▶)
  // 内側=微調整(0.15°/タップ・10°/s 長押し) / 外側=大移動(1.5°/タップ・40°/s 長押し)
  // 設定「方向アロー・ズームボタン」OFF（body.pool-no-control-ui）で一括非表示。
  const AIM_TAP_STEP = 0.0026; // 単タップの微小回転 [rad]（≈0.15°）
  const AIM_HOLD_DELAY = 250; // 長押し連続回転を開始するまでの猶予 [ms]
  const AIM_HOLD_RATE = 0.18; // 長押し中の回転速度 [rad/s]（≈10°/s）
  const AIM_BIG_TAP_STEP = 0.026; // 外側ボタンの単タップ回転 [rad]（≈1.5°）
  const AIM_BIG_HOLD_RATE = 0.7; // 外側ボタンの長押し回転速度 [rad/s]（≈40°/s）

  const aimWrap = el('div', 'pool-aim', root);
  const aimLeftBigBtn = el('button', 'pool-aim-btn is-big', aimWrap);
  aimLeftBigBtn.textContent = '◀◀';
  aimLeftBigBtn.setAttribute('aria-label', 'ショット方向を左へ大きく移動');
  const aimLeftBtn = el('button', 'pool-aim-btn', aimWrap);
  aimLeftBtn.textContent = '◀';
  aimLeftBtn.setAttribute('aria-label', 'ショット方向を左へ微調整');
  const aimRightBtn = el('button', 'pool-aim-btn', aimWrap);
  aimRightBtn.textContent = '▶';
  aimRightBtn.setAttribute('aria-label', 'ショット方向を右へ微調整');
  const aimRightBigBtn = el('button', 'pool-aim-btn is-big', aimWrap);
  aimRightBigBtn.textContent = '▶▶';
  aimRightBigBtn.setAttribute('aria-label', 'ショット方向を右へ大きく移動');

  // エイム可能か（静止 & 手球が盤上 & 相手の手番でない）。縦キューと同条件。
  const aimAdjustable = (): boolean =>
    deps.isResting() && table.cueball.onTable() && !ruleView.inputLocked;

  // 方向角を delta だけ回し、カメラをエイム追従させる（ドラッグ時と同じ挙動）。
  const rotateAim = (delta: number): void => {
    disengageAutoAim(); // 手動でショット方向を操作 → オートエイム解除
    cue.aim.angle += delta;
    rig.setFraming(true);
    rig.alignToAim(cue.aim.angle);
  };

  // 長押し連続回転（rAF）。dir: +1=左(angle増) / -1=右(angle減)。
  // 内側/外側でレート（rad/s）が異なるため holdRate は startAimHold から渡される。
  let aimHoldDir = 0;
  let aimHoldStart = 0;
  let aimHoldLast = 0;
  let aimHoldRaf = 0;
  let aimHoldRate = AIM_HOLD_RATE;

  const aimHoldLoop = (): void => {
    if (aimHoldDir === 0) return;
    const now = performance.now();
    const dt = (now - aimHoldLast) / 1000;
    aimHoldLast = now;
    // 猶予を過ぎてから連続回転（単タップだけなら何もしない）。
    if (now - aimHoldStart >= AIM_HOLD_DELAY) {
      rotateAim(aimHoldDir * aimHoldRate * dt);
    }
    aimHoldRaf = requestAnimationFrame(aimHoldLoop);
  };

  const endAimHold = (): void => {
    if (aimHoldDir === 0) return;
    aimHoldDir = 0;
    if (aimHoldRaf) {
      cancelAnimationFrame(aimHoldRaf);
      aimHoldRaf = 0;
    }
  };

  // tapStep/holdRate を引数で受け、内側/外側どちらでも同じハンドラを使えるように。
  const startAimHold = (
    dir: number,
    e: PointerEvent,
    btn: HTMLButtonElement,
    tapStep: number,
    holdRate: number,
  ): void => {
    if (!aimAdjustable()) return;
    commitPlacement(); // 配置モード中ならここで構えへ遷移（autoAim ON なら即発動）
    e.preventDefault();
    e.stopPropagation();
    endAimHold(); // 多重押下の保険
    aimHoldDir = dir;
    aimHoldRate = holdRate;
    aimHoldStart = performance.now();
    aimHoldLast = aimHoldStart;
    try {
      btn.setPointerCapture(e.pointerId);
    } catch (err) {
      // ポインタキャプチャ非対応/失効時もタップ処理自体は継続する。
      console.warn('aim button: setPointerCapture failed', err);
    }
    rotateAim(dir * tapStep); // 単タップ分を即時適用
    aimHoldRaf = requestAnimationFrame(aimHoldLoop);
  };

  aimLeftBtn.addEventListener('pointerdown', (e) =>
    startAimHold(1, e, aimLeftBtn, AIM_TAP_STEP, AIM_HOLD_RATE),
  );
  aimRightBtn.addEventListener('pointerdown', (e) =>
    startAimHold(-1, e, aimRightBtn, AIM_TAP_STEP, AIM_HOLD_RATE),
  );
  aimLeftBigBtn.addEventListener('pointerdown', (e) =>
    startAimHold(1, e, aimLeftBigBtn, AIM_BIG_TAP_STEP, AIM_BIG_HOLD_RATE),
  );
  aimRightBigBtn.addEventListener('pointerdown', (e) =>
    startAimHold(-1, e, aimRightBigBtn, AIM_BIG_TAP_STEP, AIM_BIG_HOLD_RATE),
  );
  for (const b of [aimLeftBtn, aimRightBtn, aimLeftBigBtn, aimRightBigBtn]) {
    b.addEventListener('pointerup', endAimHold);
    b.addEventListener('pointercancel', endAimHold);
  }

  // ---- ズーム ⊕/⊖ ボタン（両ショット入力モードで常時表示・左端・残球エリアの下に縦並び） ----
  // 旧仕様（タイミング撞きモード時のみ撞点パッド上方に表示）は廃止し、引いて撞くモードでも
  // 利用できるよう既定位置を左端へ移動した。HUD レイアウト編集（registerMovableHud）で
  // ユーザーが自由に動かせる（localStorage 永続）。
  // 設定「方向アロー・ズームボタン」OFF（body.pool-no-control-ui）で全体非表示。
  // 撞ける時のみ表示する .is-off 制御は従来通り（ショット中/相手手番/ボールインハンド前等で非表示）。
  // ⊕=寄る（半径×0.9）/⊖=引く（半径×1/0.9）。長押しで 1 秒あたり ×1.5/×0.667 倍。
  const ZOOM_STEP_IN = 0.9;
  const ZOOM_STEP_OUT = 1 / 0.9;
  const ZOOM_RATE_LOG = Math.log(1.5); // 長押し: 1秒あたり 1.5 倍/0.667 倍

  const zoomCtrl = el('div', 'pool-zoom-ctrl', root);
  const zoomInBtn = el('button', 'pool-zoom-btn', zoomCtrl);
  zoomInBtn.textContent = '⊕';
  zoomInBtn.setAttribute('aria-label', 'ビューを拡大');
  const zoomOutBtn = el('button', 'pool-zoom-btn', zoomCtrl);
  zoomOutBtn.textContent = '⊖';
  zoomOutBtn.setAttribute('aria-label', 'ビューを縮小');

  let zoomHoldDir = 0; // +1=in(拡大) / -1=out(縮小)
  let zoomHoldStart = 0;
  let zoomHoldLast = 0;
  let zoomHoldRaf = 0;
  // 長押し連続ズームが一度でも適用されたら true。pointerup 時に「単タップ
  // ステップを追加するか」の判定に使う。単タップは pointerup で1回適用＝
  // 長押し時の「タップ瞬間にガクッと飛んでから連続」違和感を解消。
  let zoomContinuousApplied = false;

  const zoomHoldLoop = (): void => {
    if (zoomHoldDir === 0) return;
    const now = performance.now();
    const dt = (now - zoomHoldLast) / 1000;
    zoomHoldLast = now;
    if (now - zoomHoldStart >= AIM_HOLD_DELAY) {
      // dir=+1(in) → 倍率<1（寄る） / dir=-1(out) → 倍率>1（引く）
      const signed = zoomHoldDir === 1 ? -dt : dt;
      rig.zoomBy(Math.exp(ZOOM_RATE_LOG * signed));
      armViewResetTimer();
      zoomContinuousApplied = true; // 連続発動中＝単タップとして扱わない
    }
    zoomHoldRaf = requestAnimationFrame(zoomHoldLoop);
  };

  const endZoomHold = (): void => {
    if (zoomHoldDir === 0) return;
    const dir = zoomHoldDir;
    const continuous = zoomContinuousApplied;
    zoomHoldDir = 0;
    zoomContinuousApplied = false;
    if (zoomHoldRaf) {
      cancelAnimationFrame(zoomHoldRaf);
      zoomHoldRaf = 0;
    }
    // 長押し連続が走っていなければ「単タップ」とみなして1ステップ適用。
    // pointercancel 経由でもここに来るが、その場合も自然な単タップ扱いで問題なし。
    if (!continuous) {
      rig.zoomBy(dir > 0 ? ZOOM_STEP_IN : ZOOM_STEP_OUT);
      armViewResetTimer();
    } else {
      // 連続ズーム中のリリース直後も「最後の操作」から N 秒で復帰させる。
      armViewResetTimer();
    }
  };

  const startZoomHold = (
    dir: number,
    e: PointerEvent,
    btn: HTMLButtonElement,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    endZoomHold();
    zoomHoldDir = dir;
    zoomHoldStart = performance.now();
    zoomHoldLast = zoomHoldStart;
    zoomContinuousApplied = false;
    try {
      btn.setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('zoom button: setPointerCapture failed', err);
    }
    // 単タップステップは押下時には実行しない（pointerup で連続未発動なら適用）。
    // 長押し開始時のガクッと飛ぶ違和感を防止。
    zoomHoldRaf = requestAnimationFrame(zoomHoldLoop);
  };
  zoomInBtn.addEventListener('pointerdown', (e) => startZoomHold(1, e, zoomInBtn));
  zoomOutBtn.addEventListener('pointerdown', (e) =>
    startZoomHold(-1, e, zoomOutBtn),
  );
  for (const b of [zoomInBtn, zoomOutBtn]) {
    b.addEventListener('pointerup', endZoomHold);
    b.addEventListener('pointercancel', endZoomHold);
  }

  // HUD レイアウト編集対象に登録（ドラッグで自由に位置を変更・localStorage 永続）。
  // 既定 CSS 位置は左端・残球エリアの下。引いて撞くモード/タイミング撞きモードのどちらでも
  // 同じ位置に出るため、好みで右側へ移動したいユーザーは編集モードで動かせる。
  registerMovableHud(zoomCtrl, 'zoomCtrl');

  // ---- 撞点を構え既定（中央やや下）へ戻す ----
  // ド真ん中ではなく DEFAULT_SPIN_Y だけ下に置く（軽い引き/ストップ寄りで的玉ヒット後の
  // 前進を抑える）。レール際で下が撞けない場合は tick() の自動退避で「最も近い撞点」へ寄る。
  const resetSpinDefault = (): void => {
    setDesiredSpin(0, DEFAULT_SPIN_Y);
  };

  resetBtn.addEventListener('click', () => {
    cancelTimingShot(); // タイミング撞き進行中ならリセット
    resetSpinDefault();
    deps.onReset();
  });

  // ---- 縦キュー（引き下げショット） ----
  let cueDragging = false;
  let cueDragStartY = 0; // ドラッグ開始時のポインタ Y
  let cuePull = 0; // 現在の引き量(px)
  const cuePullMax = (): number => cueTrack.clientHeight * CUE_PULL_RATIO;

  const canShoot = (): boolean =>
    deps.isResting() && table.cueball.onTable() && !rulesBlockShoot();

  const setCuePull = (pull: number): void => {
    const max = cuePullMax();
    cuePull = Math.max(0, Math.min(max, pull));
    cueStick.style.transform = `translate(-50%, ${cuePull}px)`;
    const dead = max * CUE_DEADZONE;
    const ratio = cuePull <= dead ? 0 : (cuePull - dead) / (max - dead);
    cue.aim.power = ratio * effectiveMaxPower();
  };

  cueTrack.addEventListener('pointerdown', (e) => {
    if (!canShoot()) return;
    commitPlacement(); // 配置モード中ならここで構えへ遷移（autoAim ON なら即発動）
    cueDragging = true;
    cueDragStartY = e.clientY;
    cueTrack.setPointerCapture(e.pointerId);
    cueWrap.classList.add('is-dragging');
    e.preventDefault();
    e.stopPropagation();
  });
  cueTrack.addEventListener('pointermove', (e) => {
    if (!cueDragging) return;
    setCuePull(e.clientY - cueDragStartY);
  });

  // 共通の発射ルーチン。呼び出し時点で cue.aim.power が決まっている前提。
  // 引き撞き／タイミング撞きのどちらからも呼ぶ。
  const performShot = (): void => {
    // ショット力が確定した瞬間: オートエイム中なら実際の強さで角度を最終補正（きらめき）。
    recorrectAutoAim(true);
    // 撞く直前の盤面・狙いをスナップショット（アンドゥ用）。
    saveShotState(table);
    preAim = {
      angle: cue.aim.angle,
      ox: cue.aim.offset.x,
      oy: cue.aim.offset.y,
    };
    syncUndo();
    clearPreviewOrbit(); // 下見フォーカスを解除（再生は通常追従）
    // 撞く直前（全球静止）のスナップショットを取る機会を main に与える（オンライン再生用）。
    deps.onBeforeStrike?.();
    // ブレイクショット時のみ、現在の手玉位置をユーザーカスタムとして localStorage に保存する。
    // 配置モードで動かした位置をそのままお気に入りとして次回ラックへ引き継ぐ（既定位置のままでも上書き）。
    // 範囲外（盤外・ヘッドストリング越え）は saveBreakCuePos 側で無視される。
    if (isBreakShotNow()) {
      const cb = cueBallPos(table);
      saveBreakCuePos(deps.getPlayMode(), cb.x, cb.y);
    }
    cue.hit(table.cueball);
    // スクワート（撞点ズレによる飛び）を初速へ反映。速度=power×cos(仰角)。
    applySquirt(
      table.cueball.vel,
      cue.aim.offset.x,
      cue.aim.power * Math.cos(cue.aim.elevation),
      getSquirt(),
    );
    // 打撃の瞬間（手球に速度付与済み）にショット音を鳴らす。main の発火検出
    // （次フレーム）まで待たないことで約1フレームの遅延を解消する。
    deps.onShoot?.();
    // 手動トップビュー（左下ボタン）はショット発射で自動 OFF にする。
    // 期待動作: ボタン押下で ON ⇄ OFF・ON 中にショット → OFF。
    // 背景: main.ts onShoot で `rig.setTopViewLocked(false)` を呼んでも rig のロックは解れる一方、
    //   controls 側の `topViewOn` とボタン CSS（is-camera）は据え置きになり状態がズレていた。
    //   `setTopView(false)` は唯一の同期経路（状態変数＋CSS＋rig を 1 関数で揃える）。
    if (topViewOn) setTopView(false);
    rig.resetTopZoom(); // ショット実行でトップビューのズーム/パンを既定（全体）へ戻す
    cancelViewResetTimer(); // ショット発射中は自動リセットを抑止（カメラの自動演出を優先）
    // ブレーク成立。配置モード/ボールインハンドを終了し、以後の手玉配置を禁止。
    broken = true;
    placementMode = false;
    ballInHand = false;
    paletteOpen = false;
    syncPlacementUi();
  };

  const cueEnd = (): void => {
    if (!cueDragging) return;
    cueDragging = false;
    cueWrap.classList.remove('is-dragging');
    const max = cuePullMax();
    const dead = max * CUE_DEADZONE;
    if (cuePull > dead && canShoot()) {
      performShot();
    } else {
      cue.aim.power = 0;
    }
    setCuePull(0); // キューを最上（デフォルト位置）へ戻す
  };
  cueTrack.addEventListener('pointerup', cueEnd);
  cueTrack.addEventListener('pointercancel', cueEnd);

  // ---- 仰角UI 横ドラッグ/フリック：水平方向で仰角を調整（右→上げる、左→下げる） ----
  // ボタン上のクリックは個別ハンドラ（startElevHold）で処理されるためここに来ない。
  // 起点は現在の有効仰角 max(manualElev, autoMinElev) で、autoMinElev 張り付き時もすぐ反応。
  let elevWrapDragging = false;
  let elevWrapStartX = 0;
  let elevWrapStartManual = 0;
  elevWrap.addEventListener('pointerdown', (e) => {
    if (!canShoot()) return;
    if (e.target instanceof Element && e.target.closest('.pool-elev-arrow')) {
      return; // 矢印ボタンは個別ハンドラで処理
    }
    commitPlacement();
    elevWrapDragging = true;
    elevWrapStartX = e.clientX;
    elevWrapStartManual = Math.max(manualElev, autoMinElev);
    elevWrap.setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  });
  elevWrap.addEventListener('pointermove', (e) => {
    if (!elevWrapDragging) return;
    const right = e.clientX - elevWrapStartX; // 右へドラッグ＝仰角を上げる
    setManualElevation(elevWrapStartManual + right * ELEV_DRAG_GAIN);
  });
  const endElevWrapDrag = (): void => {
    elevWrapDragging = false;
  };
  elevWrap.addEventListener('pointerup', endElevWrapDrag);
  elevWrap.addEventListener('pointercancel', endElevWrapDrag);

  // ---- elev 矢印ボタン（∧/∨）：単タップで 1° ステップ・長押しで連続変更 ----
  // 既存のズーム ⊕⊖ / アロー ◀▶ と同じパターン（AIM_HOLD_DELAY 以降 raf で連続適用）。
  const ELEV_STEP = (1 * Math.PI) / 180; // 1° per tap
  const ELEV_RATE = (30 * Math.PI) / 180; // 30°/sec on hold
  let elevHoldDir = 0; // +1=up / -1=down / 0=idle
  let elevHoldStart = 0;
  let elevHoldLast = 0;
  let elevHoldRaf = 0;
  let elevHoldContinuous = false;
  // 仰角操作の起点：現在の有効仰角 max(manualElev, autoMinElev)。
  // manualElev が autoMinElev より低いと操作が視覚的に反映されないため、有効値を起点にする。
  const effectiveManualElev = (): number => Math.max(manualElev, autoMinElev);
  const elevHoldLoop = (): void => {
    if (elevHoldDir === 0) return;
    const now = performance.now();
    const dt = (now - elevHoldLast) / 1000;
    elevHoldLast = now;
    if (now - elevHoldStart >= AIM_HOLD_DELAY) {
      setManualElevation(effectiveManualElev() + elevHoldDir * ELEV_RATE * dt);
      elevHoldContinuous = true;
    }
    elevHoldRaf = requestAnimationFrame(elevHoldLoop);
  };
  const endElevHold = (): void => {
    if (elevHoldDir === 0) return;
    const dir = elevHoldDir;
    const continuous = elevHoldContinuous;
    elevHoldDir = 0;
    elevHoldContinuous = false;
    if (elevHoldRaf) {
      cancelAnimationFrame(elevHoldRaf);
      elevHoldRaf = 0;
    }
    if (!continuous) {
      // 単タップ＝ 1° ステップ（有効仰角を起点に増減）。
      setManualElevation(effectiveManualElev() + dir * ELEV_STEP);
    }
  };
  const startElevHold = (dir: number, e: PointerEvent, btn: HTMLButtonElement): void => {
    if (!canShoot()) return;
    e.preventDefault();
    e.stopPropagation();
    endElevHold();
    elevHoldDir = dir;
    elevHoldStart = performance.now();
    elevHoldLast = elevHoldStart;
    elevHoldContinuous = false;
    try {
      btn.setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('elev arrow: setPointerCapture failed', err);
    }
    elevHoldRaf = requestAnimationFrame(elevHoldLoop);
  };
  elevUpBtn.addEventListener('pointerdown', (e) => startElevHold(1, e, elevUpBtn));
  elevDownBtn.addEventListener('pointerdown', (e) => startElevHold(-1, e, elevDownBtn));
  for (const b of [elevUpBtn, elevDownBtn]) {
    b.addEventListener('pointerup', endElevHold);
    b.addEventListener('pointercancel', endElevHold);
  }

  // ---- 撞点パッド ----
  let padActive = false;
  // ユーザーが意図した撞点（制約をかける前の値）。レール際で適用値が上方へ抑制されても
  // これは保持し、レールから離れて制約が緩むと毎フレーム再拘束してデフォルトへ自然復帰させる。
  // （適用値 cue.aim.offset だけだと一方向に押し上がったまま貼り付くため）。
  const desiredSpin = { x: 0, y: DEFAULT_SPIN_Y };
  // 後方近接球による撞点の禁止円（offset 空間 [-0.5,0.5]）。tick() で毎フレーム再計算。
  let forbiddenDisks: SpinForbiddenDisk[] = [];
  const padMaskEls: HTMLDivElement[] = []; // 禁止円描画用の再利用プール
  // 後方クッション近接による撞点 offset.y の下限（-0.5 で制限なし）。
  let railMinY = -0.5;

  // offset を「禁止円の外」かつ「下方バンド(offset.y>=railMinY)の上」へ拘束。
  const constrainSpin = (ox: number, oy: number): { ox: number; oy: number } => {
    let y = Math.max(oy, railMinY);
    const pushed = pushOutOfForbidden(ox, y);
    y = Math.max(pushed.oy, railMinY);
    return { ox: pushed.ox, oy: y };
  };

  // offset 座標 (ox,oy) を全禁止円の外へ押し出す（最も近い境界へ）。
  const pushOutOfForbidden = (
    ox: number,
    oy: number,
  ): { ox: number; oy: number } => {
    let x = ox;
    let y = oy;
    for (let iter = 0; iter < 8; iter++) {
      let moved = false;
      for (const disk of forbiddenDisks) {
        const ddx = x - disk.cx;
        const ddy = y - disk.cy;
        const dist = Math.hypot(ddx, ddy);
        if (dist < disk.r) {
          moved = true;
          if (dist < 1e-6) {
            // 円中心ちょうど: 上方向（+oy）へ退避。
            x = disk.cx;
            y = disk.cy + disk.r;
          } else {
            x = disk.cx + (ddx / dist) * disk.r;
            y = disk.cy + (ddy / dist) * disk.r;
          }
        }
      }
      if (!moved) break;
    }
    return { ox: x, oy: y };
  };

  // offset を適用（パッド円内へクランプ → ドット & cue.setSpin）。
  const applySpin = (ox: number, oy: number, r: number): void => {
    let x = ox;
    let y = oy;
    const m = Math.hypot(x, y);
    if (m > 0.5) {
      x = (x / m) * 0.5;
      y = (y / m) * 0.5;
    }
    padDot.style.transform = `translate(${-x * 2 * r}px, ${-y * 2 * r}px)`;
    cue.setSpin(new THREE.Vector3(x, y, 0), table);
  };

  // パッドのドット位置を「現在の適用済み撞点（cue.aim.offset）」から再描画する。
  // applySpin がパッド非表示中（幅0）に呼ばれると transform が中央に化けるため、
  // パッドが表示に切り替わった瞬間に本関数で正しい位置へ描き直す。
  const renderPadDot = (): void => {
    const r = padBall.getBoundingClientRect().width / 2;
    if (r <= 0) return; // まだ非表示/未レイアウト
    const x = cue.aim.offset.x;
    const y = cue.aim.offset.y;
    padDot.style.transform = `translate(${-x * 2 * r}px, ${-y * 2 * r}px)`;
  };

  // ユーザー意図の撞点を記録しつつ、現在の制約（禁止円・下方バンド）に通して適用する。
  // 意図値を保持するので、レール際で抑制された後にレールから離れるとデフォルトへ戻る。
  const setDesiredSpin = (ox: number, oy: number): void => {
    desiredSpin.x = ox;
    desiredSpin.y = oy;
    const c = constrainSpin(ox, oy);
    applySpin(c.ox, c.oy, padBall.getBoundingClientRect().width / 2);
  };

  // 禁止円・下方バンドをパッド上に赤で描画（プールを再利用）。
  const updatePadMask = (): void => {
    const r = padBall.getBoundingClientRect().width / 2;
    // 後方クッションによる下方バンド: offset.y < railMinY を下端から塗る。
    const bandRatio = Math.max(0, Math.min(1, railMinY + 0.5));
    padBand.style.height = `${(bandRatio * 100).toFixed(1)}%`;
    while (padMaskEls.length < forbiddenDisks.length) {
      padMaskEls.push(el('div', 'pool-pad-forbidden', padBall));
    }
    forbiddenDisks.forEach((disk, i) => {
      const m = padMaskEls[i]!;
      const dia = disk.r * 4 * r; // 半径(offset)→px = disk.r*2r、直径 = ×2
      const cdx = -disk.cx * 2 * r;
      const cdy = -disk.cy * 2 * r;
      m.style.width = `${dia}px`;
      m.style.height = `${dia}px`;
      m.style.transform = `translate(calc(-50% + ${cdx}px), calc(-50% + ${cdy}px))`;
      m.hidden = false;
    });
    for (let i = forbiddenDisks.length; i < padMaskEls.length; i++) {
      padMaskEls[i]!.hidden = true;
    }
  };

  const setPad = (cx: number, cy: number): void => {
    const rect = padBall.getBoundingClientRect();
    const r = rect.width / 2;
    let dx = cx - (rect.left + r);
    let dy = cy - (rect.top + r);
    const d = Math.hypot(dx, dy);
    if (d > r) {
      dx = (dx / d) * r;
      dy = (dy / d) * r;
    }
    // tailuge と同じ符号規約（dx,dy を反転して offset へ）。
    let ox = -dx / (2 * r);
    let oy = -dy / (2 * r);
    // センタースナップ: 中心付近では一旦 ド真ん中 (0,0) に吸着し、合わせやすくする。
    if (Math.hypot(ox, oy) <= CENTER_DETENT) {
      ox = 0;
      oy = 0;
    }
    setDesiredSpin(ox, oy);
    // 撞点が変わるとスクワート量が変わる → オートエイム中なら角度を再補正（きらめき）。
    recorrectAutoAim(false);
  };
  updatePadMask();

  // ---- タイミング撞き（設定: ショット入力方法='timing'） ----
  // 1回目: 撞点パッドをタップ → 撞点を確定しオシレーション開始（球の上半円弧が伸縮）。
  // 2回目: 撞点パッドの球内をタップ → その瞬間のパワーで即発射。
  // キャンセル: 撞点パッド以外をタップで取り消し（オシレーション停止・cue.aim.power=0）。
  // インジケータ: padBall 内に SVG 弧（上半円）を重ね、パワーに応じて dashoffset を更新。
  const powerArcSvgNs = 'http://www.w3.org/2000/svg';
  const powerArcSvg = document.createElementNS(powerArcSvgNs, 'svg');
  powerArcSvg.setAttribute('class', 'pool-power-arc');
  powerArcSvg.setAttribute('viewBox', '-1 -1 2 2');
  powerArcSvg.setAttribute('preserveAspectRatio', 'none');
  const powerArcDefs = document.createElementNS(powerArcSvgNs, 'defs');
  const powerArcGrad = document.createElementNS(powerArcSvgNs, 'linearGradient');
  powerArcGrad.setAttribute('id', 'pool-power-arc-grad');
  powerArcGrad.setAttribute('x1', '0');
  powerArcGrad.setAttribute('y1', '0');
  powerArcGrad.setAttribute('x2', '1');
  powerArcGrad.setAttribute('y2', '0');
  const powerArcStops: Array<[string, string]> = [
    ['0', '#7ee2a0'],
    ['0.5', '#fde68a'],
    ['1', '#e74c3c'],
  ];
  for (const [offset, color] of powerArcStops) {
    const stop = document.createElementNS(powerArcSvgNs, 'stop');
    stop.setAttribute('offset', offset);
    stop.setAttribute('stop-color', color);
    powerArcGrad.appendChild(stop);
  }
  powerArcDefs.appendChild(powerArcGrad);
  powerArcSvg.appendChild(powerArcDefs);
  // 上半円弧: (-0.92, 0) → (0.92, 0) へ上を通る。
  // SVG は y-down 座標。9時 (-x,0) から 3時 (+x,0) へ「上(=-y側)を経由」したい時は、
  // 視覚的に「時計回り」となる sweep-flag=1 を指定する（時計の 9→12→3 と同じ向き）。
  // sweep-flag=0 だと下半分を経由してしまうため要注意。
  // stroke-linecap=butt: パワー0(dashoffset=100)時にラインキャップが path 終点に残って
  //   赤い点として見えるのを防止。round より見た目はシャープになる。
  const powerArcPath = document.createElementNS(powerArcSvgNs, 'path');
  powerArcPath.setAttribute('d', 'M -0.92 0 A 0.92 0.92 0 0 1 0.92 0');
  powerArcPath.setAttribute('fill', 'none');
  powerArcPath.setAttribute('stroke', 'url(#pool-power-arc-grad)');
  powerArcPath.setAttribute('stroke-width', '0.3');
  powerArcPath.setAttribute('stroke-linecap', 'butt');
  powerArcPath.setAttribute('pathLength', '100');
  powerArcPath.setAttribute('stroke-dasharray', '100');
  powerArcPath.setAttribute('stroke-dashoffset', '100');
  powerArcSvg.appendChild(powerArcPath);
  padBall.appendChild(powerArcSvg);

  // タイミング撞き: 状態とアニメーション
  // フェーズ: 0=伸長(0→1), 1=最大保持, 2=収縮(1→0), 3=最小保持(短)
  const TIMING_EXTEND_MS = 700; // 0→100% 伸長
  const TIMING_HOLD_MAX_MS = 1000; // 最大で停止
  const TIMING_CONTRACT_MS = 700; // 100%→0 収縮
  const TIMING_HOLD_MIN_MS = 120; // 0 で一瞬停止して再伸長（タイミング取りやすさ）
  let timingActive = false;
  let timingPhase: 0 | 1 | 2 | 3 = 0;
  let timingPhaseStart = 0;
  let timingPower = 0;
  let timingRaf = 0;

  const updatePowerArc = (): void => {
    // 0..1 → dashoffset 100..0 (100=見えない・0=完全可視)
    const off = (1 - timingPower) * 100;
    powerArcPath.setAttribute('stroke-dashoffset', String(off));
  };
  const timingLoop = (now: number): void => {
    if (!timingActive) return;
    const elapsed = now - timingPhaseStart;
    if (timingPhase === 0) {
      timingPower = Math.min(1, elapsed / TIMING_EXTEND_MS);
      if (elapsed >= TIMING_EXTEND_MS) {
        timingPower = 1;
        timingPhase = 1;
        timingPhaseStart = now;
      }
    } else if (timingPhase === 1) {
      timingPower = 1;
      if (elapsed >= TIMING_HOLD_MAX_MS) {
        timingPhase = 2;
        timingPhaseStart = now;
      }
    } else if (timingPhase === 2) {
      timingPower = Math.max(0, 1 - elapsed / TIMING_CONTRACT_MS);
      if (elapsed >= TIMING_CONTRACT_MS) {
        timingPower = 0;
        timingPhase = 3;
        timingPhaseStart = now;
      }
    } else {
      // phase 3
      timingPower = 0;
      if (elapsed >= TIMING_HOLD_MIN_MS) {
        timingPhase = 0;
        timingPhaseStart = now;
      }
    }
    // 3D キューにも反映（タイミング中も撃ち出し時と同じ視覚パワー）。
    cue.aim.power = timingPower * effectiveMaxPower();
    updatePowerArc();
    timingRaf = requestAnimationFrame(timingLoop);
  };
  const startTimingShot = (): void => {
    if (timingActive) return;
    timingActive = true;
    timingPhase = 0;
    timingPhaseStart = performance.now();
    timingPower = 0;
    padBall.classList.add('is-timing');
    updatePowerArc();
    timingRaf = requestAnimationFrame(timingLoop);
  };
  const cancelTimingShot = (): void => {
    if (!timingActive) return;
    timingActive = false;
    if (timingRaf) cancelAnimationFrame(timingRaf);
    timingRaf = 0;
    timingPower = 0;
    cue.aim.power = 0;
    padBall.classList.remove('is-timing');
    updatePowerArc();
  };
  const fireTimingShot = (): void => {
    if (!timingActive) return;
    // 現在の timingPower で発射。cue.aim.power は既に同期済みなのでそのまま使う。
    const power = timingPower * effectiveMaxPower();
    cue.aim.power = power;
    // 状態を先にリセット（performShot が再ラックで autoAim 再発動など走らせるため）。
    timingActive = false;
    if (timingRaf) cancelAnimationFrame(timingRaf);
    timingRaf = 0;
    timingPower = 0;
    padBall.classList.remove('is-timing');
    updatePowerArc();
    if (canShoot()) {
      performShot();
    }
    cue.aim.power = 0;
  };

  padBall.addEventListener('pointerdown', (e) => {
    if (ruleView.inputLocked) return; // 相手の手番中は撞点調整も禁止
    commitPlacement(); // 撞点を触り始めた瞬間に「配置は終わり」扱い → autoAim 即発動
    if (getShotInputMode() === 'timing') {
      // タイミング撞き: 1回目=撞点確定+オシレーション開始 / 2回目=発射
      if (!canShoot()) return;
      e.preventDefault();
      e.stopPropagation();
      if (timingActive) {
        fireTimingShot();
      } else {
        setPad(e.clientX, e.clientY);
        startTimingShot();
      }
      return;
    }
    // 引き撞き（既定）: 撞点を触り始め＝ドラッグ開始
    padActive = true;
    padBall.setPointerCapture(e.pointerId);
    setPad(e.clientX, e.clientY);
    e.preventDefault();
    e.stopPropagation();
  });
  padBall.addEventListener('pointermove', (e) => {
    if (padActive) setPad(e.clientX, e.clientY);
  });
  const padEnd = (): void => {
    padActive = false;
  };
  padBall.addEventListener('pointerup', padEnd);
  padBall.addEventListener('pointercancel', padEnd);

  // タイミング撞きのキャンセル: 撞点パッド外を pointerdown するとオシレーションを停止する。
  // capture フェーズに登録し、他のハンドラより先に走らせる。**伝播を完全に止める**ため、
  // キャンセルと同時に下見ビュー（setOrbitTarget）・オービット・設定オープン等の他の操作は
  // 一切発動しない＝ユーザー指示「レベル動作中は他の操作を同時発動せずキャンセルのみ」を実現。
  // pointermove/up も同様にキャンセル直後の余波を吸収する（同一ジェスチャの down→up 一往復を遮蔽）。
  let timingSuppressPointerId: number | null = null;
  const suppressIfTimingCancelled = (e: PointerEvent): void => {
    if (timingSuppressPointerId !== null && e.pointerId === timingSuppressPointerId) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.type === 'pointerup' || e.type === 'pointercancel') {
        timingSuppressPointerId = null;
      }
    }
  };
  document.addEventListener('pointerdown', (e) => {
    if (!timingActive) return;
    const t = e.target;
    if (t instanceof Node && padBall.contains(t)) return; // padBall 内は別ハンドラで処理
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    timingSuppressPointerId = e.pointerId;
    cancelTimingShot();
  }, true);
  document.addEventListener('pointermove', suppressIfTimingCancelled, true);
  document.addEventListener('pointerup', suppressIfTimingCancelled, true);
  document.addEventListener('pointercancel', suppressIfTimingCancelled, true);

  // ショット入力方法の切替（設定→「ショット入力方法」）時の UI 同期。
  // timing→pull 切替時は進行中オシレーションを安全に解除し、縦キューを再表示する。
  const syncShotInputMode = (): void => {
    const mode = getShotInputMode();
    document.body.classList.toggle('pool-timing-shot', mode === 'timing');
    if (mode !== 'timing') cancelTimingShot();
  };
  subscribeShotInputChange(syncShotInputMode);
  syncShotInputMode(); // 起動時に現在値を反映

  // ---- 仰角（キューのグラフィックを上下ドラッグして調整。数値のみ表示） ----
  // manualElev: プレイヤー指定。autoMinElev: 後方レール上面をクリアする最小仰角
  // （tick() で再計算、レール上ギリギリ／上限キャップ付き）。実効値 = max(両者)。
  let manualElev = 0;
  let autoMinElev = 0;
  // 仰角ドラッグの初期値（キュー軸を後方で掴んだ時に記録）。
  let elevStartY = 0;
  let elevStartManual = 0;
  const ELEV_DRAG_GAIN = 0.004; // rad / px（上ドラッグでキューを立てる）

  // ---- cuedrag 起点ドラッグの aim ↔ elevate 動的モード切替 ----
  // cue 領域（kind='cue'）を起点としたドラッグは仰角と aim の両用領域。初回の縦/横優勢で
  // モード確定後も、**明らかに反対軸を操作したと判定できた時にモードを切替**する。
  // 起点アンカー (anchorClientX/Y) を保持し、現在モードの軸方向に動いたら都度リセット、
  // 反対軸方向に MODE_SWITCH_PX 以上溜まった時にモード切替＋アンカー再設定＋
  // 該当モードの初期値（aim:Angle/Grab・elevate:StartY/Manual）を現状に揃えてジャンプを防ぐ。
  let cuedragOriginated = false; // この ドラッグが cue 領域から始まったか
  let anchorClientX = 0;
  let anchorClientY = 0;
  const MODE_SWITCH_PX = 18; // 反対軸へのドラッグ累積がこれを超えるとモード切替（ヒステリシス）
  const MODE_ACTIVE_PX = 6; // 現在軸へのドラッグがこれを超えるとアンカーリセット（同方向継続使用と判定）

  const applyEffectiveElevation = (): void => {
    const eff = Math.min(MAX_ELEVATION, Math.max(manualElev, autoMinElev));
    cue.aim.elevation = eff;
    const deg = ((eff * 180) / Math.PI).toFixed(0);
    elevLabel.textContent =
      autoMinElev > manualElev + 1e-3 ? `仰角 ${deg}° (自動)` : `仰角 ${deg}°`;
    // 仰角を上げると禁止円・後方クッション制限が緩和される（tick() で再評価）。
  };

  const setManualElevation = (rad: number): void => {
    manualElev = Math.max(0, Math.min(MAX_ELEVATION, rad));
    applyEffectiveElevation();
  };
  setManualElevation(0);

  // ---- 配置モード（手玉配置 / プラクティスの球配置） ----
  const tableLayout = getTableLayout();
  const { halfX: tableHalfX, halfY: tableHalfY } = tableLayout;
  // オートエイム用ポケット狙点＋ジョー（ナックル）。ポケット可能角度の窓・優先順位に使う。
  const pocketAims = getPocketAims();
  // この距離以下のサイドポケットはコーナーより優先（短辺半幅の約1.6倍＝中央寄りの球が対象）。
  const AUTO_AIM_SIDE_NEAR = tableHalfY * 1.6;

  // HUD ポケット重なり時の自動半透明: 6 ポケット中心をスクリーン投影し、各 HUD の bounding rect
  // と被るなら opacity を下げる。150ms 周期（カメラ追従中も負荷ゼロ近い）。編集モード中は無効。
  const HUD_OVERLAP_INTERVAL_MS = 150;
  const HUD_OVERLAP_MARGIN_PX = 12;
  const HUD_DIM_OPACITY = '0.32';
  const updateHudPocketOverlap = (): void => {
    if (hudEditing || movableHuds.length === 0) {
      for (const hud of movableHuds) {
        hud.primary.el.style.opacity = '';
        for (const f of hud.followers) f.el.style.opacity = '';
      }
      return;
    }
    // 「自動」トップビュー中（ショット後の自動遷移・対象球が隠れた時の自動トップ）は
    // 短時間で出入りするため透過が点滅して見え、視覚的にちらつく。手動ロック中は
    // ユーザー明示なので従来通り透過する（ロビー等が見たい場面ではないため）。
    if (rig.isAutoTopActive()) {
      for (const hud of movableHuds) {
        hud.primary.el.style.opacity = '';
        for (const f of hud.followers) f.el.style.opacity = '';
      }
      return;
    }
    const screens: Array<{ x: number; y: number }> = [];
    for (const p of pocketAims) {
      const s = worldToScreen(p.x, p.y, 0);
      if (s.visible) screens.push({ x: s.x, y: s.y });
    }
    for (const hud of movableHuds) {
      // 撞点パッド（spinPad＋仰角ラベル）・キュー（cueTrack）・ロビー（lobby）は自動半透明の対象外。
      // ・spinPad/cueTrack: トップビューでポケット投影と頻繁に重なり透けて見にくくなるため常時不透明。
      // ・lobby: ロビーボタンは HUD 編集で任意位置へ移動できるようになったため、ポケット近傍での
      //   自動透過は不要（ユーザーが重ならない位置へ移動して回避できる）。
      if (
        hud.primary.key === 'spinPad' ||
        hud.primary.key === 'cueTrack' ||
        hud.primary.key === 'lobby'
      ) {
        hud.primary.el.style.opacity = '';
        for (const f of hud.followers) f.el.style.opacity = '';
        continue;
      }
      const targets: HTMLElement[] = [hud.primary.el, ...hud.followers.map((f) => f.el)];
      for (const t of targets) {
        if (t.hidden || t.offsetWidth === 0) {
          t.style.opacity = '';
          continue;
        }
        const r = t.getBoundingClientRect();
        let overlap = false;
        for (const s of screens) {
          if (
            s.x >= r.left - HUD_OVERLAP_MARGIN_PX &&
            s.x <= r.right + HUD_OVERLAP_MARGIN_PX &&
            s.y >= r.top - HUD_OVERLAP_MARGIN_PX &&
            s.y <= r.bottom + HUD_OVERLAP_MARGIN_PX
          ) {
            overlap = true;
            break;
          }
        }
        t.style.opacity = overlap ? HUD_DIM_OPACITY : '';
      }
    }
  };
  setInterval(updateHudPocketOverlap, HUD_OVERLAP_INTERVAL_MS);
  let placementMode = false;
  let grabbedBallId: number | null = null;
  let paletteOpen = false; // プラクティスの球パレット開閉
  let broken = false; // 非プラクティス: 最初のショットを撞いたか（撞いたら手玉配置不可）
  let ballInHand = false; // スクラッチ後のボールインハンド（手玉を盤面自由配置・キッチン制限なし）
  const isPractice = (): boolean => deps.getPlayMode() === 'practice';
  // 非プラクティスの手玉配置はキッチン（ヘッドストリングより手前）に限定。
  const kitchenLimitX = (): number => -tableHalfX / 2;
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const planeZ0 = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const hitPoint = new THREE.Vector3();

  // ポインタ位置をテーブル平面(z=0)へ投影。盤外ならクランプ前の生座標を返す。
  const pointerToTable = (cx: number, cy: number): { x: number; y: number } | null => {
    const rect = canvas.getBoundingClientRect();
    ndc.x = ((cx - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((cy - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    const p = raycaster.ray.intersectPlane(planeZ0, hitPoint);
    return p ? { x: hitPoint.x, y: hitPoint.y } : null;
  };

  // 配置モードで掴める球を探す（プラクティス=全球 / それ以外=手玉のみ）。
  const grabBallAt = (cx: number, cy: number): number | null => {
    const t = pointerToTable(cx, cy);
    if (!t) return null;
    let best: number | null = null;
    let bestD = (ballRadius * 2.2) ** 2;
    for (const b of table.balls) {
      if (!b.onTable()) continue;
      if (!isPractice() && b.id !== 0) continue;
      const d = (b.pos.x - t.x) ** 2 + (b.pos.y - t.y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = b.id;
      }
    }
    return best;
  };

  // ---- プラクティス用 配置レイアウトの保存/復元 ----
  // ユーザーが「現在の盤面」をスナップショットとして保存し、リセット時にそこへ戻すための機構。
  // localStorage に PersistedBall[] を JSON 保存。保存があれば main 側の onReset が
  // 自動的にこの配置を適用する（hasPracticeLayout/loadPracticeLayout を経由）。
  const PRACTICE_LAYOUT_KEY = 'pool.practiceLayout';
  // 直近に「保存済み」と判定した盤面の JSON シグネチャ。現在盤面と一致する間は保存ボタンを無効化。
  // 球を動かしたり追加/削除した瞬間にズレ、再保存可能に戻る。null は保存なし。
  let lastSavedSig: string | null = null;
  const currentBallsSig = (): string => JSON.stringify(serializeBalls(table));
  const savePracticeLayout = (): boolean => {
    try {
      const data = serializeBalls(table);
      const json = JSON.stringify(data);
      localStorage.setItem(PRACTICE_LAYOUT_KEY, json);
      lastSavedSig = json; // 直近保存の盤面を覚える（保存ボタン disabled 判定に使う）
      return true;
    } catch (err) {
      console.error('practiceLayout: 保存失敗:', err);
      return false;
    }
  };
  const clearPracticeLayout = (): void => {
    try {
      localStorage.removeItem(PRACTICE_LAYOUT_KEY);
      lastSavedSig = null;
    } catch (err) {
      console.error('practiceLayout: 削除失敗:', err);
    }
  };
  const hasPracticeLayout = (): boolean => {
    try {
      return localStorage.getItem(PRACTICE_LAYOUT_KEY) !== null;
    } catch {
      return false;
    }
  };
  const loadPracticeLayout = (): PersistedBall[] | null => {
    try {
      const raw = localStorage.getItem(PRACTICE_LAYOUT_KEY);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      // 構造ゆるチェック: id/x/y がある要素のみ通す。
      const out: PersistedBall[] = [];
      for (const it of parsed) {
        if (
          typeof it === 'object' &&
          it !== null &&
          typeof (it as { id?: unknown }).id === 'number' &&
          typeof (it as { x?: unknown }).x === 'number' &&
          typeof (it as { y?: unknown }).y === 'number'
        ) {
          out.push(it as PersistedBall);
        }
      }
      return out.length > 0 ? out : null;
    } catch (err) {
      console.error('practiceLayout: 読込失敗:', err);
      return null;
    }
  };
  // 起動時: 保存配置があれば lastSavedSig を初期化して、保存ボタンの初期 disabled を正しく判定する。
  // ただし main 側のリセットで盤面が保存配置から復元されるかどうかに依存する（プラクティス時のみ復元）。
  // ここでは「保存値そのもの」を sig としておき、現在盤面と一致したら disabled、違ったら enabled。
  (() => {
    try {
      const raw = localStorage.getItem(PRACTICE_LAYOUT_KEY);
      if (raw !== null) lastSavedSig = raw;
    } catch {
      /* noop */
    }
  })();

  // ---- プラクティス用 球パレット（追加/削除） ----
  const palette = el('div', 'pool-palette', root);
  const paletteBtns: { btn: HTMLButtonElement; id: number }[] = [];
  for (let id = 1; id <= 15; id++) {
    const btn = el('button', 'pool-palette-ball', palette);
    btn.textContent = String(id);
    btn.dataset.id = String(id);
    btn.addEventListener('click', () => {
      const onTable = onTableBallIds(table);
      if (onTable.includes(id)) {
        removeBall(table, id);
      } else {
        // フットスポット付近から螺旋状に空きスポットを探して追加。
        let px = 0.3;
        let py = 0;
        for (let k = 0; k < 80; k++) {
          const ang = k * 1.4;
          const rr = ballRadius * 2.3 * Math.sqrt(k);
          const cx = 0.3 + Math.cos(ang) * rr;
          const cy = Math.sin(ang) * rr;
          if (isFreeSpot(table, cx, cy, id)) {
            px = cx;
            py = cy;
            break;
          }
        }
        addBall(table, id, px, py);
      }
      clearShotState(); // 球数が変わるためアンドゥ無効化
      preAim = null;
      syncUndo();
      deps.onBallsChanged();
      refreshPalette();
    });
    paletteBtns.push({ btn, id });
  }
  // 保存／デフォルト／閉じる操作ボタン（パレット下段のツールバー）。
  // レイアウト: [ ↺ 初期化(左端) ]  [ 💾 配置を保存(中央) ]  [ ✕ 閉じる(右端) ]
  // 保存後もパレットは開いたままにし、is-active クラスで「保存済み」を視認できるようにする。
  const paletteTools = el('div', 'pool-palette-tools', palette);
  // 左端: ↺ デフォルトに戻す（小アイコン）
  const resetLayoutBtn = el('button', 'pool-palette-tool is-icon', paletteTools);
  resetLayoutBtn.textContent = '↺';
  resetLayoutBtn.title = '保存した配置を破棄してデフォルトに戻す';
  resetLayoutBtn.addEventListener('click', () => {
    clearPracticeLayout();
    refreshPaletteTools();
    deps.onResetPracticeLayoutToDefault?.();
  });
  // 中央: 💾 配置を保存（押下後はパレットを開いたまま＝保存状態が確認できる）
  const saveLayoutBtn = el('button', 'pool-palette-tool is-center', paletteTools);
  saveLayoutBtn.textContent = '💾 配置を保存';
  saveLayoutBtn.title = '現在のプラクティス配置を保存（リセットでここへ戻る）';
  saveLayoutBtn.addEventListener('click', () => {
    if (savePracticeLayout()) {
      saveLayoutBtn.classList.add('is-saved'); // 一瞬ハイライト
      setTimeout(() => saveLayoutBtn.classList.remove('is-saved'), 800);
      refreshPaletteTools();
    }
  });
  // 右端: ✕ 閉じる（パレットを閉じるだけ。配置モードは維持される）
  const closePaletteBtn = el('button', 'pool-palette-tool is-icon', paletteTools);
  closePaletteBtn.textContent = '✕';
  closePaletteBtn.title = '閉じる';
  closePaletteBtn.addEventListener('click', () => {
    paletteOpen = false;
    syncPlacementUi();
  });
  // 保存済みかどうかでアイコンの活性を切替（保存無し ⇒ ↺ を淡く・💾 は is-active で枠強調）。
  // 加えて、現在盤面が「直近保存と完全一致」している間は保存ボタンを disabled にする
  // （球を動かしたり追加/削除した瞬間に sig がズレて再保存可能になる）。
  const refreshPaletteTools = (): void => {
    const has = hasPracticeLayout();
    resetLayoutBtn.classList.toggle('is-dim', !has);
    saveLayoutBtn.classList.toggle('is-active', has);
    const matchSaved = has && lastSavedSig !== null && currentBallsSig() === lastSavedSig;
    saveLayoutBtn.disabled = matchSaved;
    saveLayoutBtn.classList.toggle('is-disabled', matchSaved);
  };
  refreshPaletteTools();

  const refreshPalette = (): void => {
    const onTable = onTableBallIds(table);
    for (const p of paletteBtns) {
      p.btn.classList.toggle('is-on', onTable.includes(p.id));
    }
    refreshPaletteTools();
  };
  refreshPalette();

  // パレットをトップバーの直下に配置する。CSS 既定 top(56px) はトップバー 1 段分の想定。
  // 通常は updateTopbarCompact() が 1 段を保証するが、極狭幅のフォールバック折り返し時にも
  // 重ならないよう、表示のたびに実測の下端から top を再計算する。
  const positionPalette = (): void => {
    if (palette.hidden) return;
    // パレットは「＋球を追加」ボタンの直下にぶら下げる（配置中は両者とも上中央に表示される）。
    // ＋球が隠れている異常系のみトップバー下端をフォールバック基準にする。
    // hidden 切替直後でも getBoundingClientRect はレイアウトを同期確定させた値を返す。
    const anchor = addBtn.hidden ? topbar : addBtn;
    const anchorBottom = anchor.getBoundingClientRect().bottom;
    if (Number.isFinite(anchorBottom) && anchorBottom > 0) {
      palette.style.top = `${Math.round(anchorBottom) + 8}px`;
    } else {
      // 基準要素が非表示（HUD編集中等）の異常系: インライン指定を外して CSS 既定へ戻す。
      palette.style.removeProperty('top');
    }
  };
  // トップバーの圧縮表示（アイコンのみ）判定。
  // ラベル表示状態で実測し、表示中ボタンが 2 段に折り返している（offsetTop が先頭ボタンと
  // 異なる）場合に is-compact を付与してラベルを隠し、1 段表示へ縮退する。
  // 「設定が 2 段目に落ちて直下のロビーと重なる」UX 事故の再発防止
  // （knowledge/architecture/mobile-hud-ux-guidelines.md §3）。
  // 呼び出しタイミング: ボタンの表示数が変わる syncPlacementUi と window resize。
  // 縮退順序（mobile-hud §3: ①縮小 → ②ラベル省略 の順）に従い 2 段階で縮める:
  //   第0段: ラベル全表示（既定）
  //   第1段: is-snug   = フォント/余白/間隔だけ縮小（ラベルは維持＝可読性優先）
  //   第2段: is-compact = アイコンのみ（配置中の ✋配置 ラベルだけ CSS で残す）
  // いずれも「常に 1 段」を保つ実測ベース判定（offsetTop の差で折り返しを検出）。
  const updateTopbarCompact = (): void => {
    try {
      const fitsOneRow = (): boolean => {
        const kids = Array.from(topbar.children).filter(
          (c): c is HTMLElement => c instanceof HTMLElement && !c.hidden,
        );
        if (kids.length <= 1) return true;
        const firstTop = kids[0]!.offsetTop;
        return kids.every((k) => k.offsetTop === firstTop);
      };
      // 一旦すべて解除しラベル全表示で実測（縮退中のまま測ると復帰判定ができない）。
      topbar.classList.remove('is-snug', 'is-compact');
      if (!fitsOneRow()) {
        topbar.classList.add('is-snug'); // 第1段: 縮小（ラベル維持）
        if (!fitsOneRow()) {
          topbar.classList.remove('is-snug');
          topbar.classList.add('is-compact'); // 第2段: アイコンのみ
        }
      }
    } catch (err) {
      // 計測失敗時はラベル表示のまま（はみ出しは CSS の flex-wrap が下支え）。
      console.error('topbar compact 判定に失敗:', err);
    }
    positionPalette();
  };
  window.addEventListener('resize', updateTopbarCompact);

  const syncPlacementUi = (): void => {
    // ドリル中は配置系 UI を完全に封じる（固定配置を保証）。
    if (drillLock) {
      placementMode = false;
      paletteOpen = false;
      placeBtn.hidden = true;
      addBtn.hidden = true;
      palette.hidden = true;
      placementCollapse = false; // ドリル中は配置なし＝収納も解除（即時反映）
      applyShotUiAppearance();
      updateTopbarCompact();
      return;
    }
    const practice = isPractice();
    // 非プラクティスはブレーク後（broken）は手玉配置不可。ただしボールインハンド中は可。
    const placeAllowed = practice || !broken || ballInHand;
    // 入力ロック中（相手の手番・CPU 戦時の CPU 番含む）は配置ボタン自体を隠す
    // ＝ 相手が撞く間に「配置」が青枠で active 表示される違和感を排除。
    placeBtn.hidden = ruleView.inputLocked;
    placeBtn.disabled = !placeAllowed;
    placeBtn.classList.toggle('is-camera', placementMode && !ruleView.inputLocked);
    // ＋球（パレット開閉）はプラクティス＋配置モード時のみ。
    addBtn.hidden = !(placementMode && practice);
    addBtn.classList.toggle('is-camera', paletteOpen);
    palette.hidden = !(placementMode && practice && paletteOpen);
    if (!palette.hidden) refreshPalette();
    // 配置ON/OFF をショットUI収納へ即時反映（render ループのフレーム待ちに依存させない）。
    placementCollapse = placementMode && practice;
    applyShotUiAppearance();
    // 表示ボタン数が変わった可能性があるため圧縮表示を再判定（パレット位置追従も内包）。
    updateTopbarCompact();
  };

  placeBtn.addEventListener('click', () => {
    if (drillLock) return; // ドリル中は配置不可（固定配置）
    if (!isPractice() && broken && !ballInHand) return; // ブレーク後は配置不可（ボールインハンド中は可）
    placementMode = !placementMode;
    if (!placementMode) paletteOpen = false;
    // 配置ボタン ON で自動トップビュー、OFF で押下前のビューへ復元（2026-06-14f）。
    if (placementMode) enterPlacementTopView();
    else exitPlacementTopView();
    syncPlacementUi();
  });
  addBtn.addEventListener('click', () => {
    paletteOpen = !paletteOpen;
    syncPlacementUi();
  });
  syncPlacementUi(); // 初期状態（パレット/＋を隠す）。enterPlacement で配置開始。

  // 配置中に球が動いたら、最若番号の的玉へ照準を向け直す（手玉移動で狙いがズレないように）。
  const reaimAtLowestBall = (): void => {
    if (!table.cueball.onTable()) return;
    const id = lowestOnTableBallId(table);
    if (id === null) return;
    const t = ballPositionById(table, id);
    if (!t) return;
    const cb = table.cueball.pos;
    cue.aim.angle = Math.atan2(t.y - cb.y, t.x - cb.x);
    aimTargetId = id; // 狙っている的玉（残球表示の強調用）
    rig.alignToAim(cue.aim.angle);
  };

  // タップ（ドラッグでない単発タッチ）判定。的球タップで照準を合わせるのに使う。
  const TAP_MOVE_PX = 8; // これ以上動いたらドラッグ扱い
  let tapDownX = 0;
  let tapDownY = 0;
  let tapMoved = false;

  // スクリーン座標 (cx,cy) に最も近い的玉（手球以外・盤上）の id。AIM_GRAB_PX 以内。
  const objectBallAt = (cx: number, cy: number): number | null => {
    let best: number | null = null;
    let bestD = AIM_GRAB_PX * AIM_GRAB_PX;
    for (const b of table.balls) {
      if (b.id === 0 || !b.onTable()) continue;
      const s = worldToScreen(b.pos.x, b.pos.y, ballRadius);
      if (!s.visible) continue;
      const d = (cx - s.x) * (cx - s.x) + (cy - s.y) * (cy - s.y);
      if (d < bestD) {
        bestD = d;
        best = b.id;
      }
    }
    return best;
  };

  // スクリーン座標が手球付近か（フォーカス復帰判定用）。
  const cueBallAt = (cx: number, cy: number): boolean => {
    const cb = table.cueball;
    if (!cb.onTable()) return false;
    const s = worldToScreen(cb.pos.x, cb.pos.y, ballRadius);
    if (!s.visible) return false;
    return Math.hypot(cx - s.x, cy - s.y) < AIM_GRAB_PX;
  };

  // 指定的玉へ照準を向ける。
  const aimAtBall = (id: number): void => {
    const t = ballPositionById(table, id);
    if (!t) return;
    const cb = table.cueball.pos;
    cue.aim.angle = Math.atan2(t.y - cb.y, t.x - cb.x);
    aimTargetId = id; // 狙っている的玉（残球表示の強調用）
    rig.setFraming(true);
    rig.alignToAim(cue.aim.angle);
  };

  // ---- オートエイム（自動照準） ----
  // ポケットへの経路が通っている（隠れていない）球へ、現在の撞点・強さに合わせてポケットIN角へ
  // 自動補正する（カット角の制限なし＝前方の撞ける球すべてが対象）。主に手球の出し練習用。
  // 方式: tick() で「毎フレーム維持」する（イベント駆動だと取りこぼし・撞点変更追従漏れが
  // 起きるため）。ユーザーがショット方向を手で操作したショット機会の間だけ解除し（手動優先）、
  // 静止/フリーボール配置/的玉タッチで（再）発動する。対戦中（online）は設定値に関わらずOFF固定。
  // カット角の上限（ほぼ90°＝裏に回り込む不可能ショットのみ除外。実質「角度制限なし」）。
  // カット角の上限（86.5°）。これ以上薄い球はポケットインの可能性が実質ゼロなので対象から除外する
  // （旧値 ≈ 89° は裏回りのみ除外で実質無制限・薄すぎる球まで自動照準されて当たらない問題があった）。
  const AUTO_AIM_MAX_CUT = (86.5 * Math.PI) / 180;
  // 対戦中（CPU戦・オンライン対戦・観戦のいずれか）か。観戦は撞けない（inputLocked）が、
  // 自動照準の発動条件としては「非対戦＝プラクティス/ソロ」かどうかだけを区別すればよい。
  const inMatch = (): boolean => isMatchActive() || ruleView.online;
  // 装備中 skill による「対戦中自動照準」の許容カット角 [rad]。未装備/未解禁/不正値なら null。
  // 上限は既定の AUTO_AIM_MAX_CUT（86.5°）でクランプする。
  const skillMatchMaxCut = (): number | null => {
    const deg = deps.getEquippedSkillAutoAimMaxCutDeg?.();
    if (typeof deg !== 'number' || !(deg > 0)) return null;
    return (Math.min(deg, 86.5) * Math.PI) / 180;
  };
  // ドリル中に許可する自動照準の許容カット角 [rad]（保有スキルの最大・owned ベース）。
  // 未保有なら null（＝ドリル中は従来どおり自動照準オフ）。F-30・案A。
  const drillSkillMaxCut = (): number | null => {
    const deg = deps.getDrillAutoAimMaxCutDeg?.();
    if (typeof deg !== 'number' || !(deg > 0)) return null;
    return (Math.min(deg, 86.5) * Math.PI) / 180;
  };
  // オートエイムが有効か。
  //  - ドリル中: 既定はオフ（スキル測定）。ただし保有スキルがあれば「その獲得範囲（最大カット角）」まで発動する（案A）。
  //  - 非対戦（プラクティス/ソロ）: 設定トグル isAutoAim() に従う。自由配置でも有用なため許可
  //    （`pickBestMakeable` が「最厚（カット最小）の撞ける球」を選ぶ）。
  //  - 対戦中: 通常は強制 OFF だが、skill を装備している時のみ発動する（設定トグルは対戦中
  //    凍結されるため参照しない＝スキル装備が唯一の発動条件）。
  const autoAimOn = (): boolean => {
    if (drillLock) return drillSkillMaxCut() !== null; // 保有スキルの獲得範囲でのみ発動
    if (!inMatch()) return isAutoAim();
    return skillMatchMaxCut() !== null;
  };
  // 現在の許容カット角 [rad]。ドリル中は保有スキル角、対戦中スキル発動時はスキル角に制限、それ以外は既定上限。
  const autoAimMaxCut = (): number => {
    if (drillLock) return drillSkillMaxCut() ?? AUTO_AIM_MAX_CUT;
    const skill = inMatch() ? skillMatchMaxCut() : null;
    return skill ?? AUTO_AIM_MAX_CUT;
  };
  // ユーザーが UI で選んだ「優先ポケット」の index（pocketAims のインデックス）。
  // null = 通常選択（最厚＝カット角最小）。指定があれば solveAutoAimFor がそれを優先する
  // （ただし経路が通り cutAngle ≤ maxCut の場合のみ。解けないと通常に戻る）。
  // ショット静止時（L2424）にリセットし、毎ショット中立スタートとする。
  let preferredPocketIndex: number | null = null;
  // 現在「狙っている的玉」の id（残球表示の強調用）。タップ/最若球照準/オートエイムで設定し、
  // 手動でショット方向を操作したら null（＝特定対象なし）。null のときは syncRoster が照準線上で
  // 最初に当たる的玉へフォールバックする。物理的な第一接触（間の障害球）ではなく「意図した的玉」を
  // 強調するため、第一接触の aimedTargetId とは別に保持する。
  let aimTargetId: number | null = null;
  // 現在オートエイムが角度を支配しているか。
  let autoAimEngaged = false;
  // ユーザーが的玉タッチで明示選択した対象（sticky・null=自動選択モード）。
  // ロック対象が今は解けなくても他球へ勝手に移らない（タッチした球を尊重）。
  let autoAimLockedId: number | null = null;
  // 現在維持中の対象 id（自動選択モードでは解けなくなると再選択する）。
  let autoAimActiveId: number | null = null;
  // このショット機会でユーザーが手で照準した（＝オートエイムを抑止する）か。
  let autoAimUserAimed = false;
  // 直近の実シミュ逆算の入力シグネチャ（手球位置＋撞点）。変化時のみ重い再計算を行う。
  let autoAimSig: string | null = null;

  // 指定的玉のオートエイム幾何解を求める（角度は書き換えない・純粋）。解が無ければ null。
  // ユーザーが UI で指定した `preferredPocketIndex` があり、そのポケットで解ければそれを優先する
  // （他は全て無視＝ユーザー意図尊重）。優先ポケットで解けない場合は通常選択（最厚）にフォールバック。
  const solveAutoAimFor = (targetId: number): AutoAimSolution | null => {
    if (!table.cueball.onTable()) return null;
    const target = ballPositionById(table, targetId);
    if (!target) return null;
    const obstacles: Vec2[] = [];
    for (const b of table.balls) {
      if (b.id === 0 || b.id === targetId || !b.onTable()) continue;
      obstacles.push({ x: b.pos.x, y: b.pos.y });
    }
    if (preferredPocketIndex !== null) {
      const pk = pocketAims[preferredPocketIndex];
      if (pk) {
        const onlyOne = findMakeablePockets({
          cue: cueBallPos(table),
          target,
          obstacles,
          pockets: [pk],
          ballR: ballRadius,
          maxCut: autoAimMaxCut(),
        });
        if (onlyOne[0]) {
          // pocketIndex を元の pocketAims インデックスへ復元（findMakeablePockets はローカル index で 0 を返す）。
          return { ...onlyOne[0], pocketIndex: preferredPocketIndex };
        }
      }
      // 優先ポケットで解けない → 通常選択へフォールバック（毎ショット意図を維持しすぎない）。
    }
    return solveAutoAim({
      cue: cueBallPos(table),
      target,
      obstacles,
      pockets: pocketAims,
      ballR: ballRadius,
      maxCut: autoAimMaxCut(),
      sideNearDist: AUTO_AIM_SIDE_NEAR,
    });
  };

  /** 現在ターゲットしている的玉に対し「狙える全ポケット候補」を返す内部ヘルパ。
   *  pocket-glows 表示と、ポケットタップ判定で共有する。空配列＝候補なし＝表示しない。 */
  const computeMakeablePockets = (): {
    positions: Vec2[];
    pocketIndices: number[];
    selectedIndex: number | null;
  } => {
    const empty = { positions: [] as Vec2[], pocketIndices: [] as number[], selectedIndex: null };
    if (!autoAimOn()) return empty;
    if (aimTargetId === null) return empty;
    if (!deps.isResting()) return empty;
    if (!table.cueball.onTable()) return empty;
    const target = ballPositionById(table, aimTargetId);
    if (!target) return empty;
    const obstacles: Vec2[] = [];
    for (const b of table.balls) {
      if (b.id === 0 || b.id === aimTargetId || !b.onTable()) continue;
      obstacles.push({ x: b.pos.x, y: b.pos.y });
    }
    const sols = findMakeablePockets({
      cue: cueBallPos(table),
      target,
      obstacles,
      pockets: pocketAims,
      ballR: ballRadius,
      maxCut: autoAimMaxCut(),
    });
    if (sols.length === 0) return empty;
    const positions = sols.map((s) => {
      const pk = pocketAims[s.pocketIndex]!;
      return { x: pk.x, y: pk.y };
    });
    const pocketIndices = sols.map((s) => s.pocketIndex);
    let selectedIndex: number | null = null;
    if (preferredPocketIndex !== null) {
      const idx = pocketIndices.indexOf(preferredPocketIndex);
      if (idx >= 0) selectedIndex = idx;
    } else {
      // 通常選択（最厚）: solveAutoAim を呼んで pocketIndex を取得し sols 内位置を求める。
      const sol = solveAutoAim({
        cue: cueBallPos(table),
        target,
        obstacles,
        pockets: pocketAims,
        ballR: ballRadius,
        maxCut: autoAimMaxCut(),
        sideNearDist: AUTO_AIM_SIDE_NEAR,
      });
      if (sol) {
        const idx = pocketIndices.indexOf(sol.pocketIndex);
        if (idx >= 0) selectedIndex = idx;
      }
    }
    return { positions, pocketIndices, selectedIndex };
  };

  // ポケット可能な球の中から既定対象を選ぶ。ルール/対戦中は最若球（合法球）、プラクティスは
  // ポケット可能な球の中で最も厚い（カット最小）球。makeable が無ければ null。
  const pickBestMakeable = (): number | null => {
    if (!isPractice()) return lowestOnTableBallId(table);
    let bestId: number | null = null;
    let bestCut = Infinity;
    for (const b of table.balls) {
      if (b.id === 0 || !b.onTable()) continue;
      const sol = solveAutoAimFor(b.id);
      if (sol && sol.cutAngle < bestCut) {
        bestCut = sol.cutAngle;
        bestId = b.id;
      }
    }
    return bestId;
  };

  // 現フレームの対象を決定する。①タッチ選択（ロック）が最優先 ②維持中の対象がまだ解ける
  // ならそれを継続（チラつき防止）③それ以外は makeable から再選択。
  const resolveAutoAimTarget = (): number | null => {
    // ドリルは番号順クリア（targetOrder）。スキル由来の自動照準は残しつつ、対象は常に最小番号球に固定する。
    // ＝最厚球やタッチ選択へ逸れると「誤球先接触＝judgeDrillShot で毎回 miss」になり、球を入れても
    //   残球が減らず・取り切ってもクリアにならない不具合（本番報告）を防ぐ。解けない（スキル角不足）時は
    //   maintainAutoAim が非発動にして手動照準を尊重する＝級に応じた段階解禁が成立する。
    if (drillLock) return lowestOnTableBallId(table);
    if (autoAimLockedId !== null) return autoAimLockedId;
    if (autoAimActiveId !== null && solveAutoAimFor(autoAimActiveId)) {
      return autoAimActiveId;
    }
    return pickBestMakeable();
  };

  // 角度を [-π,π] に正規化。
  const angleNorm = (a: number): number => {
    let x = a;
    while (x > Math.PI) x -= 2 * Math.PI;
    while (x < -Math.PI) x += 2 * Math.PI;
    return x;
  };

  // 的玉が desiredObjDir 方向へ実際に出る aim 角を、実物理シミュレート（simulateObjectBallDir）
  // で逆算する（スクワート・スワーブ・英式回転のスロー込み）。secant 法。解なしは null。
  const solveAimBySim = (
    targetId: number,
    desiredObjDir: number,
    power: number,
    guess: number,
  ): number | null => {
    const offset = cue.aim.offset;
    const elev = cue.aim.elevation;
    const factor = getSquirt();
    const f = (theta: number): number | null => {
      const d = simulateObjectBallDir(table, targetId, theta, power, offset, elev, factor);
      return d === null ? null : angleNorm(d - desiredObjDir);
    };
    let t0 = guess;
    let f0 = f(t0);
    if (f0 === null) return null;
    if (Math.abs(f0) < 0.0008) return t0;
    // 2点目: 誤差≈角度ずれなので guess−f0 を初期の secant 相手にする。
    let t1 = guess - f0;
    let f1 = f(t1);
    if (f1 === null) {
      t1 = guess + 0.01;
      f1 = f(t1);
      if (f1 === null) return Math.abs(f0) < 0.01 ? t0 : null;
    }
    for (let i = 0; i < 8; i++) {
      if (Math.abs(f1) < 0.0008) return t1;
      const denom = f1 - f0;
      if (Math.abs(denom) < 1e-9) break;
      let t2 = t1 - (f1 * (t1 - t0)) / denom;
      if (!Number.isFinite(t2)) break;
      const step = t2 - t1;
      if (Math.abs(step) > 0.25) t2 = t1 + Math.sign(step) * 0.25; // 暴れ防止
      const f2 = f(t2);
      if (f2 === null) break;
      t0 = t1;
      f0 = f1;
      t1 = t2;
      f1 = f2;
    }
    return Math.abs(f1) < 0.004 ? t1 : null;
  };

  // オートエイム解を求めて aim.angle へ適用する。ポケットは幾何で選び、的玉の出る方向が
  // そのポケット中心へ向くよう実シミュで aim を逆算（解析スクワートは初期推定に使う）。
  // useFinalPower=true で確定した実際の強さ、false でストップショット相当の代表速度を使う。
  // 解が無ければ false（＝発動しない）。アンギュラはここでのみ書き換える。
  const applyAutoAimAngle = (targetId: number, useFinalPower: boolean): boolean => {
    const sol = solveAutoAimFor(targetId);
    if (!sol) return false;
    const cosE = Math.max(0.2, Math.cos(cue.aim.elevation));
    const speed = useFinalPower
      ? Math.max(0.05, cue.aim.power * cosE)
      : stopShotSpeed(sol.contactDist);
    const power = speed / cosE; // cueStrike の power（水平初速＝power·cosE）
    // 初期推定 = ゴースト進路 − 解析スクワート偏向。
    const guess = sol.travelAngle - squirtDeflection(cue.aim.offset.x, speed, getSquirt());
    // 的玉が窓内の狙点方向（objDir）へ実際に出るよう実シミュで逆算。
    const refined = solveAimBySim(targetId, sol.objDir, power, guess);
    cue.aim.angle = refined ?? guess;
    return true;
  };

  // tick() から毎フレーム呼ぶ維持処理（静止・手球が盤上・相手手番でない時のみ）。
  // 対象を resolveAutoAimTarget で決め、入力（手球位置・撞点）が変わった時だけ実シミュ逆算する。
  // 解けない時は発動解除（ロック対象なら他球へは移らない）。手動照準中は何もしない。
  // 戻り値: このフレームで発動状態か。
  const maintainAutoAim = (): boolean => {
    if (!autoAimOn() || autoAimUserAimed) return false;
    const id = resolveAutoAimTarget();
    if (id === null) {
      autoAimEngaged = false;
      autoAimActiveId = null;
      autoAimSig = null;
      return false;
    }
    const cbp = cueBallPos(table);
    const sig =
      `${id}|${cbp.x.toFixed(4)},${cbp.y.toFixed(4)}` +
      `|${cue.aim.offset.x.toFixed(3)},${cue.aim.offset.y.toFixed(3)}`;
    // 同じ対象＆入力なら再計算不要。
    if (autoAimEngaged && autoAimActiveId === id && sig === autoAimSig) return true;
    const firstEngage = !autoAimEngaged || autoAimActiveId !== id;
    if (!applyAutoAimAngle(id, false)) {
      // 解けない: 非発動。ロック対象は維持（他球へ移らない）、自動選択は次フレーム再選択。
      autoAimEngaged = false;
      autoAimActiveId = autoAimLockedId;
      autoAimSig = null;
      return false;
    }
    autoAimEngaged = true;
    autoAimActiveId = id;
    aimTargetId = id; // 狙っている的玉（残球表示の強調用）
    autoAimSig = sig;
    if (firstEngage) {
      rig.setFraming(true);
      rig.alignToAim(cue.aim.angle);
      deps.onAutoAimGlimmer?.();
    }
    return true;
  };

  // 的玉タッチでの明示選択（ロック）。解ければ発動＋きらめき、解けなくてもロックは維持する
  // （他球へ移らず、手球/撞点を変えれば解けた時点で発動する）。戻り値: 発動したか。
  const lockAutoAimTarget = (id: number): boolean => {
    if (!autoAimOn()) return false;
    autoAimLockedId = id;
    autoAimActiveId = id;
    aimTargetId = id; // 解けなくてもロック対象を強調（残球表示）
    autoAimUserAimed = false;
    autoAimEngaged = false;
    autoAimSig = null;
    return maintainAutoAim();
  };

  // ---- ボールロスター（左端の縦並びオーバーレイ） ----
  // 全球を①〜縦に並べ、ポケットインした球はアイコンを消す（スペースは詰めない）。
  // アイコンタップで、的玉タップと同じ照準動作（オートエイムON=ロック／OFF=正面照準）を行う。
  const roster = el('div', 'pool-roster', root);
  // モード別の的玉番号セット（手球0は含めない）。練習は盤上の的玉を昇順で動的表示。
  const rosterNumbersForMode = (): number[] => {
    const mode = deps.getPlayMode();
    if (mode === 'nineBall') return [1, 2, 3, 4, 5, 6, 7, 8, 9];
    if (mode === 'tenBall') return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    if (mode === 'eightBall') {
      const a: number[] = [];
      for (let i = 1; i <= 15; i++) a.push(i);
      return a;
    }
    // practice: 盤上の的玉のみ（手球0除く）を昇順。
    return onTableBallIds(table)
      .filter((id) => id !== 0)
      .sort((p, q) => p - q);
  };
  const rosterChips = new Map<number, HTMLElement>();
  let rosterSig = '';
  // 残球エリアの自動位置調整: ズーム UI（既定で残球と同じ左辺）と縦方向に重なる時、
  // 残球を上へシフトする。重ならない範囲では既定の縦中央配置を維持する。
  // - 計測は実 DOM の bounding rect（HUD 編集でズーム UI が動いていても追従する）。
  // - 残球エリアが非表示ならインラインスタイルをクリアして CSS 既定（top:50%＋translate）に戻す。
  // - ロスター肥大（10/8ボール）や縦短い端末で発火することを想定。
  const ROSTER_TOP_MARGIN = 64; // topbar 直下のマージン（およそ）
  const ROSTER_ZOOM_GAP = 10; // ズーム UI との縦ギャップ
  const repositionRoster = (): void => {
    if (roster.hidden) {
      // 非表示時はインラインスタイルをクリアして CSS 既定へ戻す。
      roster.style.top = '';
      roster.style.transform = '';
      return;
    }
    const H = window.innerHeight;
    // 計測のためインラインスタイルを一旦クリアして自然な高さを取得。
    roster.style.top = '';
    roster.style.transform = '';
    const rosterH = roster.offsetHeight;
    // ズーム UI の上端を取得（非表示なら制約なし）。
    let zoomTop = H;
    // zoomCtrl は同関数スコープ内で参照可能（先に定義されている前提）。
    if (zoomCtrl && zoomCtrl.offsetHeight > 0) {
      const rect = zoomCtrl.getBoundingClientRect();
      if (rect.height > 0) zoomTop = rect.top;
    }
    // 縦中央配置の top を初期値にし、ズーム UI と重なるなら上へシフト。
    let top = (H - rosterH) / 2;
    if (top + rosterH + ROSTER_ZOOM_GAP > zoomTop) {
      top = zoomTop - ROSTER_ZOOM_GAP - rosterH;
    }
    // 画面上端からのマージンも確保（最終的にここを下回らない）。
    top = Math.max(ROSTER_TOP_MARGIN, top);
    roster.style.top = `${top}px`;
    roster.style.transform = 'none';
  };
  // 現在の番号セットでロスターを再構築する（セット変化時のみ呼ぶ）。
  const rebuildRoster = (nums: number[]): void => {
    while (roster.firstChild) roster.removeChild(roster.firstChild);
    rosterChips.clear();
    for (const num of nums) {
      const chip = el('button', 'pool-roster-ball', roster);
      chip.type = 'button';
      const hex = BALL_COLORS[num] ?? 0xffffff;
      const css = `#${hex.toString(16).padStart(6, '0')}`;
      const isStripe = num >= 9 && num <= 15;
      if (isStripe) {
        // ストライプ球: 白地＋中央カラー帯。
        chip.style.background = `linear-gradient(${css} 0 0) center/100% 56% no-repeat, #f5f3ea`;
      } else {
        chip.style.background = css;
      }
      const label = el('span', 'pool-roster-num', chip);
      label.textContent = String(num);
      chip.addEventListener('click', () => {
        // 的玉タップと同じ発火条件（静止・手球が盤上・入力ロックなし・配置モードでない。
        // ただしボールインハンド＝フリーボール中は照準切替を許可する）。
        if (
          !deps.isResting() ||
          !table.cueball.onTable() ||
          ruleView.inputLocked ||
          (placementMode && !ballInHand)
        ) {
          return;
        }
        // 盤上にない（ポケット済み）球は対象外。
        if (!onTableBallIds(table).includes(num)) return;
        if (!autoAimOn() || !lockAutoAimTarget(num)) aimAtBall(num);
      });
      rosterChips.set(num, chip);
    }
    // 球数が変わったため、ズーム UI との重なりを再評価して位置を調整。
    repositionRoster();
  };
  // ビューポートサイズ変化（端末回転/ウィンドウリサイズ）でも残球位置を再評価。
  // vmin 単位の CSS でズーム UI の bottom/高さが変わるため、追従が必要。
  // visualViewport の resize は iOS のソフトキーボード等にも対応する（任意）。
  window.addEventListener('resize', repositionRoster);
  window.addEventListener('orientationchange', repositionRoster);
  // HUD 編集完了／HUD 位置リセット時にも呼ばれるよう、上で宣言した参照に登録。
  onZoomCtrlMoved = repositionRoster;
  // tick から呼ぶ軽量同期: 番号セットが変わったら再構築、各球の在/不在と操作可否を反映。
  const syncRoster = (): void => {
    // 設定「残球表示」OFF なら非表示（設定変更は次フレームで即反映）。
    const show = isBallRoster();
    const prevHidden = roster.hidden;
    roster.hidden = !show;
    if (!show) {
      if (!prevHidden) repositionRoster(); // 非表示化時にインラインスタイルをクリア
      return;
    }
    const nums = rosterNumbersForMode();
    const sig = nums.join(',');
    if (sig !== rosterSig) {
      rosterSig = sig;
      rebuildRoster(nums);
    } else if (prevHidden) {
      // 「残球表示」を OFF→ON にしたタイミングで位置を再評価。
      repositionRoster();
    }
    const onTable = onTableBallIds(table);
    // 全的玉ポケット時は背景バーも消す（残球チップは DOM 残しつつ視覚的にコンテナごと透明化）。
    const allPotted = nums.every((id) => !onTable.includes(id));
    roster.classList.toggle('is-cleared', allPotted);
    const tappable =
      deps.isResting() &&
      table.cueball.onTable() &&
      !ruleView.inputLocked &&
      (!placementMode || ballInHand); // フリーボール中も照準切替・強調を有効に
    // 強調する「狙っている的玉」。静止・手球が盤上・入力可のときのみ。
    //   1) タップ/最若球照準/オートエイムで設定した aimTargetId（間に障害球があっても意図した的玉）
    //   2) それが無い（手動でフリーに照準中）→ 照準線上で最初に当たる的玉へフォールバック
    // ショット後は自動で次の球へ照準が合うため、その球が自然に強調される。
    let aimedId: number | null = null;
    if (tappable) {
      aimedId =
        aimTargetId !== null && onTable.includes(aimTargetId)
          ? aimTargetId
          : aimedTargetId(table, cue.aim.angle);
    }
    for (const [num, chip] of rosterChips) {
      const potted = !onTable.includes(num);
      // ポケット済みはアイコンを消す（DOMは残しスペース維持＝詰めない）。
      chip.classList.toggle('is-potted', potted);
      // 照準中の球だけ不透過で強調。それ以外の残球は半透過。
      const aimed = !potted && num === aimedId;
      chip.classList.toggle('is-aimed', aimed);
      chip.classList.toggle('is-dimmed', !potted && !aimed);
      // 操作不可（運動中/配置中/相手手番）はタップ無効化（見た目は半透過のまま）。
      chip.classList.toggle('is-locked', !tappable);
    }
  };

  // 撞点変更・ショット力確定時の即時再補正＋きらめき（毎フレーム維持に加えての視覚キュー）。
  const recorrectAutoAim = (useFinalPower: boolean): void => {
    if (!autoAimOn() || !autoAimEngaged || autoAimActiveId === null) return;
    if (applyAutoAimAngle(autoAimActiveId, useFinalPower)) {
      deps.onAutoAimGlimmer?.();
    }
  };

  // ユーザーがショット方向を手で操作した → このショット機会はオートエイム抑止。
  const disengageAutoAim = (): void => {
    autoAimEngaged = false;
    autoAimUserAimed = true;
    autoAimSig = null;
    // 特定の的玉狙いではなくなる → 残球表示は照準線上の第一接触へフォールバック。
    aimTargetId = null;
  };

  // 配置モード中にショットUI（キュー引き・撞点パッド・ショット方向ボタン）を操作した瞬間に
  // 「もう配置は終わって構えに入る」とみなして配置モードを抜ける。autoAim が ON なら即発動して
  // きらめき、ユーザーに「次は撞ける」フィードバックを返す。球の物理位置やラック状態は変更しない。
  const commitPlacement = (): void => {
    if (!placementMode) return;
    placementMode = false;
    paletteOpen = false;
    // 配置ボタン起因でトップビュー中なら押下前ビューへ戻す（構えへ移行するため。2026-06-14f）。
    exitPlacementTopView();
    syncPlacementUi();
    if (autoAimOn() && !autoAimEngaged) {
      if (maintainAutoAim()) deps.onAutoAimGlimmer?.();
    }
  };

  // ---- キャンバス: 視点回転 / ショット方向 / ピンチズーム / 配置 ----
  canvas.addEventListener('pointerdown', (e) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // 下見ビュー中の操作（オービット/ピンチ/エイム/配置）はアイドルタイマーを再充填
    refreshPreviewOrbitIdle();
    if (pointers.size === 2) {
      const pts = [...pointers.values()];
      pinchDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      gesture = 'pinch';
      return;
    }
    if (pointers.size !== 1) return;
    tapDownX = e.clientX;
    tapDownY = e.clientY;
    tapMoved = false;
    const primary = e.pointerType !== 'mouse' || e.button === 0;
    const canInteract = deps.isResting() && primary;
    // ① 配置モード中に球本体付近 → 球を移動（相手の手番ではロック）。
    if (placementMode && canInteract && !ruleView.inputLocked) {
      const id = grabBallAt(e.clientX, e.clientY);
      if (id !== null) {
        grabbedBallId = id;
        gesture = 'place';
        return;
      }
    }
    // ② 照準ガイドライン/キュー軸の近傍 → ショット方向（エイム）。
    const kind =
      canInteract && table.cueball.onTable() && !ruleView.inputLocked
        ? grabKind(e.clientX, e.clientY)
        : null;
    if (kind === 'cue') {
      // バット（キュー手元）掴み: 方向は保留。最初のドラッグが上下なら仰角、
      // 左右ならショット方向に確定する（pointermove で判定）。
      gesture = 'cuedrag';
      elevStartY = e.clientY;
      elevStartManual = manualElev;
      // cue 領域起点のドラッグはモード切替（aim↔elevate）を有効化する。
      cuedragOriginated = true;
      anchorClientX = e.clientX;
      anchorClientY = e.clientY;
      rig.setFraming(true);
      return;
    }
    // aim 領域起点のドラッグはモード切替対象外（縦方向の動きは aim 操作の一部）。
    cuedragOriginated = false;
    if (kind === 'aim') {
      disengageAutoAim(); // 手動でショット方向を操作 → オートエイム解除
      const cb = table.cueball.pos;
      const a = cue.aim.angle;
      const p = pointerToTable(e.clientX, e.clientY);
      // 手玉/前方ガイドライン → ショット方向（エイム）。
      gesture = 'aim';
      aimStartAngle = a;
      aimPerpX = -Math.sin(a);
      aimPerpY = Math.cos(a);
      if (p) {
        aimGrabX = p.x;
        aimGrabY = p.y;
        aimGrabRadius = Math.max(ballRadius * 4, Math.hypot(p.x - cb.x, p.y - cb.y));
        aimFromCue = false; // 前方掴みのみ（後方は elevate へ分岐済み）
      } else {
        aimGrabX = cb.x;
        aimGrabY = cb.y;
        aimGrabRadius = ballRadius * 4;
        aimFromCue = false;
      }
      rig.setFraming(true);
      rig.alignToAim(cue.aim.angle);
      return;
    }
    // ③ それ以外 → 視点回転。
    gesture = 'orbit';
  });

  // クライアント座標 → NDC（[-1,1]）。トップビューのピンチズーム/パンの逆投影に使う。
  const clientToNdc = (cx: number, cy: number): { x: number; y: number } => {
    const r = canvas.getBoundingClientRect();
    return {
      x: ((cx - r.left) / r.width) * 2 - 1,
      y: -((cy - r.top) / r.height) * 2 + 1,
    };
  };

  canvas.addEventListener('pointermove', (e) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    // 下見ビュー中: 移動操作中もタイマーをリセット（離した瞬間から 2 秒）
    refreshPreviewOrbitIdle();
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    prev.x = e.clientX;
    prev.y = e.clientY;
    if (
      !tapMoved &&
      Math.hypot(e.clientX - tapDownX, e.clientY - tapDownY) > TAP_MOVE_PX
    ) {
      tapMoved = true; // ドラッグ確定
    }

    if (gesture === 'pinch' && pointers.size >= 2) {
      const pts = [...pointers.values()];
      const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      if (pinchDist > 0) {
        const factor = pinchDist / dist;
        if (rig.isTopView()) {
          // トップビューは維持したまま、指の中点を中心に拡大縮小。
          // トップビュー中のピンチ/パンは自動リセット対象外（トップビューは意図的な状態）。
          const n = clientToNdc((pts[0]!.x + pts[1]!.x) / 2, (pts[0]!.y + pts[1]!.y) / 2);
          rig.topPinch(factor, n.x, n.y);
        } else {
          rig.zoom(factor);
          armViewResetTimer();
        }
      }
      pinchDist = dist;
      return;
    }
    if (gesture === 'place' && grabbedBallId !== null) {
      const t = pointerToTable(e.clientX, e.clientY);
      if (t) {
        let tx = t.x;
        // 非プラクティスの手玉はキッチン内に限定。
        // ブレーク配置はキッチン限定。ボールインハンド中は盤面自由（制限なし）。
        if (!isPractice() && grabbedBallId === 0 && !ballInHand) {
          tx = Math.min(tx, kitchenLimitX());
        }
        placeBallAt(table, grabbedBallId, tx, t.y);
        // 照準・カメラの追従は「手球（id 0）を動かしている間」だけに限定する。
        // 的玉（grabbedBallId !== 0）の移動では追従しない＝配置中に「選択中（最若）の的玉」を
        // 動かしても手球・キュー・視点がその的玉方向へ回り込まず、配置しやすくする（2026-06-14e）。
        if (grabbedBallId === 0) {
          // 手球を動かしている間: オートエイム有効なら対象へ追従（手球が動くので毎回再解決）。
          // 発動中はカメラも追従。発動できない／無効時は最若球への幾何照準。
          if (autoAimOn() && maintainAutoAim()) {
            rig.setFraming(true);
            rig.alignToAim(cue.aim.angle);
          } else {
            reaimAtLowestBall();
          }
        }
      }
      return;
    }
    if (gesture === 'cuedrag') {
      // バット掴みの方向確定: 閾値を超えるまで保留。上下優勢→仰角 / 左右優勢→方向。
      const totDx = e.clientX - tapDownX;
      const totDy = e.clientY - tapDownY;
      if (Math.hypot(totDx, totDy) <= TAP_MOVE_PX) return;
      if (Math.abs(totDx) > Math.abs(totDy)) {
        // 左右ドラッグ → ショット方向（バット側掴みなので回転符号を反転＝aimFromCue=true）。
        initAimFromCurrentPointer(e, /*aimFromCueSide*/ true);
        gesture = 'aim';
      } else {
        // 上下ドラッグ → 仰角。確定点を基準に取り直してジャンプを防ぐ。
        initElevateFromCurrentPointer(e);
        gesture = 'elevate';
      }
      // 確定時にアンカー再設定（以後のモード切替判定はここから累積する）。
      anchorClientX = e.clientX;
      anchorClientY = e.clientY;
      return;
    }
    // cuedrag 起点ドラッグ中のモード切替（aim ↔ elevate ヒステリシス）。
    // 現在モードの軸方向に MODE_ACTIVE_PX 以上動いたらアンカーをリセット（=継続使用）、
    // 反対軸方向に MODE_SWITCH_PX 以上溜まったらモード切替＋アンカー再設定＋初期値同期。
    if (cuedragOriginated && (gesture === 'aim' || gesture === 'elevate')) {
      const dxA = e.clientX - anchorClientX;
      const dyA = e.clientY - anchorClientY;
      const absDx = Math.abs(dxA);
      const absDy = Math.abs(dyA);
      if (gesture === 'aim') {
        // aim 中: 横方向に動いていれば継続。縦方向が縦優勢で閾値を超えたら elevate へ切替。
        if (absDx > MODE_ACTIVE_PX && absDx >= absDy) {
          anchorClientX = e.clientX;
          anchorClientY = e.clientY;
        } else if (absDy > MODE_SWITCH_PX && absDy > absDx) {
          initElevateFromCurrentPointer(e);
          gesture = 'elevate';
          anchorClientX = e.clientX;
          anchorClientY = e.clientY;
          return;
        }
      } else {
        // elevate 中: 縦方向に動いていれば継続。横方向が横優勢で閾値を超えたら aim へ切替。
        if (absDy > MODE_ACTIVE_PX && absDy >= absDx) {
          anchorClientX = e.clientX;
          anchorClientY = e.clientY;
        } else if (absDx > MODE_SWITCH_PX && absDx > absDy) {
          initAimFromCurrentPointer(e, /*aimFromCueSide*/ true);
          gesture = 'aim';
          anchorClientX = e.clientX;
          anchorClientY = e.clientY;
          return;
        }
      }
    }
    if (gesture === 'orbit') {
      // トップビューで拡大中の1本指ドラッグはパン（オービットはトップでは無意味）。
      // トップビュー中の topPan は自動リセット対象外（トップビューは意図的な状態）。
      if (rig.isTopView() && rig.getTopZoom() > 1.0001) {
        const prevN = clientToNdc(e.clientX - dx, e.clientY - dy);
        const nowN = clientToNdc(e.clientX, e.clientY);
        rig.topPan(prevN.x, prevN.y, nowN.x, nowN.y);
      } else {
        rig.orbit(-dx * ORBIT_K, dy * ORBIT_K);
        armViewResetTimer();
      }
    } else if (gesture === 'elevate') {
      // キューを上ドラッグ（画面上方向）で仰角を上げる。
      const up = elevStartY - e.clientY;
      setManualElevation(elevStartManual + up * ELEV_DRAG_GAIN);
    } else if (gesture === 'aim') {
      // 掴み点を手玉まわりに回す相対方式（カメラ非依存・左右反転なし）。
      // 横移動量 / 掴み半径 で回転量を決めるため、的球側（半径大）で掴むほど微調整になる。
      const p = pointerToTable(e.clientX, e.clientY);
      if (p) {
        const lateral = (p.x - aimGrabX) * aimPerpX + (p.y - aimGrabY) * aimPerpY;
        const delta = (AIM_GAIN * lateral) / aimGrabRadius;
        cue.aim.angle = aimStartAngle + (aimFromCue ? -delta : delta);
        rig.alignToAim(cue.aim.angle);
      }
    }
  });

  const endPointer = (e: PointerEvent): void => {
    // 1) ポケットグロータップ（配置モード中でも有効）。autoAim ON & ターゲット球あり時のみ。
    //    既存の球タップ判定は配置モード中は無効化されるため（球は配置の対象になる）、
    //    ポケット切替だけは配置モードに関わらず受け付ける（球と異なる場所なので競合しない）。
    if (
      e.type === 'pointerup' &&
      !tapMoved &&
      pointers.size === 1 &&
      gesture !== 'place' &&
      gesture !== 'pinch' &&
      !ruleView.inputLocked &&
      deps.isResting() &&
      table.cueball.onTable() &&
      autoAimOn() &&
      aimTargetId !== null
    ) {
      const p = pointerToTable(e.clientX, e.clientY);
      if (p) {
        const mp = computeMakeablePockets();
        const POCKET_TAP_R2 = (ballRadius * 5) * (ballRadius * 5);
        let hitPocketIndex = -1;
        let bestD2 = POCKET_TAP_R2;
        for (let i = 0; i < mp.positions.length; i++) {
          const pp = mp.positions[i]!;
          const dx = pp.x - p.x;
          const dy = pp.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) {
            bestD2 = d2;
            hitPocketIndex = mp.pocketIndices[i]!;
          }
        }
        if (hitPocketIndex >= 0) {
          preferredPocketIndex = hitPocketIndex;
          autoAimSig = null;
          if (maintainAutoAim()) deps.onAutoAimGlimmer?.();
          pointers.delete(e.pointerId);
          if ((pointers.size as number) === 0) gesture = 'idle';
          else if ((pointers.size as number) === 1) gesture = 'orbit';
          return;
        }
      }
    }
    // 2) 的玉タップで照準: ドラッグでない単発タップ & 静止中 & 手球が盤上 & （配置モードでない or
    // ボールインハンド）時、タップ位置の的玉へ照準を合わせる（pointerup のみ。配置/ピンチ中は対象外）。
    // ボールインハンド（フリーボール）は手球のみドラッグ配置で、的玉タップは配置ではなく照準にする
    // （手球ドラッグは gesture==='place' で除外されるため競合しない）。
    if (
      e.type === 'pointerup' &&
      !tapMoved &&
      pointers.size === 1 &&
      gesture !== 'place' &&
      gesture !== 'pinch' &&
      (!placementMode || ballInHand) &&
      !ruleView.inputLocked &&
      deps.isResting() &&
      table.cueball.onTable()
    ) {
      const id = objectBallAt(e.clientX, e.clientY);
      if (id !== null) {
        // 的玉タップ → オートエイムの対象としてロック選択。発動できなければ通常照準
        // （ロックは維持され、手球/撞点を変えれば解けた時点で自動発動する）。
        if (!autoAimOn() || !lockAutoAimTarget(id)) {
          aimAtBall(id);
        }
      } else if (cueBallAt(e.clientX, e.clientY)) {
        clearPreviewOrbit(); // 手球タップ → フォーカスを手球へ戻す
      } else {
        // 空き盤面 / レールのタップ。
        // - 通常ビュー: その点を「下見」（オービット中心を移動。レール際の隙間確認用）。
        // - TOPビュー: 下見オービットは効かないため、代わりにキュー方向操作（手球からその点への
        //   角度へキューを向ける）にする。テーブル+レール程度の範囲内に限定し、背景の遠方点は無視。
        // ※ポケットグロータップはこのブロックより上の (1) で先に処理済み。
        const p = pointerToTable(e.clientX, e.clientY);
        if (!p) {
          // 投影失敗時は何もしない
        } else if (rig.isTopView()) {
          if (
            Math.abs(p.x) <= tableHalfX * 1.3 &&
            Math.abs(p.y) <= tableHalfY * 1.5
          ) {
            const cb = table.cueball.pos;
            const dx = p.x - cb.x;
            const dy = p.y - cb.y;
            // 手球本体内 or 至近距離は無視（角度が暴れる）。
            if (dx * dx + dy * dy > ballRadius * ballRadius * 4) {
              disengageAutoAim(); // 手動でショット方向を指定 → オートエイム抑止
              cue.aim.angle = Math.atan2(dy, dx);
              rig.setFraming(true);
              rig.alignToAim(cue.aim.angle);
            }
          }
        } else if (Math.abs(p.x) <= tableHalfX && Math.abs(p.y) <= tableHalfY) {
          setPreviewOrbit(p.x, p.y, 0);
        }
      }
    }
    if (gesture === 'place' && grabbedBallId !== null) {
      // プラクティスで的玉を「ポケット狙点の近傍」へドラッグして離したら取り除く。
      // 旧仕様: 盤外（|x|>halfX or |y|>halfY）で削除 → レール際で意図せず消える事故が頻発。
      // 新仕様: 6 ポケット中心のどれかに半径 ballRadius*2 以内なら削除（実機の落球感に近い）。
      const t = pointerToTable(e.clientX, e.clientY);
      if (deps.getPlayMode() === 'practice' && grabbedBallId !== 0 && t) {
        const POCKET_REMOVE_R2 = (ballRadius * 2) * (ballRadius * 2);
        let inPocket = false;
        for (const pk of pocketAims) {
          const dx = pk.x - t.x;
          const dy = pk.y - t.y;
          if (dx * dx + dy * dy < POCKET_REMOVE_R2) {
            inPocket = true;
            break;
          }
        }
        if (inPocket) {
          removeBall(table, grabbedBallId);
          clearShotState(); // 球数が変わるためアンドゥ無効化
          preAim = null;
          syncUndo();
          deps.onBallsChanged();
          refreshPalette();
        }
      }
      if (grabbedBallId === 0) {
        // ブレイク配置モード（broken前・ballInHandでない）で手玉を動かしたなら、ユーザー
        // カスタム位置として localStorage に保存する。フリーボール（スクラッチ復帰）や
        // インプレイ中の手玉移動は記憶対象外（ブレイク開始位置だけを上書きする目的）。
        if (placementMode && !ballInHand && !broken) {
          const cb = table.cueball.pos;
          saveBreakCuePos(deps.getPlayMode(), cb.x, cb.y);
        }
        // フリーボールで手球を置き終えた瞬間: オートエイムを確定発動（きらめき）。
        if (autoAimOn()) {
          const wasEngaged = autoAimEngaged;
          if (maintainAutoAim() && !wasEngaged) deps.onAutoAimGlimmer?.();
        }
      }
      grabbedBallId = null;
    }
    pointers.delete(e.pointerId);
    if (pointers.size === 0) {
      gesture = 'idle';
      cuedragOriginated = false; // ドラッグ終了でモード切替フラグもクリア
    } else if (pointers.size === 1) gesture = 'orbit';
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      rig.zoom(e.deltaY > 0 ? 1.1 : 0.9);
      armViewResetTimer();
    },
    { passive: false },
  );
  canvas.style.touchAction = 'none';

  // ---- iOS 限定: ダブルタップ・ピンチによるブラウザ側ズームを予防する ----
  // 背景:
  //   iOS Safari/WebKit は `user-scalable=no` / `maximum-scale=1.0` を無視する仕様（iOS 10+）。
  //   ダブルタップ/ピンチで visual viewport がズームし、レイアウト座標とタッチ位置がズレる。
  //   一度ズームすると JS から scale をリセットする API が iOS に存在しないため、
  //   「発生予防」が唯一の現実解となる。
  // 設計:
  //   `quirks.needsDoubleTapSuppression` が false の OS（PC/Android 等）では登録されない＝既存挙動不変。
  //   canvas に限定して登録するため、設定モーダル・ロビーモーダル等のスクロール/長押しは影響を受けない。
  if (quirks.needsDoubleTapSuppression) {
    const DOUBLE_TAP_INTERVAL_MS = 300;
    let lastTapEndTime = 0;
    canvas.addEventListener(
      'touchend',
      (e) => {
        const now = performance.now();
        if (now - lastTapEndTime < DOUBLE_TAP_INTERVAL_MS) {
          // 前回タップから 300ms 以内 = ダブルタップ。default 抑制で smart-zoom を阻止。
          e.preventDefault();
        }
        lastTapEndTime = now;
      },
      { passive: false },
    );
    // ピンチ予防: TouchEvent.scale は WebKit 固有（iOS Safari/Chrome 共通）。
    // scale=1 以外 = ピンチ操作の可能性 → 即座に default を抑制する。
    canvas.addEventListener(
      'touchstart',
      (e) => {
        const ev = e as TouchEvent & { scale?: number };
        if (typeof ev.scale === 'number' && ev.scale !== 1) {
          e.preventDefault();
        }
      },
      { passive: false },
    );
    // WebKit 固有の gesture イベント（ピンチ/ローテートに発火）。
    // 標準型に無いため Event ベースで扱う（addEventListener はキャスト不要）。
    const onWebkitGesture = (e: Event): void => {
      e.preventDefault();
    };
    canvas.addEventListener('gesturestart', onWebkitGesture, { passive: false });
    canvas.addEventListener('gesturechange', onWebkitGesture, { passive: false });
    canvas.addEventListener('gestureend', onWebkitGesture, { passive: false });
  }

  // ---- UI 更新ループ ----
  let prevResting = true;
  const tick = (): void => {
    const resting = deps.isResting();

    // ボールロスター（左端の縦並び）を同期（セット変化時のみ再構築・在/不在を反映）。
    syncRoster();
    // パレット表示中は保存ボタンの disabled 判定を毎フレーム更新する（ドラッグ中も反映するため）。
    // パレットが閉じている時は呼ばない（無駄な serializeBalls を避ける）。
    if (paletteOpen) refreshPaletteTools();

    // ショット（または任意の運動）が完了した瞬間: 撞点を構え既定（中央やや下）へ戻す。
    if (!prevResting && resting) {
      resetSpinDefault();
      // 新しいショット機会: オートエイムの状態をリセット（実際の発動は下の維持処理が行う）。
      autoAimEngaged = false;
      autoAimLockedId = null;
      autoAimActiveId = null;
      autoAimUserAimed = false;
      autoAimSig = null;
      // 狙い的玉もクリア（直後に main.aimAtLowestBall→setAimTarget か維持処理が再設定する）。
      aimTargetId = null;
      // 優先ポケットも毎ショット中立にリセット（ユーザー要望: A 案）。
      preferredPocketIndex = null;
      // ショット完了でカット角 HUD も消す（次の構えで自動照準が決まるまで非表示）。
      setCutAngle(null);
    }
    prevResting = resting;

    // プラクティス × 自動照準発動中の構え中（静止）に、ビリヤードの標準カット角度
    // （= 手玉→的玉中心ライン と 的玉→ポケット方向のなす角・「小さいほど厚い」）を更新する。
    // `AutoAimSolution.cutAngle` が既に計算済みなので再利用する（自前で atan2 で出さない）。
    // ショット中（!resting）は更新せず直前の値を保持し、ショット完了で prevResting 遷移ブロックが
    // null クリアする＝「決まった瞬間〜ショット完了まで表示」。
    // プラクティス以外のモードでは常に非表示（モード切替で HUD が残らないよう毎フレーム明示）。
    // ドリル中（drillLock）は自動照準もガイドも禁止＝カット角 HUD も無意味なので常に非表示にする。
    if (deps.getPlayMode() !== 'practice' || drillLock) {
      setCutAngle(null);
    } else if (resting && autoAimEngaged && autoAimActiveId !== null) {
      const sol = solveAutoAimFor(autoAimActiveId);
      if (sol) setCutAngle((sol.cutAngle * 180) / Math.PI);
    }

    // 進行中（球が運動中）または相手の手番（観戦/相手ショット表示中）、もしくは「プラクティスの
    // 配置モード中」はショット/撞点系UIを丸ごと隠す（撞点パッド・縦キュー・仰角）。矢印は下の
    // aimShown で別途制御。配置中はユーザーが ✋配置ボタンで解除するまで隠したまま（球の配置に集中）。
    // ※ブレーク手玉配置（非プラクティス）では隠さない＝配置直後にそのまま構えて撞きたいため
    //   （プラクティスのみ自由配置の頻度が高く、ショットUIが邪魔になるという要望に基づく限定）。
    // ただし CPU プレビュー中（cpuPreviewActive）は撞点パッドを見せる＝CPU の構えを可視化。
    const inMotion = !resting;
    const hidePlacementShotUi = placementMode && isPractice();
    // 配置中は「完全非表示」ではなく通常の右収納（body.pool-shot-ui-hidden）へ切替え、退避タブを
    // 残して復帰導線を確保する（タブ押下で配置解除＝上の click ハンドラ）。永続はしない。
    placementCollapse = hidePlacementShotUi;
    applyShotUiAppearance(); // 状態変化時のみ DOM 更新（毎フレーム呼んでも安全）
    // ショット中（運動中）・相手手番は操作不能なので従来どおり完全非表示（タブも消す＝死にボタン化を防ぐ）。
    // 配置中は hard 非表示にせず、収納（transform）＋タブ残置で扱う。
    const hideShotUiHard = inMotion || (ruleView.inputLocked && !cpuPreviewActive);

    // F-10t: ショット方向・ズーム・カメラオービット操作中の一時非表示。
    // 対象ジェスチャ: aim (照準ドラッグ) / orbit (カメラ回転) / pinch (ピンチズーム)。
    //   cuedrag (バット掴み直後の未確定) / elevate (仰角ドラッグ) / place (球配置) は対象外。
    // 対象ボタン長押し: 方向アロー (aimHoldDir != 0) / ズーム (zoomHoldDir != 0)。
    // 設定 OFF (= getShotUiTempHideMs() <= 0) の場合は常に表示。
    const tempHideDelay = getShotUiTempHideMs();
    const tempHideEnabled = tempHideDelay > 0;
    const interactionGesture =
      gesture === 'aim' || gesture === 'orbit' || gesture === 'pinch';
    const interactionHold = aimHoldDir !== 0 || zoomHoldDir !== 0;
    const interactionNow = tempHideEnabled && (interactionGesture || interactionHold);
    const nowForHide = performance.now();
    if (interactionNow) {
      shotUiInteractionActive = true;
      shotUiRestoreAt = 0;
    } else if (shotUiInteractionActive) {
      // 操作終了の立下りエッジ: 遅延タイマーをセット（OFF 設定なら 0 即時復帰）。
      shotUiInteractionActive = false;
      shotUiRestoreAt = computeRestoreAt(nowForHide, tempHideDelay);
    } else if (shotUiRestoreAt > 0 && nowForHide >= shotUiRestoreAt) {
      // タイマー満了でクリア（次フレーム以降の判定を軽くする）。
      shotUiRestoreAt = 0;
    }
    const tempHide =
      tempHideEnabled &&
      shouldTempHide(nowForHide, shotUiInteractionActive, shotUiRestoreAt);
    const hideShotUi = hideShotUiHard || tempHide;

    padWrap.style.display = hideShotUi ? 'none' : '';
    elevWrap.style.display = hideShotUi ? 'none' : ''; // ショット中・相手手番中・操作中も他UI と統一して非表示
    cueWrap.style.display = hideShotUi ? 'none' : '';
    // 退避タブも同時に隠す（操作中は意味がない＝ユーザー要望 2026-06-27）。
    shotUiToggleBtn.style.display = hideShotUi ? 'none' : '';
    // パッド表示中は毎フレーム現在値からドットを描き直す。applySpin がパッド非表示中(幅0)や
    // DOM未レイアウト時に呼ばれるとドットが中央に化けるが、表示中は本描画で常に正しい位置へ
    // 追従させる（パッドは静止時のみ表示＝球停止中なので毎フレーム描画でも負荷は無視できる）。
    if (!hideShotUi) renderPadDot();

    // 縦キューの有効/無効（静止 & 手球が盤上 & ルール上ショット可のときだけ引ける）。
    const shootable = resting && table.cueball.onTable() && !rulesBlockShoot();
    cueWrap.classList.toggle('is-disabled', !shootable);
    // ドラッグ中に撞けなくなった場合は引き量をリセット。
    if (!shootable && cueDragging) {
      cueDragging = false;
      cueWrap.classList.remove('is-dragging');
      setCuePull(0);
    }

    // ショット方向 微調整アロー: 撞ける時に表示（トップビューでも有効）。
    // プラクティス配置モード中は隠す（撞点パッド等と統一し、配置に集中させる）。
    const aimShown = shootable && !hidePlacementShotUi;
    aimWrap.classList.toggle('is-off', !aimShown);
    if (!aimShown) endAimHold(); // 非表示になったら連続回転を止める
    // ズーム⊕⊖: 矢印と同じ条件（撞ける時のみ）+ タイミング撞きモードは
    // CSS の body.pool-timing-shot で別途制御される（OFFモード時は常に非表示）。
    zoomCtrl.classList.toggle('is-off', !aimShown);
    if (!aimShown) endZoomHold(); // 非表示になったら連続ズームを止める
    // 視点を元に戻すボタン: 撞ける場面 かつ 自動リセット OFF のときのみ表示。
    // 表示中は body.pool-recenter-on でズーム列を 1 段押し上げ、左下の重なりを防ぐ
    // （ズームボタン設定 OFF でズーム列が消えても本ボタンは独立して残す）。
    const recenterShown = aimShown && !isAutoViewReset();
    recenterBtn.classList.toggle('is-off', !recenterShown);
    document.body.classList.toggle('pool-recenter-on', recenterShown);

    // 後方レールをクリアする最小仰角・禁止円・後方クッション下方制限を再計算（静止中のみ）。
    // 相手の手番（観戦/相手ショット表示中）はローカル照準が無効なので重い再計算を省く。
    if (resting && table.cueball.onTable() && !ruleView.inputLocked) {
      // オートエイムを毎フレーム維持（角度を撞点/手球位置に追従。未発動なら自動発動）。
      // 下流の自動仰角・禁止円が補正後の角度を使うよう、先頭で角度を確定させる。
      maintainAutoAim();
      // レール上ギリギリを越える自動仰角（下撞点ほど立つ。縦キュー化はキャップで防止）。
      // ブレイク時のみ: 下撞点(BREAK_LOW_OFFSET_Y)を確保できるよう上限を緩めて立てる。
      // 権威判定（isBreakShotNow）を使用＝相手ブレイク後の通常ショットを誤ってブレイク扱いしない。
      const isBreakShot = isBreakShotNow();
      const railMaxElev = isBreakShot
        ? BREAK_RAIL_MAX_ELEVATION
        : NORMAL_RAIL_MAX_ELEVATION;
      const clearTargetY = isBreakShot
        ? Math.min(cue.aim.offset.y, BREAK_LOW_OFFSET_Y)
        : cue.aim.offset.y;
      // レール際で「キューを立てる価値があるか」を判定（実機同様レールに沿わせる挙動）。
      // 上限仰角まで立てても到達できる最低撞点が中心より上(>= RAISE_BENEFIT_Y)なら、立てても
      // 意味がない（中心〜上しか撞けない）ので立てずに寝かせる＝レール添わせ。
      const minYAtMax = computeRailMinOffsetY(
        table,
        cue.aim.angle,
        cue.length,
        railMaxElev,
      );
      const railElev =
        minYAtMax < RAISE_BENEFIT_Y
          ? computeRailClearanceElevation(
              table,
              cue.aim.angle,
              cue.length,
              clearTargetY,
              railMaxElev,
            )
          : 0;
      // 下限 BASE_AUTO_ELEVATION（4°）は常に確保（ほぼ水平＝レール添わせと矛盾しない）。
      const nextElev = Math.max(BASE_AUTO_ELEVATION, railElev);
      if (Math.abs(nextElev - autoMinElev) > 1e-4) {
        autoMinElev = nextElev;
        applyEffectiveElevation();
      }
      // 禁止円は球が実際に重なる範囲のみ。下方バンドは後方クッション近接時のみ。
      forbiddenDisks = computeSpinForbiddenDisks(
        table,
        cue.aim.angle,
        cue.length,
        cue.aim.elevation,
      );
      railMinY = computeRailMinOffsetY(
        table,
        cue.aim.angle,
        cue.length,
        cue.aim.elevation,
      );
      updatePadMask();
      // 意図撞点 desiredSpin を現在の制約に通して適用（ドラッグ中以外）。意図値から再拘束する
      // ため、レール際で抑制された後にレールから離れると意図したデフォルトへ自然に戻る。
      if (!padActive) {
        const cur = constrainSpin(desiredSpin.x, desiredSpin.y);
        if (
          Math.abs(cur.ox - cue.aim.offset.x) > 1e-6 ||
          Math.abs(cur.oy - cue.aim.offset.y) > 1e-6
        ) {
          applySpin(cur.ox, cur.oy, padBall.getBoundingClientRect().width / 2);
        }
      }
    }

    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  // ---- race スコアボード（画面上部中央）＆ race 選択モーダル ----
  // 遅延生成: 初回呼び出し時に DOM を生成し、以降は再利用する。
  let raceScoreEl: HTMLElement | null = null;
  const ensureRaceScore = (): HTMLElement => {
    if (raceScoreEl) return raceScoreEl;
    const elx = el('div', 'pool-race-score', document.body);
    elx.hidden = true;
    raceScoreEl = elx;
    return elx;
  };
  const setRaceScore = (
    view:
      | {
          readonly myWins: number;
          readonly oppWins: number;
          readonly targetWins: number;
          readonly rackIndex: number;
          readonly oppName: string;
          readonly vsCpu: boolean;
        }
      | null,
  ): void => {
    const e = ensureRaceScore();
    if (!view) {
      e.hidden = true;
      while (e.firstChild) e.removeChild(e.firstChild);
      return;
    }
    e.hidden = false;
    e.classList.toggle('is-cpu', view.vsCpu);
    while (e.firstChild) e.removeChild(e.firstChild);
    const me = el('span', 'pool-race-score-me', e);
    me.textContent = String(view.myWins);
    const sep = el('span', 'pool-race-score-sep', e);
    sep.textContent = '—';
    const opp = el('span', 'pool-race-score-opp', e);
    opp.textContent = String(view.oppWins);
    const meta = el('span', 'pool-race-score-meta', e);
    meta.textContent = `先取${view.targetWins} ・ R${view.rackIndex} ・ ${view.oppName}`;
    // 中断ボタン: 対戦中（race スコアボード表示中＝CPU/人間とも有効）に常設。
    // タップで onLeaveMatch を発火（main 側で teardownMatch を呼んでロビーへ戻る）。
    const abort = el('button', 'pool-race-abort', e) as HTMLButtonElement;
    abort.textContent = '✕';
    abort.title = '対戦を中断してロビーへ戻る';
    abort.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (confirm('対戦を中断してロビーへ戻りますか？ race は記録されません。')) {
        deps.onLeaveMatch?.();
      }
    });
  };

  // race 選択モーダル（1先/3先/5先/9先 + 交互/勝者ブレイク）。
  // - CPU 戦: 単独で値を決め、対戦開始ボタンで `onRaceStart` を発火（旧経路の後方互換）。
  // - 人間対戦: 両者対称ネゴ UI。自分の値変更で `onRaceConfigChange(false)`、対戦開始押下で
  //   `onRaceConfigChange(true)`、キャンセルで `onRaceCancel`。相手の値/承諾は `updateRaceModalRemote` で受ける。
  //   相手の race が変化したら自分の承諾フラグをリセットする（再ネゴが必要な状態に戻す）。
  //
  // 値変更時は `localStorage('pool.race.lastConfig')` に保存し、次回起動時の既定値として復元する。
  const RACE_LS_KEY = 'pool.race.lastConfig';
  const isRaceTargetWins = (n: number): n is 1 | 3 | 5 | 9 =>
    n === 1 || n === 3 || n === 5 || n === 9;
  const isBreakRule = (s: string): s is 'alternate' | 'winner' =>
    s === 'alternate' || s === 'winner';
  const loadRaceLastConfig = (): RaceConfig | null => {
    try {
      const raw = localStorage.getItem(RACE_LS_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'targetWins' in parsed &&
        'breakRule' in parsed
      ) {
        const obj = parsed as { targetWins: unknown; breakRule: unknown };
        if (
          typeof obj.targetWins === 'number' &&
          isRaceTargetWins(obj.targetWins) &&
          typeof obj.breakRule === 'string' &&
          isBreakRule(obj.breakRule)
        ) {
          return { targetWins: obj.targetWins, breakRule: obj.breakRule };
        }
      }
    } catch {
      // localStorage 不可（プライベートブラウズ等）/JSON 破損 → 既定値で続行
    }
    return null;
  };
  const saveRaceLastConfig = (race: RaceConfig): void => {
    try {
      localStorage.setItem(
        RACE_LS_KEY,
        JSON.stringify({ targetWins: race.targetWins, breakRule: race.breakRule }),
      );
    } catch {
      // 保存失敗は致命的でない（次回はデフォルトに戻るだけ）
    }
  };

  let raceModalEl: HTMLElement | null = null;
  let raceWinsBtns: HTMLButtonElement[] = [];
  let raceBrkBtns: HTMLButtonElement[] = [];
  let raceOkBtn: HTMLButtonElement | null = null;
  let raceRemoteEl: HTMLElement | null = null;
  let raceRemoteValueEl: HTMLElement | null = null;
  let raceRemoteAcceptedEl: HTMLElement | null = null;
  let raceAcceptRemoteBtnEl: HTMLButtonElement | null = null; // 「相手の設定で承認」ボタン
  let raceAcceptRemoteTimer: number = 0; // 1 秒猶予タイマー（window.setTimeout の戻り・0=未起動）
  let raceOkBtnOriginalText = '対戦開始';
  // ラック設定モーダル内チャット要素（vsCpu=true のとき hidden）。
  // 折りたたみ式: 通常は 💬 アイコンのみ表示。押下でパネル展開。
  let raceChatWrapEl: HTMLElement | null = null;
  let raceChatToggleBtn: HTMLButtonElement | null = null;
  let raceChatBadgeEl: HTMLElement | null = null;
  let raceChatPanelEl: HTMLElement | null = null;
  let raceChatLogEl: HTMLElement | null = null;
  let raceChatInputEl: HTMLInputElement | null = null;
  let raceChatLastSentAt = 0; // 0.8 秒連投抑制
  let raceChatPanelOpen = false; // パネル展開状態
  let raceChatUnreadCount = 0; // 未読件数（パネル閉時に受信したメッセージ数・最大 9+ 表示）
  // モーダル現在状態。
  let raceModalState: RaceConfig = { targetWins: 3, breakRule: 'alternate' };
  let raceModalVsCpu = false;
  let raceModalMatchId: string | null = null;
  // 自分の承諾フラグ（人間対戦のみ意味あり）。値を変更すると false に戻る。
  let raceMyAccepted = false;
  // 相手側のスナップショット（人間対戦のみ）。
  let raceRemoteRace: RaceConfig | null = null;
  let raceRemoteAccepted = false;

  const formatRace = (r: RaceConfig): string =>
    `${r.targetWins}先 / ${r.breakRule === 'alternate' ? '交互' : '勝者'}`;
  const sameRace = (a: RaceConfig, b: RaceConfig): boolean =>
    a.targetWins === b.targetWins && a.breakRule === b.breakRule;

  const renderRaceSelection = (): void => {
    raceWinsBtns.forEach((btn) => {
      const n = Number(btn.dataset.value);
      btn.classList.toggle('is-selected', n === raceModalState.targetWins);
    });
    raceBrkBtns.forEach((btn) => {
      btn.classList.toggle('is-selected', btn.dataset.value === raceModalState.breakRule);
    });
  };

  // 「相手の設定で承認」ボタンの表示条件:
  //   - vsCpu=false
  //   - 相手が承諾済（raceRemoteAccepted=true）
  //   - 自分が未承諾（raceMyAccepted=false）
  //   - 自分と相手の race が異なる（同じなら通常の「対戦開始」で確定すれば良い）
  //   - 承認猶予タイマーが起動中でない（猶予中は同じ動作の二重発火を防ぐ）
  const shouldShowAcceptRemoteBtn = (): boolean => {
    if (raceModalVsCpu) return false;
    if (!raceRemoteRace) return false;
    if (!raceRemoteAccepted) return false;
    if (raceMyAccepted) return false;
    if (sameRace(raceRemoteRace, raceModalState)) return false;
    if (raceAcceptRemoteTimer !== 0) return false;
    return true;
  };

  const renderRaceAcceptRemoteBtn = (): void => {
    if (!raceAcceptRemoteBtnEl) return;
    raceAcceptRemoteBtnEl.hidden = !shouldShowAcceptRemoteBtn();
  };

  const renderRaceRemoteArea = (): void => {
    if (!raceRemoteEl || !raceRemoteValueEl || !raceRemoteAcceptedEl) return;
    if (raceModalVsCpu) {
      raceRemoteEl.hidden = true;
      renderRaceAcceptRemoteBtn();
      return;
    }
    raceRemoteEl.hidden = false;
    if (!raceRemoteRace) {
      raceRemoteValueEl.textContent = '未提示';
      raceRemoteAcceptedEl.textContent = '';
      renderRaceAcceptRemoteBtn();
      return;
    }
    raceRemoteValueEl.textContent = formatRace(raceRemoteRace);
    raceRemoteAcceptedEl.textContent = raceRemoteAccepted ? '✅承諾済' : '';
    renderRaceAcceptRemoteBtn();
  };

  const renderRaceGoState = (): void => {
    if (!raceOkBtn) return;
    if (raceModalVsCpu) {
      raceOkBtn.classList.remove('is-waiting-remote');
      raceOkBtn.textContent = '対戦開始';
      return;
    }
    // 人間対戦: 自分が承諾済みで相手未承諾なら「相手の承諾待ち」状態を可視化する。
    const waiting = raceMyAccepted && !raceRemoteAccepted;
    raceOkBtn.classList.toggle('is-waiting-remote', waiting);
    raceOkBtn.textContent = '対戦開始';
  };

  const onLocalRaceChanged = (): void => {
    // 値変更時は自分の承諾を解除し、localStorage に保存、人間対戦なら相手へ通知。
    raceMyAccepted = false;
    saveRaceLastConfig(raceModalState);
    if (!raceModalVsCpu && raceModalMatchId !== null) {
      deps.onRaceConfigChange?.(raceModalMatchId, raceModalState, false);
    }
    renderRaceGoState();
  };

  const showRaceModal = (params: {
    readonly opponentName: string;
    readonly vsCpu: boolean;
    readonly matchId?: string;
    readonly initialRace?: RaceConfig;
  }): void => {
    if (!raceModalEl) {
      raceModalEl = el('div', 'pool-modal pool-race-modal', document.body);
      const box = el('div', 'pool-modal-box', raceModalEl);
      const title = el('div', 'pool-modal-title', box);
      title.textContent = '対戦設定';
      const msg = el('div', 'pool-modal-msg', box);
      msg.dataset.role = 'opponent';
      // 先取数: 1/3/5/9 ボタン群。
      const winsGroup = el('div', 'pool-race-group', box);
      const winsLabel = el('div', 'pool-race-group-label', winsGroup);
      winsLabel.textContent = '先取数';
      const winsRow = el('div', 'pool-race-group-row', winsGroup);
      raceWinsBtns = [];
      ([1, 3, 5, 9] as const).forEach((n) => {
        const b = el('button', 'pool-race-opt', winsRow) as HTMLButtonElement;
        b.textContent = `${n}先`;
        b.dataset.value = String(n);
        b.addEventListener('click', () => {
          raceModalState = { ...raceModalState, targetWins: n };
          renderRaceSelection();
          onLocalRaceChanged();
        });
        raceWinsBtns.push(b);
      });
      // ブレイク権: 交互/勝者ボタン群。
      const brkGroup = el('div', 'pool-race-group', box);
      const brkLabel = el('div', 'pool-race-group-label', brkGroup);
      brkLabel.textContent = 'ブレイク権';
      const brkRow = el('div', 'pool-race-group-row', brkGroup);
      raceBrkBtns = [];
      (
        [
          { v: 'alternate' as const, label: '交互' },
          { v: 'winner' as const, label: '勝者' },
        ] as const
      ).forEach(({ v, label }) => {
        const b = el('button', 'pool-race-opt', brkRow) as HTMLButtonElement;
        b.textContent = label;
        b.dataset.value = v;
        b.addEventListener('click', () => {
          raceModalState = { ...raceModalState, breakRule: v };
          renderRaceSelection();
          onLocalRaceChanged();
        });
        raceBrkBtns.push(b);
      });
      // 相手の設定エリア（人間対戦のみ表示）。
      raceRemoteEl = el('div', 'pool-race-remote', box);
      const remoteLabel = el('div', 'pool-race-remote-label', raceRemoteEl);
      remoteLabel.textContent = '相手の設定';
      const remoteLine = el('div', 'pool-race-remote-line', raceRemoteEl);
      raceRemoteValueEl = el('span', 'pool-race-remote-value', remoteLine);
      raceRemoteAcceptedEl = el('span', 'pool-race-remote-accepted', remoteLine);
      // 「相手の設定で承認」ボタン。表示条件は shouldShowAcceptRemoteBtn が判定。
      const acceptRemoteBtn = el(
        'button',
        'pool-race-accept-remote',
        raceRemoteEl,
      ) as HTMLButtonElement;
      acceptRemoteBtn.type = 'button';
      acceptRemoteBtn.textContent = '✓ 相手の設定で承認';
      acceptRemoteBtn.hidden = true;
      raceAcceptRemoteBtnEl = acceptRemoteBtn;
      acceptRemoteBtn.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        if (raceModalVsCpu || raceModalMatchId === null || !raceRemoteRace) return;
        // 相手の値で自分のローカル選択を上書き（UI 表示も同期）。
        raceModalState = {
          targetWins: raceRemoteRace.targetWins,
          breakRule: raceRemoteRace.breakRule,
        };
        renderRaceSelection();
        saveRaceLastConfig(raceModalState);
        // 「対戦開始」ボタンを暫定的に無効化＋カウントダウン表示（1 秒猶予）。
        if (raceOkBtn) {
          raceOkBtn.disabled = true;
          raceOkBtn.textContent = '承認中...（1 秒）';
        }
        renderRaceAcceptRemoteBtn(); // タイマー起動中は accept ボタン自身も隠す
        const matchId = raceModalMatchId;
        const targetRace: RaceConfig = raceModalState;
        raceAcceptRemoteTimer = window.setTimeout(() => {
          raceAcceptRemoteTimer = 0;
          // 1 秒後に正式承諾を相手へ通知。閉じる/キャンセル動作は main 側の raceStart 受信で行う。
          raceMyAccepted = true;
          if (raceOkBtn) {
            raceOkBtn.disabled = false;
            raceOkBtn.textContent = raceOkBtnOriginalText;
          }
          renderRaceGoState();
          renderRaceAcceptRemoteBtn();
          deps.onRaceConfigChange?.(matchId, targetRace, true);
        }, 1000);
      });
      // アクション。
      const acts = el('div', 'pool-modal-actions', box);
      const ok = el('button', 'pool-race-go', acts) as HTMLButtonElement;
      ok.textContent = '対戦開始';
      raceOkBtn = ok;
      raceOkBtnOriginalText = ok.textContent;
      ok.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        if (raceModalVsCpu) {
          // CPU 戦は単独で確定。
          raceModalEl!.hidden = true;
          deps.onRaceStart?.({
            targetWins: raceModalState.targetWins,
            breakRule: raceModalState.breakRule,
          });
          return;
        }
        // 人間対戦: 自分の承諾フラグを立てて相手へ通知。両者承諾は main 側で raceStart を発火する。
        if (raceModalMatchId === null) return;
        raceMyAccepted = true;
        renderRaceGoState();
        deps.onRaceConfigChange?.(raceModalMatchId, raceModalState, true);
      });
      const cancel = el('button', 'pool-modal-sub', acts) as HTMLButtonElement;
      cancel.textContent = 'キャンセル';
      cancel.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        // 1 秒猶予タイマーが起動中なら停止し、ボタン状態を元に戻す（接続切れ後の冗長 update 送信を防ぐ）。
        if (raceAcceptRemoteTimer !== 0) {
          clearTimeout(raceAcceptRemoteTimer);
          raceAcceptRemoteTimer = 0;
          if (raceOkBtn) {
            raceOkBtn.disabled = false;
            raceOkBtn.textContent = raceOkBtnOriginalText;
          }
        }
        raceModalEl!.hidden = true;
        if (!raceModalVsCpu && raceModalMatchId !== null) {
          deps.onRaceCancel?.(raceModalMatchId);
        }
      });
      // ---- ラック設定モーダル内チャット（vsCpu=false のときのみ表示・broadcast 経路） ----
      // 折りたたみ式: 通常は 💬 アイコンのみ。押下でパネル展開（既存対戦中チャットと同パターン）。
      // 定型文チップは削除（入力欄＋送信ボタンのシンプル構成）。
      raceChatWrapEl = el('div', 'pool-race-chat', box);
      const toggleBtn = el('button', 'pool-race-chat-toggle', raceChatWrapEl) as HTMLButtonElement;
      toggleBtn.type = 'button';
      toggleBtn.setAttribute('aria-label', 'チャットを開く');
      const toggleIcon = el('span', 'pool-race-chat-toggle-icon', toggleBtn);
      toggleIcon.textContent = '💬';
      const toggleLabel = el('span', 'pool-race-chat-toggle-label', toggleBtn);
      toggleLabel.textContent = 'チャット';
      const badge = el('span', 'pool-race-chat-badge', toggleBtn);
      badge.hidden = true;
      raceChatToggleBtn = toggleBtn;
      raceChatBadgeEl = badge;
      raceChatPanelEl = el('div', 'pool-race-chat-panel', raceChatWrapEl);
      raceChatPanelEl.hidden = true;
      const panelHeader = el('div', 'pool-race-chat-panel-header', raceChatPanelEl);
      const panelTitle = el('span', 'pool-race-chat-panel-title', panelHeader);
      panelTitle.textContent = 'チャット';
      // TTS トグル（close ✕ の左）。非対応環境では hidden。
      makeChatTtsToggle(panelHeader);
      const panelClose = el('button', 'pool-race-chat-panel-close', panelHeader) as HTMLButtonElement;
      panelClose.type = 'button';
      panelClose.textContent = '✕';
      panelClose.setAttribute('aria-label', '閉じる');
      raceChatLogEl = el('div', 'pool-race-chat-log', raceChatPanelEl);
      const chatInputRow = el('div', 'pool-race-chat-input-row', raceChatPanelEl);
      const chatInput = el('input', 'pool-race-chat-input', chatInputRow);
      chatInput.type = 'text';
      chatInput.maxLength = CHAT_TEXT_MAXLEN;
      chatInput.placeholder = `メッセージ（最大${CHAT_TEXT_MAXLEN}文字）`;
      raceChatInputEl = chatInput;
      const chatSendBtn = el('button', 'pool-race-chat-send', chatInputRow) as HTMLButtonElement;
      chatSendBtn.type = 'button';
      chatSendBtn.textContent = '送信';
      const sendRaceChat = (raw: string): void => {
        const text = sanitizeChatText(raw);
        if (!text) return;
        const now = Date.now();
        if (now - raceChatLastSentAt < 800) return; // 0.8 秒連投抑制
        raceChatLastSentAt = now;
        if (raceModalVsCpu || raceModalMatchId === null) return;
        // 自分側の echo 表示と sendLobbyChat 呼び出しは main の onLobbyChatSend が担当する
        // （nickname 解決を main に集約するため・既存 onChat と同じパターン）。
        deps.onLobbyChatSend?.(raceModalMatchId, text);
      };
      chatSendBtn.addEventListener('click', () => {
        sendRaceChat(chatInput.value);
        chatInput.value = '';
      });
      chatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          sendRaceChat(chatInput.value);
          chatInput.value = '';
        }
      });
      toggleBtn.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        setRaceChatPanelOpen(!raceChatPanelOpen);
      });
      panelClose.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        setRaceChatPanelOpen(false);
      });
    }
    // 初期値: initialRace > localStorage 復元 > デフォルト（3先/交互）。
    const restored = loadRaceLastConfig();
    raceModalState = params.initialRace ?? restored ?? { targetWins: 3, breakRule: 'alternate' };
    raceModalVsCpu = params.vsCpu;
    raceModalMatchId = params.matchId ?? null;
    raceMyAccepted = false;
    raceRemoteRace = null;
    raceRemoteAccepted = false;
    // 1 秒猶予タイマーが残っていれば停止（前回モーダルの掃き残しを防ぐ）。
    if (raceAcceptRemoteTimer !== 0) {
      clearTimeout(raceAcceptRemoteTimer);
      raceAcceptRemoteTimer = 0;
      if (raceOkBtn) {
        raceOkBtn.disabled = false;
        raceOkBtn.textContent = raceOkBtnOriginalText;
      }
    }
    // チャットエリアの可視/非可視・前回ログの初期化（CPU 戦時は非表示）。
    // 折りたたみ状態・未読バッジも毎回リセット。
    if (raceChatWrapEl) raceChatWrapEl.hidden = params.vsCpu;
    if (raceChatLogEl) {
      while (raceChatLogEl.firstChild) raceChatLogEl.removeChild(raceChatLogEl.firstChild);
    }
    if (raceChatInputEl) raceChatInputEl.value = '';
    raceChatLastSentAt = 0;
    setRaceChatPanelOpen(false);
    resetRaceChatUnread();
    renderRaceSelection();
    renderRaceRemoteArea();
    renderRaceGoState();
    const msg = raceModalEl.querySelector('[data-role="opponent"]') as HTMLElement;
    if (msg) {
      msg.textContent = params.vsCpu
        ? `🎱 ${params.opponentName} に挑戦します。先取数とブレイク権ルールを選んでください。`
        : `${params.opponentName} と対戦します。先取数とブレイク権ルールを選んでください。`;
    }
    raceModalEl.hidden = false;
  };

  const updateRaceModalRemote = (race: RaceConfig, accepted: boolean): void => {
    // CPU 戦時は UI なし → 何もしない。
    if (raceModalVsCpu) return;
    // 相手の race が変わったら自分の承諾もリセット（再ネゴ要求）。
    if (raceRemoteRace !== null && !sameRace(raceRemoteRace, race)) {
      raceMyAccepted = false;
      // 1 秒猶予タイマーが起動中なら停止する（相手が再度値を変えた＝古い承認は意味を失う）。
      if (raceAcceptRemoteTimer !== 0) {
        clearTimeout(raceAcceptRemoteTimer);
        raceAcceptRemoteTimer = 0;
        if (raceOkBtn) {
          raceOkBtn.disabled = false;
          raceOkBtn.textContent = raceOkBtnOriginalText;
        }
      }
    }
    raceRemoteRace = race;
    raceRemoteAccepted = accepted;
    renderRaceRemoteArea();
    renderRaceGoState();
  };

  const closeRaceModal = (): void => {
    if (!raceModalEl) return;
    // 1 秒猶予タイマーが起動中なら停止する（外部から閉じられた＝接続切れや承諾済等の解消イベント）。
    if (raceAcceptRemoteTimer !== 0) {
      clearTimeout(raceAcceptRemoteTimer);
      raceAcceptRemoteTimer = 0;
      if (raceOkBtn) {
        raceOkBtn.disabled = false;
        raceOkBtn.textContent = raceOkBtnOriginalText;
      }
    }
    // チャットパネル状態・未読件数・履歴をリセット（次回モーダル表示時の初期状態を保証）。
    setRaceChatPanelOpen(false);
    resetRaceChatUnread();
    if (raceChatLogEl) {
      while (raceChatLogEl.firstChild) raceChatLogEl.removeChild(raceChatLogEl.firstChild);
    }
    raceModalEl.hidden = true;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  // 未読バッジ表示を最新の raceChatUnreadCount に同期する（0=非表示・>=10 は "9+"）。
  const renderRaceChatBadge = (): void => {
    if (!raceChatBadgeEl) return;
    if (raceChatUnreadCount <= 0) {
      raceChatBadgeEl.hidden = true;
      raceChatBadgeEl.textContent = '';
      return;
    }
    raceChatBadgeEl.hidden = false;
    raceChatBadgeEl.textContent = raceChatUnreadCount > 9 ? '9+' : String(raceChatUnreadCount);
  };

  // 未読件数を 0 にリセット（モーダル close / 表示時 / パネル展開時）。
  const resetRaceChatUnread = (): void => {
    raceChatUnreadCount = 0;
    renderRaceChatBadge();
  };

  // パネルの展開状態をセットする（true=展開・false=折りたたみ）。
  // 展開時は未読バッジを 0 リセット・トグルボタンに is-open クラス付与・入力欄へフォーカス。
  const setRaceChatPanelOpen = (open: boolean): void => {
    raceChatPanelOpen = open;
    if (raceChatPanelEl) raceChatPanelEl.hidden = !open;
    if (raceChatToggleBtn) raceChatToggleBtn.classList.toggle('is-open', open);
    if (open) {
      resetRaceChatUnread();
      if (raceChatInputEl) raceChatInputEl.focus();
    }
  };

  // ラック設定モーダル内チャットに 1 件追加。最大 3 件を保持し、超過分は先頭から削除する。
  // 受信（who='opponent'）でパネル閉時は未読バッジを +1（最大 9+ 表示）。
  const showLobbyChatInRaceModal = (
    text: string,
    who: 'me' | 'opponent',
    name?: string,
    voice?: VoiceProfile,
  ): void => {
    if (!raceChatLogEl) return;
    const t = sanitizeChatText(text);
    if (!t) return;
    const msg = el('div', `pool-race-chat-msg is-${who}`, raceChatLogEl);
    const label = (name ?? '').trim().slice(0, 16);
    msg.textContent = label ? `${label}: ${t}` : t;
    while (raceChatLogEl.childElementCount > 3 && raceChatLogEl.firstChild) {
      raceChatLogEl.removeChild(raceChatLogEl.firstChild);
    }
    raceChatLogEl.scrollTop = raceChatLogEl.scrollHeight;
    // 受信メッセージかつパネルが閉じているなら未読バッジを +1（自分の送信 echo は変動なし）。
    if (who === 'opponent' && !raceChatPanelOpen) {
      raceChatUnreadCount += 1;
      renderRaceChatBadge();
    }
    // 読み上げ（OFF時 / 非対応時は no-op）。
    // 受信時 voice が同梱されていれば「相手の設定に従う」、無ければ既定で読む。
    chatTts.speak(t, who, voice);
  };

  // ---- 相手選択モーダル（§7A）：左右スライド式 + 自動開始タイマー ----
  // 申込側がロビーから「対戦相手をさがす」を選んだ際に表示する。候補は待機中プレイヤーの
  // スナップショット。`updateOpponentPickerCandidates` で動的更新可能。
  //
  // UI:
  //  - 中央 1 枚の大きなカードを表示し、左右ボタン (< < / > >) で切替（スワイプ非対応）
  //  - 「対人戦を自動開始」チェックボックス（デフォルト ON / localStorage 永続）
  //  - 対人カードにフォーカス中かつチェック ON なら 5 秒カウントダウン後に自動申込
  //
  // カード順序（main.ts 側 buildOpponentCandidates() が保証）:
  //   [CPU1] [CPU2] [対人 userId 昇順 ...]
  type PickerCandidate = {
    readonly userId: string;
    readonly nickname: string;
    readonly isCpu: boolean;
  };
  const PICKER_AUTO_START_SEC = 5;
  const PICKER_AUTO_START_KEY = 'pool.opponent.autoStart';

  const loadPickerAutoStart = (): boolean => {
    try {
      const v = localStorage.getItem(PICKER_AUTO_START_KEY);
      if (v === null) return true; // デフォルト ON
      return v === '1' || v === 'true';
    } catch {
      return true;
    }
  };
  const savePickerAutoStart = (on: boolean): void => {
    try {
      localStorage.setItem(PICKER_AUTO_START_KEY, on ? '1' : '0');
    } catch {
      // localStorage 利用不可（プライベートモード等）でも UI 動作は続行。
    }
  };
  const firstHumanIndex = (cands: readonly PickerCandidate[]): number => {
    const idx = cands.findIndex((c) => !c.isCpu);
    return idx >= 0 ? idx : 0;
  };

  let pickerModalEl: HTMLElement | null = null;
  let pickerCountEl: HTMLElement | null = null;
  let pickerLeftBtn: HTMLButtonElement | null = null;
  let pickerRightBtn: HTMLButtonElement | null = null;
  let pickerCardEl: HTMLElement | null = null;
  let pickerNameEl: HTMLElement | null = null;
  let pickerSubEl: HTMLElement | null = null;
  let pickerCountdownEl: HTMLElement | null = null;
  let pickerAutoCheck: HTMLInputElement | null = null;
  let pickerSubmitBtn: HTMLButtonElement | null = null;

  let pickerCandidates: readonly PickerCandidate[] = [];
  let pickerIndex = 0;
  let pickerAutoStartOn = loadPickerAutoStart();
  let pickerAutoStartRemain = 0;
  let pickerAutoStartTimer = 0; // window.setInterval の戻り値（0=停止中）
  // 「別の人が来るまで待機する」モードの ON/OFF。main 側で `lobby.setWaiting(true)` を維持しつつ
  // picker を閉じている状態を指す。picker 内ボタン押下と main からの `setBackgroundWaitingMode` で同期。
  // 待機モード中は自動申込カウントダウンを抑止する（勝手に申し込まないため）。
  let backgroundWaitingMode = false;
  let pickerWaitingBtn: HTMLButtonElement | null = null;

  const stopPickerAutoStart = (): void => {
    if (pickerAutoStartTimer !== 0) {
      clearInterval(pickerAutoStartTimer);
      pickerAutoStartTimer = 0;
    }
    pickerAutoStartRemain = 0;
  };

  const renderPickerCountdown = (): void => {
    if (!pickerCountdownEl) return;
    if (pickerAutoStartTimer !== 0 && pickerAutoStartRemain > 0) {
      pickerCountdownEl.textContent = `自動開始まで ${pickerAutoStartRemain} 秒...`;
      pickerCountdownEl.classList.add('is-active');
    } else if (!pickerAutoStartOn) {
      pickerCountdownEl.textContent = '自動開始: OFF';
      pickerCountdownEl.classList.remove('is-active');
    } else {
      pickerCountdownEl.textContent = '';
      pickerCountdownEl.classList.remove('is-active');
    }
  };

  const isAutoStartEligible = (): boolean => {
    if (!pickerAutoStartOn) return false;
    if (!pickerModalEl || pickerModalEl.hidden) return false;
    if (pickerCandidates.length === 0) return false;
    // バックグラウンド待機モード中は勝手に申込まない（ユーザーは「人が来たら通知してほしい」だけ）。
    if (backgroundWaitingMode) return false;
    const cur = pickerCandidates[pickerIndex];
    if (!cur || cur.isCpu) return false;
    // 対人候補が 1 人以上いるかは「現在フォーカス中が対人」で実質保証されるが念のため。
    return pickerCandidates.some((c) => !c.isCpu);
  };

  // タイマーを 5 秒からリセット起動。停止条件を満たすときは停止だけ行う。
  const resetPickerAutoStart = (): void => {
    stopPickerAutoStart();
    if (!isAutoStartEligible()) {
      renderPickerCountdown();
      return;
    }
    pickerAutoStartRemain = PICKER_AUTO_START_SEC;
    renderPickerCountdown();
    pickerAutoStartTimer = window.setInterval(() => {
      pickerAutoStartRemain -= 1;
      if (pickerAutoStartRemain <= 0) {
        const cur = pickerCandidates[pickerIndex];
        stopPickerAutoStart();
        renderPickerCountdown();
        if (!cur || cur.isCpu) return; // 安全ガード
        if (pickerModalEl) pickerModalEl.hidden = true;
        deps.onOpponentPickerSubmit?.(cur.userId);
      } else {
        renderPickerCountdown();
      }
    }, 1000);
  };

  // タイマーが停止中で起動条件を満たすなら起動。動作中なら何もしない（延長しない）。
  const maybeStartPickerAutoStart = (): void => {
    if (pickerAutoStartTimer !== 0) return; // 既に動作中
    if (!isAutoStartEligible()) {
      renderPickerCountdown();
      return;
    }
    resetPickerAutoStart();
  };

  // 待機ボタンの表示を現在の backgroundWaitingMode に合わせて更新する。
  // - 待機OFF時: グレーで「別の対戦者が来るまで待機 (通知あり)」
  // - 待機ON 時: 緑で「待機中… 解除する」（押下で待機 OFF）
  const renderPickerWaitingButton = (): void => {
    if (!pickerWaitingBtn) return;
    if (backgroundWaitingMode) {
      pickerWaitingBtn.textContent = '待機中… 解除する';
      pickerWaitingBtn.classList.add('is-active');
    } else {
      pickerWaitingBtn.textContent = '🔔 別の対戦者が来るまで待機（通知あり）';
      pickerWaitingBtn.classList.remove('is-active');
    }
  };

  // main から「バックグラウンド待機モード ON/OFF」を反映する公開 API。
  // picker 内ボタンの見た目を切替＋自動申込カウントダウンを再評価。
  const setBackgroundWaitingMode = (active: boolean): void => {
    if (backgroundWaitingMode === active) return;
    backgroundWaitingMode = active;
    renderPickerWaitingButton();
    // 待機ON へ移行＝カウントダウン停止／待機OFF へ移行＝条件を満たせば再開。
    if (active) {
      stopPickerAutoStart();
      renderPickerCountdown();
    } else if (pickerModalEl && !pickerModalEl.hidden) {
      maybeStartPickerAutoStart();
    }
    // ロビーボタン側の小バッジ表示（待機中インジケーター）。
    if (lobbyBtn) lobbyBtn.classList.toggle('is-waiting', active);
  };

  const renderPickerCard = (): void => {
    if (
      !pickerCardEl ||
      !pickerNameEl ||
      !pickerSubEl ||
      !pickerSubmitBtn ||
      !pickerLeftBtn ||
      !pickerRightBtn ||
      !pickerCountEl
    )
      return;
    const n = pickerCandidates.length;
    // 候補数表示。
    pickerCountEl.textContent = `対戦候補：${n}名`;
    // 空状態。
    if (n === 0) {
      pickerCardEl.classList.remove('is-cpu');
      pickerCardEl.classList.add('is-empty');
      pickerNameEl.textContent = '待機中のプレイヤーはいません';
      pickerSubEl.textContent = '';
      pickerSubmitBtn.disabled = true;
      pickerLeftBtn.disabled = true;
      pickerRightBtn.disabled = true;
      return;
    }
    const cur = pickerCandidates[pickerIndex] ?? pickerCandidates[0]!;
    pickerCardEl.classList.remove('is-empty');
    if (cur.isCpu) pickerCardEl.classList.add('is-cpu');
    else pickerCardEl.classList.remove('is-cpu');
    pickerNameEl.textContent = cur.nickname;
    pickerSubEl.textContent = cur.isCpu ? '(CPU)' : '(対人)';
    pickerSubmitBtn.disabled = false;
    const single = n <= 1;
    pickerLeftBtn.disabled = single;
    pickerRightBtn.disabled = single;
  };

  const moveTo = (delta: number): void => {
    const n = pickerCandidates.length;
    if (n <= 1) return;
    pickerIndex = ((pickerIndex + delta) % n + n) % n;
    renderPickerCard();
    // 手動カード切替はタイマーをリセット（5 秒から再カウント）。CPU へ移動した場合は停止される。
    resetPickerAutoStart();
  };

  const showOpponentPicker = (params: {
    readonly candidates: readonly PickerCandidate[];
  }): void => {
    if (!pickerModalEl) {
      pickerModalEl = el('div', 'pool-modal pool-opponent-picker', document.body);
      const box = el('div', 'pool-modal-box', pickerModalEl);
      // ヘッダ行: タイトル + 自動開始チェックボックス
      const header = el('div', 'pool-opponent-picker-header', box);
      const title = el('div', 'pool-modal-title', header);
      title.textContent = '対戦相手を選ぶ';
      const autoLabel = el('label', 'pool-opponent-picker-auto', header);
      const autoCheck = document.createElement('input');
      autoCheck.type = 'checkbox';
      autoCheck.checked = pickerAutoStartOn;
      autoLabel.appendChild(autoCheck);
      const autoText = document.createElement('span');
      autoText.textContent = '対人戦を自動開始';
      autoLabel.appendChild(autoText);
      pickerAutoCheck = autoCheck;
      autoCheck.addEventListener('change', () => {
        const next = autoCheck.checked;
        const prev = pickerAutoStartOn;
        pickerAutoStartOn = next;
        savePickerAutoStart(next);
        if (!next) {
          // OFF: タイマー停止
          stopPickerAutoStart();
          renderPickerCountdown();
        } else if (!prev) {
          // OFF→ON: 起動条件を満たすなら 5 秒からリセット起動
          resetPickerAutoStart();
        }
      });

      // 候補数
      pickerCountEl = el('div', 'pool-opponent-picker-count', box);

      // スライダ本体（左ボタン / 中央カード / 右ボタン）
      const slider = el('div', 'pool-opponent-picker-slider', box);
      const leftBtn = el('button', 'pool-opponent-picker-arrow', slider) as HTMLButtonElement;
      leftBtn.type = 'button';
      leftBtn.textContent = '< <';
      leftBtn.setAttribute('aria-label', '前の候補');
      pickerLeftBtn = leftBtn;
      leftBtn.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        moveTo(-1);
      });
      const card = el('div', 'pool-opponent-picker-card', slider);
      pickerCardEl = card;
      pickerNameEl = el('div', 'pool-opponent-picker-name', card);
      pickerSubEl = el('div', 'pool-opponent-picker-sub', card);
      const rightBtn = el('button', 'pool-opponent-picker-arrow', slider) as HTMLButtonElement;
      rightBtn.type = 'button';
      rightBtn.textContent = '> >';
      rightBtn.setAttribute('aria-label', '次の候補');
      pickerRightBtn = rightBtn;
      rightBtn.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        moveTo(1);
      });

      // カウントダウン表示
      pickerCountdownEl = el('div', 'pool-opponent-picker-countdown', box);

      // アクションボタン
      const acts = el('div', 'pool-modal-actions', box);
      const submit = el('button', 'pool-race-go', acts) as HTMLButtonElement;
      submit.textContent = '対戦を申し込む';
      pickerSubmitBtn = submit;
      submit.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        if (pickerCandidates.length === 0) return;
        const chosen = pickerCandidates[pickerIndex];
        if (!chosen) return;
        stopPickerAutoStart();
        pickerModalEl!.hidden = true;
        deps.onOpponentPickerSubmit?.(chosen.userId);
      });
      // 「別の対戦者が来るまで待機 (通知あり)」ボタン: picker を閉じてバックグラウンド待機モードへ移行。
      // 既に待機モード中ならトグルで OFF（実質キャンセル相当＝モーダル閉じ＋ waiting 解除）。
      // ラベル/カラーは renderPickerWaitingButton で待機モード状態に応じて切り替え。
      const waiting = el('button', 'pool-opponent-picker-waiting', acts) as HTMLButtonElement;
      waiting.type = 'button';
      pickerWaitingBtn = waiting;
      waiting.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        const willActivate = !backgroundWaitingMode;
        stopPickerAutoStart();
        pickerModalEl!.hidden = true;
        deps.onToggleBackgroundWaiting?.(willActivate);
      });
      const cancel = el('button', 'pool-modal-sub', acts) as HTMLButtonElement;
      cancel.textContent = 'キャンセル';
      cancel.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        stopPickerAutoStart();
        pickerModalEl!.hidden = true;
        deps.onOpponentPickerCancel?.();
      });
    }
    // 表示前に最新状態を反映
    if (pickerAutoCheck) pickerAutoCheck.checked = pickerAutoStartOn;
    // 待機ボタンの状態を現在のモードに合わせて初期化（main の setBackgroundWaitingMode と同期済み）。
    renderPickerWaitingButton();
    pickerCandidates = params.candidates.slice();
    // 初期フォーカス: 対人がいれば最初の対人、いなければ先頭（CPU）
    pickerIndex = firstHumanIndex(pickerCandidates);
    renderPickerCard();
    pickerModalEl.hidden = false;
    // 自動開始タイマーの起動判定（対人にフォーカス中かつチェック ON ならカウント開始）
    resetPickerAutoStart();
  };

  const updateOpponentPickerCandidates = (
    candidates: readonly PickerCandidate[],
  ): void => {
    const prevCur = pickerCandidates[pickerIndex] ?? null;
    const prevId = prevCur?.userId ?? null;
    const prevWasCpu = prevCur?.isCpu ?? false;
    pickerCandidates = candidates.slice();
    if (pickerCandidates.length === 0) {
      pickerIndex = 0;
    } else {
      // prevId 追従: 現在表示中の候補が残っていればその位置へ。
      const idx = prevId === null ? -1 : pickerCandidates.findIndex((c) => c.userId === prevId);
      if (idx >= 0) {
        pickerIndex = idx;
      } else if (prevWasCpu) {
        // CPU にフォーカス中で消失した場合 → 新規対人がいればそちらへ、なければ先頭。
        pickerIndex = firstHumanIndex(pickerCandidates);
      } else {
        // 対人が消失した場合 → 次のインデックスへ（範囲補正）。
        pickerIndex = Math.min(pickerIndex, pickerCandidates.length - 1);
      }
    }
    // 自動フォーカス移動: 現在 CPU にフォーカス中で対人が候補にいれば最初の対人へ。
    if (prevWasCpu && pickerCandidates.some((c) => !c.isCpu)) {
      pickerIndex = firstHumanIndex(pickerCandidates);
    }
    renderPickerCard();
    // 候補更新は「タイマー延長しない」。停止条件を満たせば止め、起動条件を満たすなら起動のみ。
    if (!isAutoStartEligible()) {
      stopPickerAutoStart();
      renderPickerCountdown();
    } else {
      maybeStartPickerAutoStart();
    }
  };

  const closeOpponentPicker = (): void => {
    stopPickerAutoStart();
    if (!pickerModalEl) return;
    pickerModalEl.hidden = true;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  // ---- 対戦勧誘ダイアログ ----
  // CPU 戦中 or バックグラウンド待機モード中に新規 waiting 対人が現れた時、
  // 「対戦する／今は続ける」で意思確認するモーダル。背景クリック/ESC は無効
  // （誤操作で対戦を始めない/見逃さないため）。
  let invitationModalEl: HTMLElement | null = null;
  let invitationNameEl: HTMLElement | null = null;
  let invitationOpponentUserId: string | null = null;

  const showMatchInvitation = (params: {
    readonly opponentUserId: string;
    readonly opponentNickname: string;
  }): void => {
    invitationOpponentUserId = params.opponentUserId;
    if (!invitationModalEl) {
      invitationModalEl = el('div', 'pool-modal pool-match-invitation', document.body);
      const box = el('div', 'pool-modal-box', invitationModalEl);
      const title = el('div', 'pool-modal-title', box);
      title.textContent = '対戦リクエスト';
      const body = el('div', 'pool-match-invitation-body', box);
      const lead = el('div', 'pool-match-invitation-lead', body);
      invitationNameEl = el('span', 'pool-match-invitation-name', lead);
      const tail = el('span', '', lead);
      tail.textContent = ' さんが対戦を待機しています。';
      const sub = el('div', 'pool-match-invitation-sub', body);
      sub.textContent = '対戦しますか？';
      const acts = el('div', 'pool-modal-actions', box);
      const accept = el('button', 'pool-race-go', acts) as HTMLButtonElement;
      accept.type = 'button';
      accept.textContent = '対戦する';
      accept.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        const userId = invitationOpponentUserId;
        invitationModalEl!.hidden = true;
        invitationOpponentUserId = null;
        if (userId) deps.onAcceptMatchInvitation?.(userId);
      });
      const dismiss = el('button', 'pool-modal-sub', acts) as HTMLButtonElement;
      dismiss.type = 'button';
      dismiss.textContent = '今は続ける';
      dismiss.addEventListener('click', () => {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        const userId = invitationOpponentUserId;
        invitationModalEl!.hidden = true;
        invitationOpponentUserId = null;
        if (userId) deps.onDismissMatchInvitation?.(userId);
      });
    }
    if (invitationNameEl) {
      invitationNameEl.textContent = (params.opponentNickname || '').slice(0, 24);
    }
    invitationModalEl.hidden = false;
  };

  const closeMatchInvitation = (): void => {
    invitationOpponentUserId = null;
    if (!invitationModalEl) return;
    invitationModalEl.hidden = true;
  };

  const isMatchInvitationOpen = (): boolean => {
    return !!invitationModalEl && !invitationModalEl.hidden;
  };

  return {
    setRuleView: (view: RuleView) => {
      const wasLocked = ruleView.inputLocked;
      ruleView = view;
      renderRuleView();
      // inputLocked の切替で配置ボタン表示も更新（相手手番に切替時に配置 UI を隠す）。
      syncPlacementUi();
      // 相手の手番に切替わったらタイミング撞きのオシレーションを安全停止する。
      if (!wasLocked && view.inputLocked) cancelTimingShot();
    },
    setLobbyView: (view: LobbyView) => {
      lobbyView = view;
      renderLobby();
    },
    startAuthFlow: (name: string) => startAuthFlow(name),
    closeLobby: () => {
      lobbyOpen = false;
      renderLobby();
    },
    openLobby: () => {
      // 既に開いていれば何もしない（leaderboard 再取得の連打を避ける）。
      if (lobbyOpen) return;
      lobbyOpen = true;
      deps.onLobbyOpen?.(); // 最新の順位表を取得
      renderLobby();
    },
    showToast: (text: string) => showToast(text),
    showMatchEvent: (
      title: string,
      subtitle: string,
      kind: 'start' | 'win' | 'lose' | 'abort' | 'leave',
    ) => showMatchEvent(title, subtitle, kind),
    setChatEnabled: (enabled: boolean) => setChatEnabled(enabled),
    showChatMessage: (
      text: string,
      who: 'me' | 'opponent',
      name?: string,
      voice?: VoiceProfile,
    ) => showChatMessage(text, who, name, voice),
    enterPlacement: () => {
      cancelTimingShot(); // 進行中のタイミング撞きは安全停止
      // 新ラック（ブレーク）開始: 配置可能に戻し、配置モードを開始（キッチン限定）。
      broken = false;
      ballInHand = false;
      placementMode = true;
      paletteOpen = false;
      // 盤面が作り直されるためアンドゥ用スナップショットは破棄。
      clearShotState();
      preAim = null;
      syncUndo();
      syncPlacementUi();
      // 新ラックは新規構え扱い。前ショットの autoAim 抑止フラグを確実にクリアする
      // （L2424 のショット静止リセットが想定外パスで欠落した場合の保険）。
      autoAimEngaged = false;
      autoAimUserAimed = false;
      autoAimLockedId = null;
      autoAimActiveId = null;
      autoAimSig = null;
      // モード別のブレイク既定撞点を適用（プレイヤーは以後変更可）。
      const sp = breakSpin(deps.getPlayMode());
      setDesiredSpin(sp.ox, sp.oy);
    },
    enterBallInHand: () => {
      // スクラッチ後: 手玉を盤面自由配置（再ラックしない・キッチン制限なし）。
      ballInHand = true;
      placementMode = true;
      paletteOpen = false;
      // フリーボール開始は新規構え扱い。前ショットでユーザーが手で照準していた場合の
      // `autoAimUserAimed=true` が残ったままだと、新しい手球位置を配置しても自動照準が
      // 抑止されたままになる（「稀に効かない」事象の原因）。確実にクリアする。
      autoAimEngaged = false;
      autoAimUserAimed = false;
      autoAimLockedId = null;
      autoAimActiveId = null;
      autoAimSig = null;
      syncPlacementUi();
    },
    enterResumed: (state) => {
      // 復元した盤面に合わせて配置状態を復旧（再ラックしない）。
      broken = state.broken;
      ballInHand = state.ballInHand;
      // ボールインハンド中なら配置モードで再開。通常進行中は配置モードに入らない。
      placementMode = state.ballInHand;
      paletteOpen = false;
      // 撞く直前スナップショットはリロードで失われているためアンドゥは無効状態で開始。
      clearShotState();
      preAim = null;
      syncUndo();
      syncPlacementUi();
    },
    getPersistState: () => ({ broken, ballInHand }),
    setDrillLock: (active: boolean) => {
      drillLock = active;
      if (active) {
        placementMode = false;
        paletteOpen = false;
      }
      // ドリル中はトップバーの「↺ リセット」を「✕ 終了」へ（動作は同じ＝ドリル解除→プラクティス復帰）。
      // 「リセット＝初期化」ではなく「ドリルを終了する」意味と表現を一致させる（ラベル幅は悪化しない）。
      const ico = resetBtn.querySelector('.pool-btn-ico');
      const lab = resetBtn.querySelector('.pool-btn-label');
      if (ico) ico.textContent = active ? '✕' : '↺';
      if (lab) lab.textContent = active ? '終了' : 'リセット';
      const resetTitle = active ? 'ドリルを終了' : 'リセット';
      resetBtn.title = resetTitle;
      resetBtn.setAttribute('aria-label', resetTitle);
      // ドリル中は「↶ 戻す（ショット単位アンドゥ）」を「↻ やり直し（全リセット）」に切り替える。
      const uico = undoBtn.querySelector('.pool-btn-ico');
      const ulab = undoBtn.querySelector('.pool-btn-label');
      if (uico) uico.textContent = active ? '↻' : '↶';
      if (ulab) ulab.textContent = active ? 'やり直し' : '戻す';
      const undoTitle = active ? '最初からやり直す' : '戻す';
      undoBtn.title = undoTitle;
      undoBtn.setAttribute('aria-label', undoTitle);
      syncUndo(); // ドリル中は常時押下可、解除後はスナップショット有無で再判定
      syncPlacementUi();
    },
    setBallSpeed,
    resetCueElevation: () => {
      // 次のショットへユーザー操作仰角を引き継がない。自動仰角（autoMinElev）はそのまま保つ。
      setManualElevation(0);
    },
    /** 現在のドラッグジェスチャ種別（ドラッグ位置ガイドの色変更用）。
     *  'aim'=ショット方向操作中 / 'elevate'=仰角操作中 / 'idle'=非ドラッグ or 他ジェスチャ。
     *  drag-guide.ts が 'aim'/'elevate' のとき該当モードの色だけを残す（操作中の表示を絞る）。 */
    getDragGesture: (): 'aim' | 'elevate' | 'idle' => {
      if (gesture === 'aim') return 'aim';
      if (gesture === 'elevate') return 'elevate';
      return 'idle';
    },
    getMovableBallIds: (): Set<number> => {
      // 配置可能の判定は controls 内部状態（placementMode/ballInHand/プラクティス）に閉じる。
      // 相手手番中（inputLocked）はそもそも操作不可なので空セットを返す。
      const ids = new Set<number>();
      if (drillLock) return ids; // ドリル中は球を一切動かせない（固定配置）
      if (!placementMode || ruleView.inputLocked) return ids;
      if (isPractice()) {
        // プラクティス: 盤上の全球を移動可能（手球+的玉）。
        for (const b of table.balls) {
          if (b.onTable()) ids.add(b.id);
        }
      } else if (table.cueball.onTable()) {
        // 非プラクティス（ブレーク前 or ボールインハンド）: 手球のみ。
        ids.add(0);
      }
      return ids;
    },
    loadPracticeLayout: () => loadPracticeLayout(),
    getMakeablePockets: () => computeMakeablePockets(),
    setAccountInfo: (name: string, serverAuth: boolean): void => {
      accountName = name;
      accountServerAuth = serverAuth;
      // 設定パネルのアカウントセクションを再描画（開閉中・閉鎖中問わず DOM 更新）。
      accountSectionRefs.rebuild?.();
    },
    setInventoryView: (view: InventoryView): void => {
      invView = view;
      // モーダルが開いていれば即時再描画。閉じていても次に開く時の `renderInventoryPanel` で反映される。
      if (!invModal.hidden) renderInventoryPanel();
    },
    toggleInventoryPanel: (): void => {
      invModal.hidden = !invModal.hidden;
      if (!invModal.hidden) renderInventoryPanel();
    },
    openProfileLoading: (name: string, lastSeen: number, online: boolean) =>
      profilePanel.openLoading(name, lastSeen, online),
    openProfile: (
      name: string,
      data: AnalyticsResponse | null,
      lastSeen: number,
      online: boolean,
    ) => profilePanel.open(name, data, lastSeen, online),
    closeProfile: () => profilePanel.close(),
    selectPocket: (pocketIndex: number) => {
      preferredPocketIndex = pocketIndex;
      autoAimSig = null; // 強制再計算
      if (autoAimOn()) {
        if (maintainAutoAim()) deps.onAutoAimGlimmer?.();
      }
    },
    getCutAngleFrameVertices: () => {
      // 静止中・手玉が盤上・ターゲット的玉あり・ポケット解あり が揃わない限り表示不可。
      if (aimTargetId === null) return null;
      if (!deps.isResting()) return null;
      if (!table.cueball.onTable()) return null;
      const sol = solveAutoAimFor(aimTargetId);
      if (!sol) return null;
      const tp = ballPositionById(table, aimTargetId);
      if (!tp) return null;
      const pocket = pocketAims[sol.pocketIndex];
      if (!pocket) return null;
      const cb = cueBallPos(table);
      // 軸 u = 手玉 → 的玉中心の単位ベクトル（長方形の 1 辺の方向 ＝ ユーザー指定の基準）。
      const ax = tp.x - cb.x;
      const ay = tp.y - cb.y;
      const axisLen = Math.hypot(ax, ay);
      if (axisLen < 1e-6) return null; // 手玉と的玉がほぼ同一点（異常値）の保険
      const ux = ax / axisLen;
      const uy = ay / axisLen;
      const nx = -uy; // u を反時計 90° 回転した直交単位
      const ny = ux;
      // 対角 = 的玉中心 T と ポケット狙点 P。a = 軸方向射影、b = 直交方向射影。
      // a=|b| のとき正方形 ⇔ カット角 45°。LineLoop で閉じる順序で 4 頂点を返す。
      const dx = pocket.x - tp.x;
      const dy = pocket.y - tp.y;
      const a = dx * ux + dy * uy;
      const b = dx * nx + dy * ny;
      return [
        { x: tp.x, y: tp.y }, // T
        { x: tp.x + a * ux, y: tp.y + a * uy }, // T + a·u
        { x: pocket.x, y: pocket.y }, // P（= T + a·u + b·n）
        { x: tp.x + b * nx, y: tp.y + b * ny }, // T + b·n
      ];
    },
    setAimTarget: (id: number | null) => {
      // main の自動照準（最若球など）が設定した「狙い的玉」を残球表示の強調に反映する。
      aimTargetId = id;
    },
    getAimTargetId: () => aimTargetId,
    setRaceScore: (view) => setRaceScore(view),
    showRaceModal: (params) => showRaceModal(params),
    updateRaceModalRemote: (race, accepted) => updateRaceModalRemote(race, accepted),
    closeRaceModal: () => closeRaceModal(),
    showLobbyChatInRaceModal: (text, who, name, voice) =>
      showLobbyChatInRaceModal(text, who, name, voice),
    showOpponentPicker: (params) => showOpponentPicker(params),
    updateOpponentPickerCandidates: (candidates) =>
      updateOpponentPickerCandidates(candidates),
    closeOpponentPicker: () => closeOpponentPicker(),
    setBackgroundWaitingMode: (active) => setBackgroundWaitingMode(active),
    showMatchInvitation: (params) => showMatchInvitation(params),
    closeMatchInvitation: () => closeMatchInvitation(),
    isMatchInvitationOpen: () => isMatchInvitationOpen(),
    applyAimPreview: (preview) => {
      // 相手（CPU）の構えを可視化する。inputLocked 中でも cue/ガイド線/撞点パッドを
      // 同期更新するため、内部状態（cue.aim.* + 撞点パッド + パワー）を直接書き換える。
      // プレビューフラグを立てて tick() が UI を隠さないようにする。
      cpuPreviewActive = true;
      cue.aim.angle = preview.angle;
      // CPU 物理計算用 elevation（≈0.05 rad / 2.86°）はキューが寝かせすぎで、
      // 描画上テーブル面にめり込んで見える。描画用は最小 0.20 rad（≈11.5°）でクランプ。
      // 物理エンジンへは plan.shot.elevation がそのまま渡されるため、思考結果は不変。
      const MIN_DISPLAY_ELEVATION = 0.20;
      cue.aim.elevation = Math.max(preview.elevation, MIN_DISPLAY_ELEVATION);
      const padR = padBall.getBoundingClientRect().width / 2;
      // applySpin はパッド非表示中だと r=0 でドットが中央に化けるが、cpuPreviewActive=true で
      // 次フレームの tick() がパッドを表示にし、renderPadDot が cue.aim.offset から正しく再描画する。
      applySpin(preview.offsetX, preview.offsetY, padR);
      // パワー: cue.aim.power 単位 [m/s] と同じ。vendor cue.ts が引き量に反映。
      cue.aim.power = preview.power;
    },
    clearAimPreview: () => {
      // 通常 UI に戻す（パワーのみリセット・aim/offset は CPU が決めた値を直前まで維持）。
      cpuPreviewActive = false;
      cue.aim.power = 0;
    },
  };
};
